// PsychXAI v3.0 — built April 24 2026
import { useState, useMemo, useEffect, useRef } from "react";
import { auth, db, adminCall } from "./aws-client";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────

const C = {
  // Green — 3 variants only
  sage:        "#3D6B47",   // primary — buttons, active states
  sageDark:    "#2A4A31",   // dark — hover, nav
  sageLight:   "#E8F0E9",   // light — backgrounds, hover states
  sageBorder:  "#B8CEB9",   // border
  sageDeep:    "#1E3524",   // logo, deepest

  // Warm beige neutrals
  black:       "#1C1F1A",
  gray700:     "#4A4F46",
  gray500:     "#7A7F76",
  gray400:     "#A8ADA4",
  gray200:     "#DDE0DA",
  gray100:     "#ECEEE9",
  white:       "#FDFCF8",

  // Warm beige backgrounds
  paper:       "#F5F2EA",
  paperDark:   "#EDE9DF",

  // Signals
  red:         "#B5433A",
  redLight:    "#FAF0EE",
  redBorder:   "#E8C4C0",
  amber:       "#A06B2A",
  amberLight:  "#FAF4EA",
  amberBorder: "#E8D4AA",
};

// 8px spacing grid
const sp = {
  xs:  "4px",
  sm:  "8px",
  md:  "16px",
  lg:  "24px",
  xl:  "32px",
  xxl: "48px",
};

// Type scale — 6 sizes only
const fs = {
  xs:   "15px",  // metadata, labels
  sm:   "17px",  // body, table content
  md:   "19px",  // card titles
  lg:   "22px",  // page headers
  xl:   "28px",  // section hero
  hero: "64px",  // hero number
};

const fonts = {
  body: "'Inter', 'Helvetica Neue', sans-serif",
  mono: "'IBM Plex Mono', 'Courier New', monospace",
};


// ─── GOOGLE FONTS LOADER ──────────────────────────────────────────────────────

function FontLoader() {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel  = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap";
    document.head.appendChild(link);
    document.title = "PsychXAI";
  }, []);
  return null;
}

// ─── LOGO MARK ────────────────────────────────────────────────────────────────

function LogoMark({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="8" fill="#1E3524"/>
      {/* Waveform where the leftmost tall bar + right bars suggest a P */}
      {/* Stem of P — tall, bold, left of center */}
      <rect x="4"  y="6"  width="3.5" height="20" rx="1.5" fill="white"/>
      {/* Bowl of P — bars that arc up then down on the right */}
      <rect x="10" y="12" width="2.5" height="14" rx="1.2" fill="white" opacity="0.75"/>
      <rect x="14" y="9"  width="2.5" height="11" rx="1.2" fill="white" opacity="0.9"/>
      <rect x="18" y="12" width="2.5" height="8"  rx="1.2" fill="white" opacity="0.75"/>
      {/* Trailing bars — suggest voice continuing */}
      <rect x="23" y="16" width="2"   height="6"  rx="1"   fill="white" opacity="0.45"/>
      <rect x="27" y="19" width="2"   height="4"  rx="1"   fill="white" opacity="0.25"/>
    </svg>
  );
}

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────

function GlobalStyles() {
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes slideInDown {
        from { opacity: 0; transform: translateY(-12px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(8px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      @keyframes countUp {
        from { opacity: 0; transform: scale(0.85); }
        to   { opacity: 1; transform: scale(1); }
      }
      @keyframes greenFlash {
        0%   { background: #3D6B47; }
        50%  { background: #E8F0E9; }
        100% { background: transparent; }
      }
      @keyframes slideInRow {
        from { opacity: 0; transform: translateX(-8px); }
        to   { opacity: 1; transform: translateX(0); }
      }
      @keyframes checkBounce {
        0%   { transform: scale(0); }
        60%  { transform: scale(1.2); }
        100% { transform: scale(1); }
      }
      @keyframes blink {
        0%, 100% { opacity: 1; }
        50%       { opacity: 0; }
      }
      @keyframes wave1 {
        from { transform: scaleY(0.6); }
        to   { transform: scaleY(1.2); }
      }
      @keyframes wave2 {
        from { transform: scaleY(0.8); }
        to   { transform: scaleY(1.3); }
      }
      @keyframes wave3 {
        from { transform: scaleY(1); }
        to   { transform: scaleY(0.5); }
      }
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50%       { opacity: 0.7; }
      }
        from { opacity: 0; transform: translateY(-6px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .patient-row {
        animation: slideInRow 0.25s ease both;
      }
      .patient-row:nth-child(1) { animation-delay: 0.05s; }
      .patient-row:nth-child(2) { animation-delay: 0.10s; }
      .patient-row:nth-child(3) { animation-delay: 0.15s; }
      .patient-row:nth-child(4) { animation-delay: 0.20s; }
      .patient-row:nth-child(5) { animation-delay: 0.25s; }
      .hero-number {
        animation: countUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
      }
      .banner-animate {
        animation: bannerSlide 0.2s ease both;
      }
      .login-card {
        animation: fadeInUp 0.3s ease both;
      }
      .tab-content {
        animation: fadeIn 0.15s ease both;
      }

      /* Mobile touch improvements */
      button { -webkit-tap-highlight-color: transparent; touch-action: manipulation; }
      input  { -webkit-tap-highlight-color: transparent; }

      /* Mobile swipe card */
      .swipe-card {
        transition: transform 0.15s ease, opacity 0.15s ease;
        user-select: none;
      }
      .swipe-card:active { transform: scale(0.98); }

      /* Bottom tab bar */
      .bottom-tab-bar {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: white;
        border-top: 1px solid #DDE0DA;
        display: flex;
        z-index: 100;
        padding-bottom: env(safe-area-inset-bottom);
      }
      .bottom-tab {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 10px 4px 8px;
        font-size: 10px;
        font-family: Inter, sans-serif;
        font-weight: 500;
        color: #A8ADA4;
        border: none;
        background: none;
        cursor: pointer;
        transition: color 0.15s;
        gap: 3px;
      }
      .bottom-tab.active { color: #3D6B47; }
      .bottom-tab-icon { font-size: 18px; line-height: 1; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  return null;
}


function genderAbbr(g) {
  if (!g) return "";
  const val = g.toLowerCase().trim();
  if (val === "male" || val === "m") return "M";
  if (val === "female" || val === "f") return "F";
  if (val.includes("nonbinary") || val.includes("non-binary") || val === "nb") return "NB";
  if (val.includes("transgender male") || val === "trans male") return "TM";
  if (val.includes("transgender female") || val === "trans female") return "TF";
  if (val === "other") return "Other";
  // Fallback — first 3 chars capitalized
  return g.slice(0, 3).toUpperCase();
}

function formatPhone(raw) {
  if (!raw || raw === "—") return raw || "—";
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits[0] === "1") {
    return `+1 (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  }
  return raw;
}

const DEMO_PATIENTS = [
  {
    id: 1,
    name: "Maria Santos",
    age: "34", gender: "Female",
    phone: "(646) 555-0192",
    date: "Apr 21, 2026", time: "8:30 AM",
    chiefComplaint: "Depression, anxiety, low mood — 4 months",
    primaryDiagnosis: "Major Depressive Disorder, single episode, moderate",
    medicationsCurrent: "Levothyroxine 50mcg",
    medicationsPrior: "None reported",
    allergies: "NKDA",
    riskLevel: "low",
    siFlag: false, hiFlag: false, graveDisabilityFlag: false,
    firearmsAccess: false, pregnancyStatus: null,
    seizureHistory: false, cardiacHistory: false, strokeHistory: false,
    status: "pending", fitScore: "accept",
    fitRationale: "Appropriate for outpatient intake. MDD + GAD presentation, no SI, no substance use, no prior hospitalizations. Engaged in therapy. Straightforward evaluation.",
    declineFlag: null, declineReason: null, declineNote: null,
    callDuration: "34 min", callDurationMins: 34, callComplete: true, decidedAt: null,
    originalNote: `PATIENT:         Santos, Maria
DATE OF SERVICE: April 21, 2026
TYPE:            Initial Psychiatric Evaluation — Sage Pre-Visit Clinical Brief

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
CLINICAL BRIEF
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
34F presenting with 4 months of progressive MDD and GAD following job change and relocation. Treatment-naive psychiatrically. Currently in weekly CBT with partial benefit. Safety screen negative. Appropriate for outpatient evaluation.

LIKELY DX
#1  Major Depressive Disorder, single episode, moderate
#2  Generalized Anxiety Disorder

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
MEDICATIONS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Current:        Levothyroxine 50mcg — PCP, hypothyroidism, stable
Psychiatric:    None — treatment naive
Prior psych:    None reported
Allergies:      NKDA

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SAFETY
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SI:             Denied
HI:             Denied
SH:             No history
Attempts:       No history
Risk:           Low — outpatient management appropriate
988 Lifeline provided. Patient verbalized understanding.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
HISTORY OF PRESENT ILLNESS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
34 y/o female with no prior psychiatric history, no hospitalizations, no suicide attempts. PMH: hypothyroidism (levothyroxine 50mcg, stable). Presents with 4 months of worsening low mood, anhedonia, fatigue, and persistent polyfocal worry following a job change and relocation. Sleep significantly disrupted — difficulty initiating, early awakening. Concentration and motivation impaired with functional impact on work. No substance use. Family history: maternal MDD with successful sertraline response. Currently in weekly CBT initiated 6 weeks ago with partial benefit. Presenting for medication evaluation.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PLAN
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
- Sertraline 25mg — first-line given maternal response; titrate to 50mg at week 1, target 100–200mg
- Continue weekly CBT — recently initiated, encourage consistency
- PHQ-9, GAD-7, TSH at appointment
- Follow-up in 4 weeks

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
BIOPSYCHOSOCIAL
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Biological:    Hypothyroidism (stable), maternal MDD with sertraline response, no substance use
Psychological: Ruminative worry, anhedonia, good insight, engaged in therapy
Social:        Recent relocation, limited social network — explore at appointment`,
  },
  {
    id: 2,
    name: "Sarah Chen",
    age: "29", gender: "Female",
    phone: "(917) 555-0347",
    date: "Apr 21, 2026", time: "9:14 AM",
    chiefComplaint: "PTSD symptoms, nightmares, hypervigilance — 6 months",
    primaryDiagnosis: "Posttraumatic Stress Disorder (MVA)",
    medicationsCurrent: "None",
    medicationsPrior: "None reported",
    allergies: "NKDA",
    riskLevel: "moderate",
    siFlag: false, hiFlag: false, graveDisabilityFlag: false,
    firearmsAccess: false, pregnancyStatus: null,
    seizureHistory: false, cardiacHistory: false, strokeHistory: false,
    status: "pending", fitScore: "review",
    fitRationale: "Clinical review advised before scheduling. PTSD presentation following motor vehicle accident. Prior trauma history requires direct clarification. No current SI.",
    declineFlag: null, declineReason: null, declineNote: null,
    callDuration: "38 min", callDurationMins: 38, callComplete: true, decidedAt: null,
    originalNote: `PATIENT:         Chen, Sarah
DATE OF SERVICE: April 21, 2026
TYPE:            Initial Psychiatric Evaluation — Sage Pre-Visit Clinical Brief

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
CLINICAL BRIEF
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
29F presenting with PTSD symptoms 6 months following MVA. Treatment-naive psychiatrically. Not currently in therapy. Safety screen negative. Significant functional impairment — not driving since accident. Appropriate for outpatient evaluation.

LIKELY DX
#1  Posttraumatic Stress Disorder (MVA)
R/O Major Depressive Disorder (assess at appointment)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SAFETY
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SI:             Denied
HI:             Denied
Risk:           Moderate — PTSD with significant functional impairment
988 Lifeline provided. Patient verbalized understanding.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
HISTORY OF PRESENT ILLNESS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
29 y/o female with no prior psychiatric history presenting with 6 months of PTSD symptoms following MVA. Intrusive memories, nightmares 3-4 nights/week, hypervigilance, exaggerated startle, driving avoidance. Has not driven since accident. Sleep disrupted. Social withdrawal. No substance use. Not in therapy.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PLAN
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
- Sertraline — first-line for PTSD
- Refer to trauma-focused CBT / EMDR
- Full trauma history at appointment
- PCL-5 at appointment`,
  },
  {
    id: 3,
    name: "James Okafor",
    age: "28", gender: "Male",
    phone: "(718) 555-0521",
    date: "Apr 20, 2026", time: "2:15 PM",
    chiefComplaint: "Mood instability, alcohol use disorder, passive SI",
    primaryDiagnosis: "Bipolar I Disorder, current episode manic",
    medicationsCurrent: "None",
    medicationsPrior: "Lithium — discontinued (tremor, cognitive side effects)",
    allergies: "Lithium (intolerance)",
    riskLevel: "high",
    siFlag: true, hiFlag: false, graveDisabilityFlag: false,
    firearmsAccess: false, pregnancyStatus: null,
    seizureHistory: false, cardiacHistory: false, strokeHistory: false,
    status: "pending", fitScore: "review",
    fitRationale: "Clinical review advised. Dual diagnosis — active mood episode confounded by heavy daily alcohol use. Outpatient appropriate only with sobriety assessment and close follow-up. Consider addiction psychiatry co-management.",
    declineFlag: null, declineReason: null, declineNote: null,
    callDuration: "41 min", callDurationMins: 41, callComplete: true, decidedAt: null,
    originalNote: `PATIENT:         Okafor, James
DATE OF SERVICE: April 20, 2026
TYPE:            Initial Psychiatric Evaluation — Sage Pre-Visit Clinical Brief

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
CLINICAL BRIEF
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
28M with Bipolar I in active manic/hypomanic episode complicated by daily heavy alcohol use and passive SI. Prior hospitalization 2022. Lithium discontinued due to intolerance. High clinical complexity — requires mandatory safety and withdrawal assessment before scheduling.

LIKELY DX
#1  Bipolar I Disorder, current episode manic/hypomanic
#2  Alcohol Use Disorder, moderate-severe

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SAFETY
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SI:             Passive — no plan, no intent
HI:             Denied
Risk:           High — active manic episode, heavy alcohol use, passive SI
988 Lifeline provided. Patient verbalized understanding.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
HISTORY OF PRESENT ILLNESS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
28 y/o male with prior Bipolar I (2022), one voluntary hospitalization. 6 weeks of worsening mood instability — decreased sleep 3-4 hrs/night, racing thoughts, elevated mood, impulsive decisions. Concurrent alcohol escalation to 6-8 drinks/night. Lithium previously discontinued due to tremor and cognitive side effects. Passive SI without plan or intent.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PLAN
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
- Mood stabilizer — valproate or lamotrigine given lithium intolerance
- Sobriety and withdrawal assessment before scheduling
- Consider addiction psychiatry co-management
- Safety plan at appointment`,
  },
  {
    id: 4,
    name: "Derek Walsh",
    age: "29", gender: "Male",
    phone: "(212) 555-0788",
    date: "Apr 20, 2026", time: "1:00 PM",
    chiefComplaint: "Severe depression, active SI with plan and intent",
    primaryDiagnosis: "Major Depressive Disorder, recurrent, severe with SI",
    medicationsCurrent: "None — sertraline discontinued",
    medicationsPrior: "Sertraline — discontinued",
    allergies: "NKDA",
    riskLevel: "high",
    siFlag: true, hiFlag: false, graveDisabilityFlag: false,
    firearmsAccess: false, pregnancyStatus: null,
    seizureHistory: false, cardiacHistory: false, strokeHistory: false,
    status: "pending", fitScore: "decline",
    fitRationale: "Higher level of care indicated. Active SI with specific plan and expressed intent. Inpatient psychiatric stabilization required before outpatient evaluation. Patient directed to nearest emergency department during intake call.",
    declineFlag: null, declineReason: null, declineNote: null,
    callDuration: "29 min", callDurationMins: 29, callComplete: true, decidedAt: null,
    originalNote: `PATIENT:         Walsh, Derek
DATE OF SERVICE: April 20, 2026
TYPE:            Initial Psychiatric Evaluation — Sage Pre-Visit Clinical Brief

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
CLINICAL BRIEF
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
29M with recurrent MDD, two prior hospitalizations, one prior attempt presenting with active SI with plan and intent. NOT appropriate for outpatient management. Patient directed to 988 and nearest ED during intake call.

LIKELY DX
#1  Major Depressive Disorder, recurrent, severe
#2  Active suicidal ideation with plan and intent — crisis level

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SAFETY
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SI:             ACTIVE — specific plan, expressed intent
Hospitalizations: Two — 2021, 2023 (both for SI with plan)
Risk:           CRITICAL — not safe for outpatient management
Patient directed to 988 Lifeline and nearest ED during intake call.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PLAN
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
- Higher level of care indicated — NOT appropriate for outpatient evaluation
- Inpatient psychiatric stabilization required
- Patient directed to 988 and nearest ED during intake call
- Outpatient evaluation only after inpatient discharge and confirmed stability`,
  },
  {
    id: 5,
    name: "Kevin Tran",
    age: "41", gender: "Male",
    phone: "(347) 555-0634",
    date: "Apr 20, 2026", time: "3:30 PM",
    chiefComplaint: "Depression, opioid use disorder, daily heroin use",
    primaryDiagnosis: "Opioid Use Disorder, severe",
    medicationsCurrent: "Heroin — daily IV, 1-2 bags/day",
    medicationsPrior: "None",
    allergies: "Unknown",
    riskLevel: "high",
    siFlag: false, hiFlag: false, graveDisabilityFlag: false,
    firearmsAccess: false, pregnancyStatus: null,
    seizureHistory: false, cardiacHistory: false, strokeHistory: false,
    status: "pending", fitScore: "decline",
    fitRationale: "Higher level of care indicated. Primary opioid use disorder with daily IV heroin use and physical dependence requires medical detoxification before psychiatric treatment.",
    declineFlag: null, declineReason: null, declineNote: null,
    callDuration: "43 min", callDurationMins: 43, callComplete: true, decidedAt: null,
    originalNote: `PATIENT:         Tran, Kevin
DATE OF SERVICE: April 20, 2026
TYPE:            Initial Psychiatric Evaluation — Sage Pre-Visit Clinical Brief

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
CLINICAL BRIEF
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
41M with severe OUD (daily IV heroin, 3 years) and comorbid depression presenting motivated for sobriety. Not appropriate for outpatient psychiatry until medical detox completed. Referred to OTP/methadone clinic.

LIKELY DX
#1  Opioid Use Disorder, severe
R/O Major Depressive Disorder (defer — cannot assess in context of active use)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SAFETY
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SI:             Denied
HI:             Denied
Risk:           High — active IV opioid use, physical dependence, withdrawal risk
SAMHSA Helpline provided (1-800-662-4357).

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
HISTORY OF PRESENT ILLNESS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
41 y/o male with no prior formal psychiatric treatment presenting for depression and opioid use. Daily IV heroin 3 years, currently 1-2 bags/day with significant withdrawal symptoms. Was using morning of intake call. Low mood, low energy, hopelessness. Motivated for change. No support system. Unstable housing.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PLAN
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
- Not appropriate for outpatient psychiatry at this time
- Refer to OTP or methadone clinic for MAT
- Hepatitis C referral — infectious disease or PCP
- Psychiatric evaluation after 30-90 days sobriety`,
  },
];

// ─── PILLS ────────────────────────────────────────────────────────────────────

const RISK_S = {
  low:      [C.sageLight, C.sageBorder, C.sageDark],
  moderate: [C.amberLight, C.amberBorder, C.amber],
  high:     [C.redLight, C.redBorder, C.red],
};
const FIT_S = {
  accept:  [C.sageLight,  C.sageBorder,  C.sageDark, "Appropriate"],
  review:  [C.amberLight, C.amberBorder, C.amber,    "Review advised"],
  decline: [C.redLight,   C.redBorder,   C.red,      "Not appropriate"],
};

// Text-only FIT label — no pill, no background
const FitLabel = ({ score, rationale }) => {
  const [show, setShow] = useState(false);
  const map = {
    accept:  { color: C.sageDark,  icon: "✓", text: "Appropriate"     },
    review:  { color: C.amber,     icon: "⚠", text: "Review advised"  },
    decline: { color: C.red,       icon: "✕", text: "Not appropriate" },
  };
  const f = map[score];
  if (!f) return <span style={{ color: C.gray300, fontSize: "13px" }}>—</span>;
  const safeRationale = rationale && rationale !== "undefined" && rationale !== "null" ? rationale : null;
  return (
    <div style={{ position: "relative", display: "inline-block" }} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <span style={{ fontSize: "12px", fontWeight: 600, color: f.color, fontFamily: fonts.body, cursor: safeRationale ? "help" : "default", whiteSpace: "nowrap" }}>
        {f.icon} {f.text}{safeRationale ? " ⓘ" : ""}
      </span>
      {show && safeRationale && (
        <div style={{ position: "absolute", bottom: "calc(100% + 6px)", right: 0, background: C.black, color: "#E8E8E8", fontSize: "13px", lineHeight: 1.6, padding: "10px 14px", borderRadius: "6px", width: "240px", zIndex: 999, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", pointerEvents: "none", fontFamily: fonts.body }}>
          {safeRationale}
        </div>
      )}
    </div>
  );
};

const Pill = ({ bg, border, color, text, style = {} }) => (
  <span style={{ background: bg, border: `1px solid ${border}`, color, fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", padding: "2px 8px", borderRadius: "100px", whiteSpace: "nowrap", fontFamily: fonts.body, ...style }}>{text}</span>
);

const RiskPill = ({ level }) => { const [bg,b,c] = RISK_S[level] || [C.gray100, C.gray200, C.gray400]; const label = level ? level.charAt(0).toUpperCase() + level.slice(1) + " Risk" : "Unknown"; return <Pill bg={bg} border={b} color={c} text={label} />; };
const FitPill  = ({ score }) => { const [bg,b,c,t] = FIT_S[score]; return <Pill bg={bg} border={b} color={c} text={t} />; };
const SIFlag   = ({ present }) => present
  ? <Pill bg={C.redLight} border={C.redBorder} color={C.red} text="⚠ SI" />
  : <span style={{ color: C.gray300, fontSize: "13px", fontFamily: fonts.body }}>—</span>;
const HIFlag        = ({ present }) => present ? <Pill bg={C.redLight} border={C.redBorder} color={C.red} text="⚠ HI" /> : null;
const GDFlag        = ({ present }) => present ? <Pill bg={C.redLight} border={C.redBorder} color={C.red} text="⚠ GD" /> : null;
const FirearmFlag   = ({ present }) => present ? <Pill bg={C.amberLight} border={C.amberBorder} color={C.amber} text="Firearm" /> : null;
const PregnancyFlag = ({ present }) => present ? <Pill bg={C.amberLight} border={C.amberBorder} color={C.amber} text="Pregnant" /> : null;

const StatusPill = ({ status, smsSent, intakeComplete }) => {
  if (status === "reviewed") {
    if (intakeComplete) return (
      <span style={{ fontSize: "12px", fontWeight: 600, color: C.sageDark, fontFamily: fonts.body }}>
        ✓ Intake Complete
      </span>
    );
    return (
      <span style={{ fontSize: "12px", fontWeight: 600, color: C.amber, fontFamily: fonts.body }}>
        Scheduled
      </span>
    );
  }
  if (status === "accepted") {
    if (smsSent) return (
      <span style={{ fontSize: "12px", fontWeight: 600, color: C.sageDark, fontFamily: fonts.body }}>
        SMS Sent
      </span>
    );
    return (
      <span style={{ fontSize: "12px", fontWeight: 600, color: C.amber, fontFamily: fonts.body }}>
        Outreach needed
      </span>
    );
  }
  if (status === "declined") return (
    <span style={{ fontSize: "12px", fontWeight: 600, color: C.red, fontFamily: fonts.body }}>
      Deferred
    </span>
  );
  return null;
};

// ─── SCREENING TOOLTIP ───────────────────────────────────────────────────────

// ─── ROI WIDGET ───────────────────────────────────────────────────────────────

function ROIWidget({ patients, physicianRate = 350 }) {
  const totalCalls   = patients.length;
  const totalMins    = patients.reduce((s, p) => s + (p.callDurationMins || 0), 0);
  const avgMins      = totalCalls > 0 ? (totalMins > 0 ? Math.round(totalMins / totalCalls) : 37) : 0;
  const sageMins     = totalMins > 0 ? totalMins : totalCalls * 37;
  const hoursSaved   = (sageMins / 60).toFixed(1);
  const dollarsSaved = Math.round((sageMins / 60) * physicianRate);

  const accepted = patients.filter(p => p.status === "accepted" || p.status === "reviewed").length;
  const deferred = patients.filter(p => p.status === "declined").length;
  const pending  = patients.filter(p => p.status === "pending").length;
  const acceptRate = totalCalls > 0 ? Math.round((accepted / totalCalls) * 100) : 0;

  return (
    <div style={{ marginBottom: "28px", maxWidth: "760px" }}>

      {/* Primary ROI card — the headline number */}
      {totalCalls > 0 && (
        <div style={{ background: C.sageLight, border: `1px solid ${C.sageBorder}`, borderRadius: "10px", padding: "20px 24px", marginBottom: "12px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: C.sage, marginBottom: "16px", fontFamily: fonts.body, textTransform: "uppercase", letterSpacing: "0.1em" }}>
            Time &amp; value returned this week
          </div>
          <div style={{ display: "flex", gap: "32px", flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <div style={{ fontSize: "44px", fontWeight: 700, color: C.sageDark, letterSpacing: "-0.04em", lineHeight: 1, fontFamily: fonts.body }}>{hoursSaved}<span style={{ fontSize: "22px", fontWeight: 400, marginLeft: "4px" }}>hrs</span></div>
              <div style={{ fontSize: "13px", color: C.gray500, fontFamily: fonts.body, marginTop: "5px" }}>across {totalCalls} intake{totalCalls !== 1 ? "s" : ""} · avg {avgMins} min each</div>
            </div>
            <div style={{ width: "1px", background: C.sageBorder, alignSelf: "stretch", minHeight: "40px" }} />
            <div>
              <div style={{ fontSize: "44px", fontWeight: 700, color: C.sageDark, letterSpacing: "-0.04em", lineHeight: 1, fontFamily: fonts.body }}>${dollarsSaved.toLocaleString()}</div>
              <div style={{ fontSize: "13px", color: C.gray500, fontFamily: fonts.body, marginTop: "5px" }}>of clinical time saved · ${physicianRate}/hr</div>
            </div>
          </div>
        </div>
      )}

      {/* Outcome breakdown — compact, no redundancy */}
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {[
          { val: totalCalls, label: "Screened", color: C.black },
          { val: `${accepted} (${acceptRate}%)`, label: "Accepted", color: accepted > 0 ? C.sageDark : C.black },
          { val: deferred,   label: "Deferred",  color: C.black },
          { val: pending,    label: "Pending",   color: pending > 0 ? C.amber : C.black },
        ].map((s, i) => (
          <div key={i} style={{ flex: 1, minWidth: "100px", padding: "12px 14px", background: C.paperDark, borderRadius: "8px", border: `1px solid ${C.gray200}` }}>
            <div style={{ fontSize: "22px", fontWeight: 700, color: s.color, letterSpacing: "-0.03em", lineHeight: 1, fontFamily: fonts.body }}>{s.val}</div>
            <div style={{ fontSize: "11px", fontWeight: 600, color: C.gray400, textTransform: "uppercase", letterSpacing: "0.08em", marginTop: "4px", fontFamily: fonts.body }}>{s.label}</div>
          </div>
        ))}
      </div>

    </div>
  );
}

// ─── VOICE CALL VISUAL ───────────────────────────────────────────────────────

function VoiceCallVisual() {
  const [tick, setTick] = useState(0);
  const BAR_COUNT = 28;

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 120);
    return () => clearInterval(id);
  }, []);

  const heights = Array.from({ length: BAR_COUNT }, (_, i) => {
    const seed = Math.sin(tick * 0.4 + i * 0.7) * 0.5 +
                 Math.sin(tick * 0.2 + i * 1.3) * 0.3 +
                 Math.sin(tick * 0.6 + i * 0.4) * 0.2;
    return Math.round(6 + ((seed + 1) / 2) * 26);
  });

  return (
    <div style={{ marginTop: "20px" }}>
      {/* Phone card */}
      <div style={{
        background: C.paperDark,
        border: `1px solid ${C.gray200}`,
        borderRadius: "17px",
        padding: "28px 24px",
        boxShadow: "0 4px 20px rgba(92,122,98,0.08)",
      }}>
        {/* Call header */}
        <div style={{ display: "flex", alignItems: "center", gap: "17px", marginBottom: "24px" }}>
          <div style={{
            width: "44px", height: "44px", borderRadius: "50%",
            background: C.sageLight, border: `2px solid ${C.sageBorder}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <span style={{ fontSize: "18px" }}>📞</span>
          </div>
          <div>
            <div style={{ fontSize: "15px", fontWeight: 500, color: C.black, fontFamily: fonts.body, marginBottom: "2px" }}>Sage</div>
            <div style={{ fontSize: "16px", color: C.sage, fontFamily: fonts.body, letterSpacing: "0.04em" }}>PsychXAI Intake Specialist</div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "6px" }}>
            <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: C.sage, animation: "sagePulse 1.8s ease-in-out infinite" }} />
            <span style={{ fontSize: "16px", color: C.sage, fontFamily: fonts.body }}>In call</span>
          </div>
        </div>

        {/* Waveform */}
        <div style={{
          background: C.sageLight,
          borderRadius: "17px",
          padding: "18px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "3px",
          marginBottom: "20px",
          minHeight: "64px",
        }}>
          {heights.map((h, i) => (
            <div
              key={i}
              style={{
                width: "3px",
                height: `${h}px`,
                borderRadius: "2px",
                background: C.sage,
                opacity: 0.5 + (h / 32) * 0.5,
                transition: "height 0.12s ease",
              }}
            />
          ))}
        </div>

        {/* Opening line */}
        <div style={{
          background: C.paperDark,
          borderRadius: "8px",
          padding: "12px 16px",
          marginBottom: "20px",
          borderLeft: `3px solid ${C.sageBorder}`,
        }}>
          <div style={{ fontSize: "17px", color: C.sage, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "5px", fontFamily: fonts.body, fontWeight: 600 }}>Sage</div>
          <div style={{ fontSize: "16px", color: C.gray700, fontFamily: fonts.body, lineHeight: 1.6, fontStyle: "italic" }}>
            "Hi, I'm Sage — I'm an intake specialist here to help your psychiatrist get to know you before your appointment. Is this a good time to talk?"
          </div>
        </div>

        {/* Call stats */}
        <div style={{ display: "flex", gap: "0px", borderTop: `1px solid ${C.gray100}`, paddingTop: "16px" }}>
          {[
            { value: "30–45 min", label: "Avg call duration" },
            { value: "DSM-5", label: "Clinical framework" },
            { value: "24 / 7", label: "Always available" },
          ].map((s, i) => (
            <div key={i} style={{ flex: 1, textAlign: "center", borderRight: i < 2 ? `1px solid ${C.gray200}` : "none", paddingRight: i < 2 ? "16px" : "0", paddingLeft: i > 0 ? "16px" : "0" }}>
              <div style={{ fontSize: "17px", fontWeight: 500, color: C.sageDark, fontFamily: fonts.body, marginBottom: "2px" }}>{s.value}</div>
              <div style={{ fontSize: "17px", color: C.gray400, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Caption */}
      <div style={{ textAlign: "center", marginTop: "17px", fontSize: "16px", color: C.gray400, fontFamily: fonts.body, fontStyle: "italic" }}>
        Full psychiatric intake across all clinical domains — HPI, ROS, safety screen, history, and more
      </div>

      <style>{`
        @keyframes sagePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.85); }
        }
      `}</style>
    </div>
  );
}

// ─── ONBOARDING ───────────────────────────────────────────────────────────────

function Onboarding({ clinic, onComplete }) {
  const [step, setStep] = useState(-1); // -1 = welcome
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(clinic.intakeNumber); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const steps = [
    {
      number: "01",
      title: "Your patient intake line is ready",
      body: "Sage answers every new patient call automatically — 24 hours a day, 7 days a week. No staff required.",
      content: (
        <div style={{ background: C.paperDark, border: `1px solid ${C.gray200}`, borderRadius: "17px", padding: "20px 24px", marginTop: "20px" }}>
          <div style={{ fontSize: "17px", color: C.gray400, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "8px", fontFamily: fonts.body }}>Your dedicated intake line</div>
          <div style={{ display: "flex", alignItems: "center", gap: "17px" }}>
            <div style={{ fontSize: "28px", fontWeight: 400, color: C.black, letterSpacing: "0.06em", fontFamily: fonts.body }}>{clinic.intakeNumber}</div>
            <button onClick={copy} style={{ background: copied ? C.sageLight : C.gray100, border: `1px solid ${copied ? C.sageBorder : C.gray200}`, borderRadius: "6px", padding: "7px 14px", color: copied ? C.sageDark : C.black, fontSize: "16px", cursor: "pointer", fontFamily: fonts.body, transition: "all 0.15s" }}>
              {copied ? "✓ Copied" : "Copy number"}
            </button>
          </div>
          <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: `1px solid ${C.gray100}` }}>
            <div style={{ fontSize: "16px", color: C.black, lineHeight: 1.8, fontFamily: fonts.body }}>
              Share this number with new patients by:<br />
              · Adding it to your website's New Patients page<br />
              · Including it in referral instructions to PCPs and therapists<br />
              · Giving it to front desk for all new patient inquiries
            </div>
          </div>
        </div>
      ),
    },
    {
      number: "02",
      title: "What your patients experience",
      body: "Your patient receives a call. Sage conducts the full psychiatric intake by voice — warm, clinical, one question at a time.",
      content: <VoiceCallVisual />,
    },
    {
      number: "03",
      title: "What you see after every call",
      body: "A complete screening assessment and clinical note — waiting before you open the chart.",
      content: (
        <div style={{ marginTop: "20px", background: C.paperDark, border: `1px solid ${C.gray200}`, borderRadius: "17px", overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.gray100}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
            <div>
              <div style={{ fontSize: "17px", fontWeight: 500, color: C.black, fontFamily: fonts.body }}>Santos, Maria</div>
              <div style={{ fontSize: "16px", color: C.gray400, fontFamily: fonts.body }}>34F · Apr 21, 2026 · 34 min · Complete</div>
            </div>
            <div style={{ display: "flex", gap: "6px" }}>
              <Pill bg={C.sageLight} border={C.sageBorder} color={C.sageDark} text="low risk" />
              <Pill bg={C.sageLight} border={C.sageBorder} color={C.sageDark} text="Appropriate for Intake" />
            </div>
          </div>
          <div style={{ padding: "14px 18px" }}>
            <div style={{ fontSize: "16px", color: C.black, lineHeight: 1.8, marginBottom: "17px", fontFamily: fonts.body }}>
              <span style={{ color: C.black, fontWeight: 600 }}>Chief complaint: </span>Depression, anxiety, low mood — 4 months<br />
              <span style={{ color: C.black, fontWeight: 600 }}>Screening: </span>Appropriate for outpatient intake. MDD + GAD, no SI, no substances.<br />
              <span style={{ color: C.black, fontWeight: 600 }}>Working diagnoses: </span>MDD single episode moderate · GAD
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <div style={{ flex: 1, background: C.sageLight, border: `1px solid ${C.sageBorder}`, borderRadius: "5px", padding: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "16px", fontWeight: 700, color: C.sageDark, fontFamily: fonts.body }}>Accept</div>
              </div>
              <div style={{ flex: 1, background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: "5px", padding: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "16px", fontWeight: 700, color: C.red, fontFamily: fonts.body }}>Defer</div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  // Welcome screen
  if (step === -1) return (
    <div style={{ minHeight: "100vh", background: C.sageLight, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: fonts.body, padding: "24px" }}>
      <div style={{ width: "100%", maxWidth: "460px", textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: "17px", marginBottom: "40px" }}>
          <div style={{ width: "32px", height: "32px", background: C.sage, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: C.white, fontSize: "16px", fontWeight: 600, fontFamily: fonts.body }}>P</span>
          </div>
          <span style={{ fontSize: "18px", fontWeight: 500, color: C.black, fontFamily: fonts.body }}>PsychXAI</span>
        </div>
        <h1 style={{ fontSize: "32px", fontWeight: 300, color: C.black, margin: "0 0 16px", letterSpacing: "-0.01em", lineHeight: 1.25, fontFamily: fonts.body }}>
          Welcome, {clinic.physician.split(",")[0]}.
        </h1>
        <p style={{ fontSize: "17px", color: C.black, lineHeight: 1.8, margin: "0 0 32px", fontFamily: fonts.body }}>
          Your practice is set up. Sage is ready to take calls.<br />
          Here's how the next 3 minutes work — we'll show you exactly what your patients experience and what you'll see when they're done.
        </p>
        <button onClick={() => setStep(0)} style={{ background: C.sage, border: "none", borderRadius: "8px", padding: "12px 32px", color: C.white, fontSize: "17px", fontWeight: 500, cursor: "pointer", fontFamily: fonts.body }}>
          Let's begin →
        </button>
      </div>
    </div>
  );

  const current = steps[step];
  const isLast  = step === steps.length - 1;

  return (
    <div style={{ minHeight: "100vh", background: C.paper, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: fonts.body, padding: "24px" }}>
      <div style={{ width: "100%", maxWidth: "600px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "17px", marginBottom: "40px" }}>
          <div style={{ width: "24px", height: "24px", background: C.sage, borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: C.white, fontSize: "17px", fontWeight: 600, fontFamily: fonts.body }}>P</span>
          </div>
          <span style={{ fontSize: "15px", fontWeight: 500, color: C.black, fontFamily: fonts.body }}>PsychXAI</span>
        </div>

        {/* Progress */}
        <div style={{ display: "flex", gap: "6px", marginBottom: "32px" }}>
          {steps.map((_, i) => (
            <div key={i} style={{ flex: 1, height: "3px", borderRadius: "2px", background: i <= step ? C.sage : C.gray200, transition: "background 0.3s" }} />
          ))}
        </div>

        <div style={{ marginBottom: "4px" }}>
          <div style={{ fontSize: "17px", color: C.gray400, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: "17px", fontFamily: fonts.body }}>Step {current.number} of {steps.length}</div>
          <h2 style={{ fontSize: "24px", fontWeight: 300, color: C.black, margin: "0 0 10px", letterSpacing: "-0.01em", fontFamily: fonts.body }}>{current.title}</h2>
          <p style={{ fontSize: "16px", color: C.black, margin: 0, lineHeight: 1.7, fontFamily: fonts.body }}>{current.body}</p>
        </div>

        {current.content}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "28px" }}>
          <button onClick={() => step > 0 && setStep(step - 1)} style={{ background: "transparent", border: "none", color: step === 0 ? C.gray200 : C.gray400, fontSize: "17px", cursor: step === 0 ? "default" : "pointer", fontFamily: fonts.body, padding: 0 }}>
            ← Back
          </button>
          <button onClick={() => isLast ? onComplete() : setStep(step + 1)} style={{ background: C.sage, border: "none", borderRadius: "6px", padding: "10px 24px", color: C.white, fontSize: "16px", fontWeight: 500, cursor: "pointer", fontFamily: fonts.body }}>
            {isLast ? "Go to dashboard →" : "Next →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── INTAKE SHARE BUTTONS ─────────────────────────────────────────────────────

function IntakeShareButtons({ intakeNumber, physician, slug }) {
  const [copied, setCopied] = useState(false);
  const doctorName = physician || "your doctor";
  const formatted  = formatPhone(intakeNumber);
  const template   = `Hi [Patient Name], before your first appointment please call ${formatted} to complete a brief intake with Sage, our AI assistant. It takes about 20 minutes and helps ${doctorName} prepare for your visit.`;

  const handleCopy = () => {
    navigator.clipboard.writeText(template);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleOpenMessages = () => {
    window.open(`sms:&body=${encodeURIComponent(template)}`, "_blank");
  };

  return (
    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
      <button onClick={handleCopy} style={{ background: copied ? C.sage : C.white, border: `1px solid ${copied ? C.sage : C.sageBorder}`, borderRadius: "6px", padding: "8px 16px", color: copied ? C.white : C.sageDark, fontSize: "15px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body, transition: "all 0.15s", whiteSpace: "nowrap" }}>
        {copied ? "✓ Copied" : "Copy message"}
      </button>
      <button onClick={handleOpenMessages} style={{ background: C.sageDeep, border: "none", borderRadius: "6px", padding: "8px 16px", color: C.white, fontSize: "15px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: "6px" }}
        onMouseEnter={e => e.currentTarget.style.background = C.sageDark}
        onMouseLeave={e => e.currentTarget.style.background = C.sageDeep}>
        <span>✉</span> Open in Messages
      </button>
    </div>
  );
}

// ─── EMPTY STATE ──────────────────────────────────────────────────────────────

// ─── AI LOADING SCREEN ────────────────────────────────────────────────────────

function AILoader({ message = "Preparing your dashboard..." }) {
  const [dots, setDots] = useState("");
  const [msgIdx, setMsgIdx] = useState(0);
  const messages = [
    "Connecting to PsychXAI...",
    "Waking up Sage...",
    "Loading your practice...",
    "Preparing clinical notes...",
  ];

  useEffect(() => {
    const d = setInterval(() => setDots(p => p.length >= 3 ? "" : p + "."), 400);
    const m = setInterval(() => setMsgIdx(p => (p + 1) % messages.length), 1800);
    return () => { clearInterval(d); clearInterval(m); };
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: C.paper, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: fonts.body }}>
      {/* Animated waveform logo */}
      <div style={{ marginBottom: "28px", position: "relative" }}>
        <svg width="52" height="52" viewBox="0 0 32 32" fill="none" style={{ animation: "pulse 1.5s ease-in-out infinite" }}>
          <rect width="32" height="32" rx="8" fill="#1E3524"/>
          <rect x="4" y="6" width="3.5" height="20" rx="1.5" fill="white" style={{ animation: "wave1 0.8s ease-in-out infinite alternate" }}/>
          <rect x="10" y="12" width="2.5" height="14" rx="1.2" fill="white" opacity="0.75" style={{ animation: "wave2 0.8s ease-in-out infinite alternate 0.1s" }}/>
          <rect x="14" y="9" width="2.5" height="11" rx="1.2" fill="white" opacity="0.9" style={{ animation: "wave3 0.8s ease-in-out infinite alternate 0.2s" }}/>
          <rect x="18" y="12" width="2.5" height="8" rx="1.2" fill="white" opacity="0.75" style={{ animation: "wave2 0.8s ease-in-out infinite alternate 0.3s" }}/>
          <rect x="23" y="16" width="2" height="6" rx="1" fill="white" opacity="0.45" style={{ animation: "wave1 0.8s ease-in-out infinite alternate 0.4s" }}/>
        </svg>
      </div>
      <div style={{ fontSize: "17px", fontWeight: 600, color: C.black, marginBottom: "8px", letterSpacing: "-0.01em" }}>PsychXAI</div>
      <div style={{ fontSize: "17px", color: C.gray400, minWidth: "200px", textAlign: "center" }}>
        {messages[msgIdx]}{dots}
      </div>
    </div>
  );
}




// ─── SAGE DEMO PLAYER ─────────────────────────────────────────────────────────

function SageDemoPlayer() {
  const [playing, setPlaying]   = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef                = useRef(null);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play();
      setPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setProgress(audio.currentTime / audio.duration);
  };

  const handleEnded = () => {
    setPlaying(false);
    setProgress(0);
    if (audioRef.current) audioRef.current.currentTime = 0;
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    audio.currentTime = pct * audio.duration;
    setProgress(pct);
  };

  const formatTime = (secs) => {
    if (!secs || isNaN(secs)) return "0:00";
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const elapsed = duration ? progress * duration : 0;

  return (
    <div style={{
      background: "#FFFFFF",
      border: "1px solid #DDE0DA",
      borderRadius: "16px",
      padding: "28px 32px",
      marginBottom: "28px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    }}>
      <audio
        ref={audioRef}
        src="/sage_demo.wav"
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onLoadedMetadata={handleLoadedMetadata}
        preload="metadata"
      />

      {/* Label */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: "10px" }}>
        <span style={{ fontSize: "12px", color: "#A8ADA4" }}>
          {formatTime(elapsed)} / {formatTime(duration)}
        </span>
      </div>

      {/* Waveform progress bar */}
      <div
        onClick={handleSeek}
        style={{ height: "6px", background: "#ECEEE9", borderRadius: "3px", marginBottom: "20px", cursor: "pointer", position: "relative" }}
      >
        <div style={{ height: "100%", width: `${progress * 100}%`, background: "#3D6B47", borderRadius: "2px", transition: "width 0.1s linear" }} />
      </div>

      {/* Controls */}
      <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
        <button
          onClick={toggle}
          style={{
            width: "56px", height: "56px", borderRadius: "50%",
            background: "#3D6B47", border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, transition: "background 0.15s",
          }}
        >
          {playing
            ? <svg width="18" height="18" viewBox="0 0 14 14" fill="white"><rect x="2" y="1" width="4" height="12" rx="1"/><rect x="8" y="1" width="4" height="12" rx="1"/></svg>
            : <svg width="18" height="18" viewBox="0 0 14 14" fill="white"><path d="M3 1.5L12.5 7 3 12.5V1.5z"/></svg>
          }
        </button>
        <div>
          <div style={{ fontSize: "14px", fontWeight: 500, color: "#1C1F1A", lineHeight: 1.3 }}>
            {playing ? "Sage is speaking…" : "Hear a sample call"}
          </div>
          <div style={{ fontSize: "12px", color: "#A8ADA4", marginTop: "2px" }}>
            An example of your actual intake experience
          </div>
        </div>
      </div>
    </div>
  );
}

function IntakeLandingPage({ slug }) {
  const [clinic, setClinic]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [calling, setCalling]         = useState(false);
  const [textSent, setTextSent]       = useState(false);
  const [typedTitle, setTypedTitle]   = useState("");
  const [doneTitleTyping, setDoneTitleTyping] = useState(false);
  const [returnedAfterCall, setReturnedAfterCall] = useState(false);
  const [confirmed, setConfirmed]     = useState(() => {
    try { return localStorage.getItem(`intake_confirmed_${slug}`) === "true"; } catch { return false; }
  });
  const [showSecurity, setShowSecurity] = useState(false);
  const isMobile                      = useMobile();

  useEffect(() => {
    if (!slug) { setNotFound(true); setLoading(false); return; }
    
    // Demo fallback — no Supabase needed
    if (slug === "janedoe") {
      setClinic({ name: "Demo Practice", physician_name: "Dr. Jane Doe, MD", intake_number: "+15550100001" });
      setLoading(false);
      return;
    }

    if (!supabase) { setNotFound(true); setLoading(false); return; }
    supabase
      .from("clinics")
      .select("name, physician_name, intake_number")
      .eq("slug", slug)
      .maybeSingle()
      .then(({ data, error }) => {
        console.log("Intake query:", data, error);
        if (!data) { setNotFound(true); } else { setClinic(data); }
        setLoading(false);
      });
  }, [slug]);

  const phone    = clinic?.intake_number;
  const formatted = formatPhone(phone);

  // Typing animation — runs when clinic loads
  useEffect(() => {
    if (!clinic) return;
    const rawDoc = clinic.physician_name
      ? clinic.physician_name.replace(/,?\s*(MD|DO|NP|PA|PhD|PsyD)\.?$/i, "").trim()
      : clinic.name;
    const title = `Before your first appointment with ${rawDoc}`;
    let i = 0;
    setTypedTitle("");
    setDoneTitleTyping(false);
    const interval = setInterval(() => {
      if (i < title.length) {
        setTypedTitle(title.slice(0, i + 1));
        i++;
      } else {
        clearInterval(interval);
        setDoneTitleTyping(true);
      }
    }, 38);
    return () => clearInterval(interval);
  }, [clinic?.name]);

  // Detect when patient leaves and returns to page after calling
  useEffect(() => {
    if (confirmed) return;
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && calling) {
        setReturnedAfterCall(true);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [calling, confirmed]);

  const handleConfirm = () => {
    try { localStorage.setItem(`intake_confirmed_${slug}`, "true"); } catch {}
    setConfirmed(true);
    setReturnedAfterCall(false);
  };

  const handleCallAgain = () => {
    setReturnedAfterCall(false);
    setCalling(false);
  };

  const handleCall = () => {
    setCalling(true);
    window.location.href = `tel:${phone}`;
    setTimeout(() => setCalling(false), 3000);
  };

  const displayName = (name) => {
    if (!name) return "your doctor";
    return name.replace(/,?\s*(MD|DO|NP|PA|PhD|PsyD)\.?$/i, "").trim();
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#F5F2EA", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif" }}>
      <svg width="36" height="36" viewBox="0 0 32 32" fill="none" style={{ marginBottom: "16px", opacity: 0.6 }}>
        <rect width="32" height="32" rx="8" fill="#1E3524"/>
        <rect x="4" y="6" width="3.5" height="20" rx="1.5" fill="white"/>
        <rect x="10" y="12" width="2.5" height="14" rx="1.2" fill="white" opacity="0.75"/>
        <rect x="14" y="9" width="2.5" height="11" rx="1.2" fill="white" opacity="0.9"/>
        <rect x="18" y="12" width="2.5" height="8" rx="1.2" fill="white" opacity="0.75"/>
      </svg>
      <div style={{ fontSize: "15px", color: "#7A9E82" }}>Loading...</div>
    </div>
  );

  if (notFound) return (
    <div style={{ minHeight: "100vh", background: "#F5F2EA", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px", fontFamily: "Inter, sans-serif" }}>
      <div style={{ fontSize: "17px", fontWeight: 600, color: "#1C1F1A", marginBottom: "8px" }}>Page not found</div>
      <div style={{ fontSize: "15px", color: "#7A7F76" }}>Please check your link or contact your provider's office.</div>
    </div>
  );

  const docName = displayName(clinic.physician_name);

  const handleTextMyself = () => {
    const msg = encodeURIComponent(`Here's your intake link for ${clinic.name}: ${window.location.href}`);
    window.location.href = `sms:?body=${msg}`;
    setTextSent(true);
    setTimeout(() => setTextSent(false), 3000);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F5F2EA", fontFamily: "'Inter', 'Helvetica Neue', sans-serif", display: "flex", flexDirection: "column" }}>

      {/* Header */}
      <div style={{ padding: "20px 32px", display: "flex", alignItems: "center", gap: "8px" }}>
        <svg width="26" height="26" viewBox="0 0 32 32" fill="none">
          <rect width="32" height="32" rx="8" fill="#1E3524"/>
          <rect x="4" y="6" width="3.5" height="20" rx="1.5" fill="white"/>
          <rect x="10" y="12" width="2.5" height="14" rx="1.2" fill="white" opacity="0.75"/>
          <rect x="14" y="9" width="2.5" height="11" rx="1.2" fill="white" opacity="0.9"/>
          <rect x="18" y="12" width="2.5" height="8" rx="1.2" fill="white" opacity="0.75"/>
        </svg>
        <span style={{ fontSize: "14px", fontWeight: 600, color: "#4A4F46" }}>PsychXAI</span>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: isMobile ? "40px 20px" : "60px 24px 40px" }}>
        <div style={{ width: "100%", maxWidth: "600px" }}>

          {/* Progress indicator */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ width: "22px", height: "22px", borderRadius: "50%", background: "#3D6B47", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: "11px", color: "white", fontWeight: 700 }}>1</span>
              </div>
              <span style={{ fontSize: "13px", color: "#3D6B47", fontWeight: 600, fontFamily: "inherit" }}>Phone intake</span>
            </div>
            <div style={{ width: "32px", height: "1px", background: "#DDE0DA" }} />
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ width: "22px", height: "22px", borderRadius: "50%", background: "#DDE0DA", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: "11px", color: "#A8ADA4", fontWeight: 700 }}>2</span>
              </div>
              <span style={{ fontSize: "13px", color: "#A8ADA4", fontFamily: "inherit" }}>Dr. reviews &amp; confirms</span>
            </div>
          </div>

          {/* Headline — typing animation */}
          <div style={{ textAlign: "center", marginBottom: isMobile ? "32px" : "48px", width: "100%", display: "flex", justifyContent: "center" }}>
            <div style={{ fontSize: isMobile ? "20px" : "28px", fontWeight: 700, color: "#1C1F1A", letterSpacing: "-0.02em", lineHeight: 1.3, whiteSpace: "nowrap", textAlign: "center", width: "100%" }}>
              {typedTitle}
              {!doneTitleTyping && (
                <span style={{ display: "inline-block", width: "2px", height: isMobile ? "20px" : "28px", background: "#3D6B47", marginLeft: "2px", verticalAlign: "middle", animation: "blink 0.8s step-end infinite" }} />
              )}
            </div>
          </div>
          <style>{`@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} } @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.7} }`}</style>

          {/* Demo player */}
          <SageDemoPlayer />

          {/* CTA — changes based on state */}
          {confirmed ? (
            <div style={{ textAlign: "center", padding: "32px 24px", background: "#F0F5F1", border: "1px solid #C8DDD0", borderRadius: "14px", marginBottom: "24px" }}>
              <div style={{ fontSize: "22px", fontWeight: 700, color: "#2A4A31", marginBottom: "10px" }}>Thank you.</div>
              <div style={{ fontSize: "16px", color: "#4A4F46", lineHeight: 1.6 }}>
                {docName} will review your intake before your appointment.
              </div>
              <button
                onClick={() => { try { localStorage.removeItem(`intake_confirmed_${slug}`); } catch {} setConfirmed(false); setCalling(false); setReturnedAfterCall(false); }}
                style={{ marginTop: "16px", background: "transparent", border: "none", fontSize: "13px", color: "#A8ADA4", cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}
              >
                I still need to complete my intake
              </button>
            </div>
          ) : returnedAfterCall ? (
            <div style={{ textAlign: "center", padding: "24px", background: "#FAFAF8", border: "1px solid #DDE0DA", borderRadius: "14px", marginBottom: "24px" }}>
              <div style={{ fontSize: "16px", color: "#4A4F46", marginBottom: "16px", lineHeight: 1.5 }}>
                Did you complete your intake call?
              </div>
              <button
                onClick={handleConfirm}
                style={{ display: "block", width: "100%", background: "#3D6B47", border: "none", borderRadius: "10px", padding: "14px", color: "white", fontSize: "16px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: "10px" }}
              >
                Yes, I completed my call
              </button>
              <button
                onClick={handleCallAgain}
                style={{ display: "block", width: "100%", background: "transparent", border: "1px solid #DDE0DA", borderRadius: "10px", padding: "14px", color: "#4A4F46", fontSize: "15px", cursor: "pointer", fontFamily: "inherit" }}
              >
                No, I need to call again
              </button>
            </div>
          ) : (
            <>
              <a
                href={`tel:${phone}`}
                onClick={() => setCalling(true)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "#3D6B47", color: "white", borderRadius: "12px",
                  padding: isMobile ? "18px 24px" : "26px 24px",
                  fontSize: isMobile ? "18px" : "22px", fontWeight: 700, textDecoration: "none",
                  transition: "background 0.15s", marginBottom: "12px", letterSpacing: "-0.01em",
                }}
              >
                {`Call ${formatted}`}
              </a>
              <div style={{ textAlign: "center", marginBottom: "16px" }}>
                <span style={{ fontSize: "15px", color: "#A8ADA4" }}>Available 24/7 · Takes 10–20 minutes</span>
              </div>
              <div style={{ textAlign: "center", marginBottom: "24px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 5.5c-2.07 0-3.5 1.05-3.5 1.5v.5h7V11c0-.45-1.43-1.5-3.5-1.5z" fill="#9CA39A"/></svg>
                <span style={{ fontSize: "13px", color: "#9CA39A", fontWeight: 500 }}>Your responses go directly to {docName} and are HIPAA protected.</span>
              </div>
            </>
          )}



        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: "1px solid #E8EAE5", padding: "12px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: "11px", color: "#C0C4BB", letterSpacing: "0.04em" }}>
          Powered by PsychXAI
        </span>
        <button
          onClick={() => setShowSecurity(true)}
          style={{ background: "none", border: "none", fontSize: "11px", color: "#A8ADA4", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: "4px", padding: 0 }}
        >
          🛡 Privacy & Security
        </button>
      </div>

      {/* Patient-facing security modal */}
      {showSecurity && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(28,31,26,0.4)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={() => setShowSecurity(false)}>
          <div style={{ background: "#FDFCF8", borderRadius: "20px 20px 0 0", padding: "32px 28px 48px", width: "100%", maxWidth: "600px", maxHeight: "85vh", overflowY: "auto", fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "8px" }}>
              <div style={{ fontSize: "18px", fontWeight: 700, color: "#1C1F1A", letterSpacing: "-0.02em", lineHeight: 1.3 }}>Privacy & Security</div>
              <button onClick={() => setShowSecurity(false)} style={{ background: "#ECEEE9", border: "none", borderRadius: "50%", width: "30px", height: "30px", cursor: "pointer", fontSize: "14px", color: "#7A7F76", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
            </div>
            <div style={{ fontSize: "13px", color: "#7A7F76", lineHeight: 1.6, marginBottom: "28px" }}>
              Your intake call is handled by Sage, an AI intake specialist. Your responses are confidential and go directly to your doctor.
            </div>

            {/* Items — no emojis, label + body only */}
            <div style={{ display: "flex", flexDirection: "column", gap: "1px", borderRadius: "10px", overflow: "hidden", border: "1px solid #E8EAE5" }}>
              {[
                { label: "HIPAA protected", body: "Your responses are protected under HIPAA — the same law that governs all medical records. They cannot be shared without your consent." },
                { label: "Never sold or advertised", body: "Your information is never sold, shared with advertisers, or used for any purpose other than preparing for your appointment." },
                { label: "AI — not training data", body: "Sage uses AI to conduct and summarize your intake. Your responses are never used to train AI models." },
                { label: `Only ${docName} sees this`, body: "Your intake summary goes directly to your doctor's secure dashboard. No one else can access it." },
              ].map((item, i, arr) => (
                <div key={i} style={{ padding: "16px 18px", background: "#FAFAF8", borderBottom: i < arr.length - 1 ? "1px solid #E8EAE5" : "none" }}>
                  <div style={{ fontSize: "13px", fontWeight: 600, color: "#1C1F1A", marginBottom: "3px" }}>{item.label}</div>
                  <div style={{ fontSize: "13px", color: "#7A7F76", lineHeight: 1.6 }}>{item.body}</div>
                </div>
              ))}
            </div>

          </div>
        </div>
      )}

    </div>
  );
}


function EmptyState({ tab }) {
  const states = {
    pending:  { title: "Queue is clear",         body: "New intakes appear here within 60 seconds of a completed call." },
    accepted: { title: "No accepted patients",   body: "Patients you accept will appear here, ready to schedule." },
    declined: { title: "No deferred patients",   body: "Patients deferred will appear here." },
    summary:  { title: "No data yet",            body: "Your practice summary will appear once you have completed intakes." },
  };
  const { title, body } = states[tab] || states.pending;

  return (
    <div style={{ padding: "48px 0 32px" }}>
      <div style={{ fontSize: "15px", fontWeight: 600, color: C.black, marginBottom: "6px", fontFamily: fonts.body }}>{title}</div>
      <div style={{ fontSize: "14px", color: C.gray400, fontFamily: fonts.body, lineHeight: 1.6 }}>{body}</div>
    </div>
  );
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────

const DEMO_EMAIL = "demo@psychxai.com";
const DEMO_PASSWORD = "Vietnam4";

function Login({ onLogin, onDemo }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [showRequest, setShowRequest] = useState(false);
  const [showForgot, setShowForgot]   = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent]   = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [reqFirstName, setReqFirstName] = useState("");
  const [reqLastName, setReqLastName]   = useState("");
  const [reqTitle, setReqTitle]         = useState("");
  const [reqEmail, setReqEmail]         = useState("");
  const [reqPractice, setReqPractice]   = useState("");
  const [reqSending, setReqSending]     = useState(false);
  const [reqSent, setReqSent]           = useState(false);
  const [reqError, setReqError]         = useState("");

  // Typing animation for tagline
  const TAGLINE = "Clinical Intelligence for Modern Psychiatry";
  const [typed, setTyped] = useState("");
  const [doneTyping, setDoneTyping] = useState(false);
  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      if (i < TAGLINE.length) {
        setTyped(TAGLINE.slice(0, i + 1));
        i++;
      } else {
        clearInterval(interval);
        setDoneTyping(true);
      }
    }, 55);
    return () => clearInterval(interval);
  }, []);

  const go = async () => {
    setError("");
    const emailVal = email.trim().toLowerCase();
    if (!emailVal || !password) { setError("Please enter your email and password."); return; }
    if (emailVal === DEMO_EMAIL && password === DEMO_PASSWORD) { onDemo(); return; }
    if (emailVal === DEMO_EMAIL && password !== DEMO_PASSWORD) { setError("Incorrect password."); return; }
    setLoading(true);
    const { error } = await onLogin(emailVal, password);
    setLoading(false);
    if (error) setError("Incorrect email or password.");
  };

  const sendForgot = async () => {
    if (!forgotEmail) return;
    setForgotLoading(true);
    await auth.resetPasswordForEmail(forgotEmail.trim().toLowerCase(), {
      redirectTo: "https://psychxai.com",
    });
    setForgotLoading(false);
    setForgotSent(true);
  };

  const sendRequest = async () => {
    setReqSending(true);
    setReqError("");
    try {
      const res = await fetch("https://formspree.io/f/xkokpygd", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ first_name: reqFirstName, last_name: reqLastName, title: reqTitle, email: reqEmail, practice_name: reqPractice }),
      });
      if (res.ok) { setReqSent(true); }
      else { setReqError("Something went wrong. Please try again."); }
    } catch { setReqError("Something went wrong. Please try again."); }
    setReqSending(false);
  };

  const canSubmit = reqFirstName && reqLastName && reqTitle && reqEmail && reqPractice;
  const inp = { width: "100%", border: `1px solid ${C.gray200}`, borderRadius: "6px", padding: "10px 12px", fontSize: "16px", fontFamily: fonts.body, color: C.black, background: C.white, outline: "none", boxSizing: "border-box", transition: "border-color 0.15s" };
  const sel = { ...inp, appearance: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23A8ADA4' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", paddingRight: "32px", cursor: "pointer" };

  return (
    <div style={{ minHeight: "100vh", background: C.paper, fontFamily: fonts.body }}>

      {/* Top nav bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: `1px solid ${C.gray200}` }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: "17px" }}>
          <LogoMark size={32} />
          <span style={{ fontSize: "17px", fontWeight: 700, color: C.black, fontFamily: fonts.body, letterSpacing: "-0.03em" }}>PsychXAI</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "17px" }}>
          <button onClick={() => setShowRequest(true)} style={{ background: C.sageDeep, border: "none", borderRadius: "6px", padding: "8px 16px", color: C.white, fontSize: "17px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body, transition: "background 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.background = C.sageDark}
            onMouseLeave={e => e.currentTarget.style.background = C.sageDeep}>
            Request access
          </button>
        </div>
      </div>

      {/* Main centered content */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "calc(100vh - 65px)", padding: "24px" }}>
        <div style={{ width: "100%", maxWidth: "360px" }}>

          {/* Logo + tagline */}
          {!showRequest && !showForgot && (
            <div style={{ marginBottom: sp.lg, textAlign: "center" }}>
              <div style={{ fontSize: fs.sm, color: C.gray500, fontFamily: fonts.body, fontWeight: 400, lineHeight: 1.6, marginBottom: sp.sm, minHeight: "24px" }}>
                {typed}<span style={{ 
                  display: "inline-block",
                  width: "3px", 
                  height: "1.1em",
                  background: C.sage,
                  marginLeft: "2px",
                  verticalAlign: "middle",
                  borderRadius: "1px",
                  animation: "blink 1s step-end infinite"
                }} />
              </div>
            </div>
          )}

        {!showRequest && !showForgot && (
          <>
            <div style={{ background: C.paperDark, borderRadius: "17px", padding: sp.xl, boxShadow: "0 2px 16px rgba(61,107,71,0.08)", border: `1px solid ${C.sageDark}` }}>
              <div style={{ display: "flex", flexDirection: "column", gap: sp.md }}>
                <div>
                  <label style={{ fontSize: "16px", color: C.black, display: "block", marginBottom: "5px", fontWeight: 500 }}>Email address</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" style={{ ...inp, fontSize: "16px" }} onFocus={e => e.target.style.borderColor=C.sage} onBlur={e => e.target.style.borderColor=C.gray200} onKeyDown={e => e.key==="Enter"&&go()} />
                </div>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "5px" }}>
                    <label style={{ fontSize: "16px", color: C.black, fontWeight: 500 }}>Password</label>
                    <button onClick={() => { setShowForgot(true); setForgotEmail(email); }} style={{ background: "none", border: "none", color: C.sage, fontSize: "16px", cursor: "pointer", fontFamily: fonts.body, padding: 0, textDecoration: "underline" }}>Forgot password?</button>
                  </div>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" style={{ ...inp, fontSize: "16px" }} onFocus={e => e.target.style.borderColor=C.sage} onBlur={e => e.target.style.borderColor=C.gray200} onKeyDown={e => e.key==="Enter"&&go()} />
                </div>
                <button
                  onClick={go}
                  onMouseEnter={e => e.currentTarget.style.background = C.sageDark}
                  onMouseLeave={e => e.currentTarget.style.background = loading ? C.sage : C.sage}
                  style={{ width: "100%", background: loading?C.sage:C.sage, border: "none", borderRadius: "6px", padding: "16px", color: C.white, fontSize: "16px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body, marginTop: "4px", transition: "background 0.15s", letterSpacing: "0.01em" }}>
                  {loading ? "Signing in..." : "Sign in →"}
                </button>
                {error && <div style={{ fontSize: "16px", color: C.red, textAlign: "center", fontFamily: fonts.body, marginTop: "4px" }}>{error}</div>}

              </div>
            </div>

            {/* Trust bar */}
            <div style={{ textAlign: "center", marginTop: sp.md }}>
              <span style={{ fontSize: "14px", color: C.gray400, fontFamily: fonts.body, whiteSpace: "nowrap" }}>
                HIPAA-compliant · BAA available · Made by psychiatrists
              </span>
            </div>
          </>
        )}

        {showForgot && (
          <div style={{ background: C.paperDark, borderRadius: "17px", padding: "28px", boxShadow: "0 2px 16px rgba(92,122,98,0.10)" }}>
            {!forgotSent ? (
              <>
                <button onClick={() => setShowForgot(false)} style={{ background: "none", border: "none", color: C.gray400, fontSize: "17px", cursor: "pointer", fontFamily: fonts.body, padding: "0 0 14px", display: "block" }}>← Back to sign in</button>
                <h2 style={{ fontSize: "16px", fontWeight: 400, color: C.black, margin: "0 0 6px", fontFamily: fonts.body }}>Reset your password</h2>
                <p style={{ fontSize: "16px", color: C.black, marginBottom: "20px", lineHeight: 1.6, fontFamily: fonts.body }}>Enter your email and we'll send you a link to reset your password.</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "17px" }}>
                  <div>
                    <label style={{ fontSize: "16px", color: C.black, display: "block", marginBottom: "5px", fontWeight: 500 }}>Email address</label>
                    <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="Email address" style={inp} onFocus={e => e.target.style.borderColor=C.sage} onBlur={e => e.target.style.borderColor=C.gray200} onKeyDown={e => e.key==="Enter"&&sendForgot()} />
                  </div>
                  <button onClick={sendForgot} disabled={!forgotEmail||forgotLoading} style={{ width: "100%", background: (!forgotEmail||forgotLoading)?C.gray200:C.sage, border: "none", borderRadius: "6px", padding: "16px", color: (!forgotEmail||forgotLoading)?C.gray400:C.white, fontSize: "16px", fontWeight: 500, cursor: (!forgotEmail||forgotLoading)?"not-allowed":"pointer", fontFamily: fonts.body }}>
                    {forgotLoading ? "Sending..." : "Send reset link"}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <div style={{ fontSize: "32px", marginBottom: "16px" }}>✓</div>
                <div style={{ fontSize: "16px", fontWeight: 400, color: C.black, fontFamily: fonts.body, marginBottom: "8px" }}>Check your email</div>
                <div style={{ fontSize: "17px", color: C.black, fontFamily: fonts.body, lineHeight: 1.7, marginBottom: "20px" }}>We sent a reset link to {forgotEmail}. Follow the link to set a new password.</div>
                <button onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(""); }} style={{ background: "none", border: "none", color: C.sage, fontSize: "17px", cursor: "pointer", fontFamily: fonts.body, textDecoration: "underline" }}>Back to sign in</button>
              </div>
            )}
          </div>
        )}

        {showRequest && (
          <div style={{ background: C.paperDark, borderRadius: "17px", padding: "28px", boxShadow: "0 2px 16px rgba(92,122,98,0.10)" }}>
            {!reqSent ? (
              <>
                <button onClick={() => setShowRequest(false)} style={{ background: "none", border: "none", color: C.gray400, fontSize: "17px", cursor: "pointer", fontFamily: fonts.body, padding: "0 0 14px", display: "block" }}>← Back to sign in</button>
                <h2 style={{ fontSize: "16px", fontWeight: 400, color: C.black, margin: "0 0 6px", fontFamily: fonts.body }}>Request access</h2>
                <p style={{ fontSize: "16px", color: C.black, marginBottom: "20px", lineHeight: 1.6, fontFamily: fonts.body }}>We'll set up your account within 24 hours.</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "17px" }}>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: "16px", color: C.black, display: "block", marginBottom: "5px", fontWeight: 500 }}>First name</label>
                      <input type="text" value={reqFirstName} onChange={e => setReqFirstName(e.target.value)} placeholder="First name" style={inp} onFocus={e => e.target.style.borderColor=C.sage} onBlur={e => e.target.style.borderColor=C.gray200} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: "16px", color: C.black, display: "block", marginBottom: "5px", fontWeight: 500 }}>Last name</label>
                      <input type="text" value={reqLastName} onChange={e => setReqLastName(e.target.value)} placeholder="Last name" style={inp} onFocus={e => e.target.style.borderColor=C.sage} onBlur={e => e.target.style.borderColor=C.gray200} />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: "16px", color: C.black, display: "block", marginBottom: "5px", fontWeight: 500 }}>Title</label>
                    <select value={reqTitle} onChange={e => setReqTitle(e.target.value)} style={{ ...sel, color: reqTitle ? C.black : C.gray400 }} onFocus={e => e.target.style.borderColor=C.sage} onBlur={e => e.target.style.borderColor=C.gray200}>
                      <option value="" disabled>Select title</option>
                      <option value="MD">MD</option>
                      <option value="DO">DO</option>
                      <option value="NP">NP</option>
                      <option value="PA">PA</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "16px", color: C.black, display: "block", marginBottom: "5px", fontWeight: 500 }}>Email address</label>
                    <input type="email" value={reqEmail} onChange={e => setReqEmail(e.target.value)} placeholder="Email address" style={inp} onFocus={e => e.target.style.borderColor=C.sage} onBlur={e => e.target.style.borderColor=C.gray200} />
                  </div>
                  <div>
                    <label style={{ fontSize: "16px", color: C.black, display: "block", marginBottom: "5px", fontWeight: 500 }}>Practice name</label>
                    <input type="text" value={reqPractice} onChange={e => setReqPractice(e.target.value)} placeholder="Practice name" style={inp} onFocus={e => e.target.style.borderColor=C.sage} onBlur={e => e.target.style.borderColor=C.gray200} />
                  </div>
                  <button onClick={sendRequest} disabled={!canSubmit||reqSending} style={{ width: "100%", background: (!canSubmit||reqSending)?C.gray200:C.sage, border: "none", borderRadius: "6px", padding: "16px", color: (!canSubmit||reqSending)?C.gray400:C.white, fontSize: "16px", fontWeight: 500, cursor: (!canSubmit||reqSending)?"not-allowed":"pointer", fontFamily: fonts.body, marginTop: "4px" }}>
                    {reqSending ? "Sending..." : "Send request"}
                  </button>
                  {reqError && <div style={{ fontSize: "16px", color: C.red, textAlign: "center", fontFamily: fonts.body }}>{reqError}</div>}
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div style={{ fontSize: "32px", marginBottom: "16px" }}>✓</div>
                <div style={{ fontSize: "16px", fontWeight: 400, color: C.black, fontFamily: fonts.body, marginBottom: "8px" }}>Request received</div>
                <div style={{ fontSize: "17px", color: C.black, fontFamily: fonts.body, lineHeight: 1.7, marginBottom: "24px" }}>Thank you, {reqFirstName}. We'll set up your account and send credentials within 24 hours.</div>
                <button onClick={() => { setShowRequest(false); setReqSent(false); setReqFirstName(""); setReqLastName(""); setReqTitle(""); setReqEmail(""); setReqPractice(""); }} style={{ background: "none", border: "none", color: C.sage, fontSize: "17px", cursor: "pointer", fontFamily: fonts.body, textDecoration: "underline" }}>Back to sign in</button>
              </div>
            )}
          </div>
        )}

        </div>
      </div>
    </div>
  );
}

// ─── MODAL ────────────────────────────────────────────────────────────────────

const DECLINE_REASONS = [
  { value: "Higher level of care indicated",    group: "clinical" },
  { value: "Not accepting new patients",         group: "administrative" },
  { value: "Insufficient clinical information",  group: "administrative" },
  { value: "Other",                              group: "administrative" },
];

const RATIONALE_BY_REASON = {
  "Higher level of care indicated":
    "Clinical presentation suggests a level of care beyond outpatient psychiatry. Patient has been deferred pending referral to a more appropriate setting.",
  "Not accepting new patients":
    "Practice is not accepting new patients at this time. Deferral is administrative; patient should be redirected to alternative providers.",
  "Insufficient clinical information":
    "Intake was incomplete — insufficient data to fully screen for fit. Rescheduled intake required before clinical determination can be made.",
  "Other": "",
};

function DecisionModal({ patient, action, onConfirm, onCancel, physician }) {
  const isAccept = action === "accept";

  const [reason, setReason]     = useState("");
  const [rationale, setRationale] = useState(patient.fitRationale || "");
  const [otherText, setOtherText] = useState("");

  const isOther      = reason === "Other";
  const otherComplete = !isOther || otherText.trim() !== "";
  const canConfirm   = isAccept || (reason !== "" && otherComplete);

  const handleReasonChange = (val) => {
    setReason(val);
    if (!rationale.trim() && RATIONALE_BY_REASON[val]) {
      setRationale(RATIONALE_BY_REASON[val]);
    }
  };

  const handleConfirm = () => {
    // rationale IS the note — one field, shown on dashboard and saved to record
    const note = isOther ? otherText : rationale;
    onConfirm({ reason, note });
  };

  const clinicalReasons       = DECLINE_REASONS.filter(r => r.group === "clinical");
  const administrativeReasons = DECLINE_REASONS.filter(r => r.group === "administrative");

  const radioRowStyle = (selected) => ({
    display: "flex", alignItems: "center", gap: "8px",
    padding: "6px 10px", marginBottom: "3px",
    border: `1px solid ${selected ? C.sageBorder : C.gray200}`,
    borderRadius: "6px", cursor: "pointer",
    background: selected ? C.sageLight : C.white,
    transition: "all 0.1s",
    fontSize: "14px", color: C.black, fontFamily: fonts.body,
  });

  const groupLabel = (text, mt = "10px") => (
    <div style={{ fontSize: "10px", fontWeight: 600, color: C.gray400, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "4px", marginTop: mt }}>
      {text}
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(28,31,26,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "20px" }}>
      <div style={{ background: C.white, borderRadius: "14px", maxWidth: "500px", width: "100%", boxShadow: "0 24px 64px rgba(28,31,26,0.18)", fontFamily: fonts.body, maxHeight: "92vh", overflowY: "auto" }}>

        {/* Header */}
        <div style={{ padding: "24px 28px 20px", borderBottom: `1px solid ${C.gray100}` }}>
          <div style={{ fontSize: "18px", fontWeight: 700, color: C.black, marginBottom: "3px" }}>
            {isAccept ? `Accept ${patient.name}?` : `Defer ${patient.name}?`}
          </div>
          <div style={{ fontSize: "14px", color: C.gray400, lineHeight: 1.5 }}>
            {isAccept
              ? "Moves to Accepted tab. Marks appropriate for scheduling."
              : "Logs as deferred. A reason is required before confirming."}
          </div>
        </div>

        <div style={{ padding: "20px 28px 24px" }}>

          {!isAccept && (
            <>
              {/* Reason — radio buttons, grouped */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ fontSize: "12px", fontWeight: 600, color: C.gray500, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: "4px" }}>
                  Reason for deferral <span style={{ color: C.red }}>*</span>
                </label>

                {groupLabel("Clinical determination", "0px")}
                {clinicalReasons.map(r => (
                  <label key={r.value} style={radioRowStyle(reason === r.value)}>
                    <input type="radio" name="deferReason" value={r.value}
                      checked={reason === r.value}
                      onChange={() => handleReasonChange(r.value)}
                      style={{ accentColor: C.sage, width: "15px", height: "15px", flexShrink: 0 }}
                    />
                    {r.value}
                  </label>
                ))}

                {groupLabel("Administrative / circumstantial")}
                {administrativeReasons.map(r => (
                  <label key={r.value} style={radioRowStyle(reason === r.value)}>
                    <input type="radio" name="deferReason" value={r.value}
                      checked={reason === r.value}
                      onChange={() => handleReasonChange(r.value)}
                      style={{ accentColor: C.sage, width: "15px", height: "15px", flexShrink: 0 }}
                    />
                    {r.value}
                  </label>
                ))}
              </div>

              {/* Other expansion */}
              {isOther && (
                <div style={{ marginBottom: "20px" }}>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: C.gray500, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: "6px" }}>
                    Specify reason <span style={{ color: C.red }}>*</span>
                  </label>
                  <textarea
                    value={otherText}
                    onChange={e => { if (e.target.value.length <= 300) setOtherText(e.target.value); }}
                    placeholder="Describe the reason for deferral..."
                    style={{
                      width: "100%", border: `1px solid ${otherText ? C.gray200 : C.redBorder}`,
                      borderRadius: "6px", padding: "9px 12px",
                      fontSize: "14px", fontFamily: fonts.body,
                      color: C.black, background: C.white, outline: "none",
                      resize: "none", height: "80px", boxSizing: "border-box", lineHeight: 1.6,
                    }}
                    onFocus={e => e.target.style.borderColor = C.sage}
                    onBlur={e => e.target.style.borderColor = otherText ? C.gray200 : C.redBorder}
                  />
                  <div style={{ fontSize: "12px", color: C.gray400, textAlign: "right", marginTop: "2px" }}>{otherText.length}/300</div>
                </div>
              )}

              {/* Clinical note — always visible, pre-filled from AI, editable */}
              {!isOther && (
                <div style={{ marginBottom: "20px", opacity: reason ? 1 : 0.5, transition: "opacity 0.2s" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                    <label style={{ fontSize: "12px", fontWeight: 600, color: C.gray500, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      Clinical note
                    </label>
                    <span style={{ fontSize: "11px", color: C.gray400 }}>AI pre-filled · editable</span>
                  </div>
                  <textarea
                    value={rationale}
                    onChange={e => setRationale(e.target.value)}
                    disabled={!reason}
                    rows={4}
                    style={{
                      width: "100%", border: `1px solid ${C.gray200}`,
                      borderRadius: "6px", padding: "10px 12px",
                      fontSize: "13px", fontFamily: fonts.body,
                      color: C.black, background: reason ? C.paper : C.gray100,
                      outline: "none", resize: "none",
                      lineHeight: 1.6, boxSizing: "border-box",
                      cursor: reason ? "text" : "default",
                    }}
                    onFocus={e => { if (reason) e.target.style.borderColor = C.sage; }}
                    onBlur={e => e.target.style.borderColor = C.gray200}
                  />
                </div>
              )}

              {/* Attribution */}
              <div style={{ background: C.gray100, borderRadius: "6px", padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
                <div style={{ fontSize: "14px", color: C.black, fontFamily: fonts.body }}>Will be logged as:</div>
                <div style={{ fontSize: "14px", color: C.gray700, fontFamily: fonts.body, textAlign: "right" }}>
                  {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {physician || ""}
                </div>
              </div>
            </>
          )}

          {/* Accept — attribution only */}
          {isAccept && (
            <div style={{ background: C.gray100, borderRadius: "6px", padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
              <div style={{ fontSize: "14px", color: C.black, fontFamily: fonts.body }}>Will be logged as:</div>
              <div style={{ fontSize: "14px", color: C.gray700, fontFamily: fonts.body, textAlign: "right" }}>
                {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {physician || ""}
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button onClick={onCancel} style={{ background: "transparent", border: `1px solid ${C.gray200}`, borderRadius: "6px", padding: "8px 16px", color: C.black, fontSize: "15px", cursor: "pointer", fontFamily: fonts.body }}>
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!canConfirm}
              style={{
                background: !canConfirm ? C.gray200 : C.sage,
                border: "none", borderRadius: "6px", padding: "8px 20px",
                color: !canConfirm ? C.gray400 : C.white,
                fontSize: "15px", fontWeight: 600,
                cursor: canConfirm ? "pointer" : "not-allowed",
                fontFamily: fonts.body, transition: "background 0.15s",
              }}
            >
              {isAccept ? "Confirm Accept" : "Confirm Deferral"}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── TABLE ────────────────────────────────────────────────────────────────────

// Sort orders for risk and screening
const RISK_ORDER  = { high: 0, moderate: 1, low: 2 };
const FIT_ORDER   = { decline: 0, review: 1, accept: 2 };

// ─── MOBILE HOOK ──────────────────────────────────────────────────────────────

function useMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function PatientTable({ rows, onSelect, onDecide, showActions, showDeclineNote, onDragStart, physician, hasBookingUrl, isDemo = false }) {
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = key => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      let av, bv;
      switch (sortKey) {
        case "name":      av = a.name.split(" ").slice(-1)[0].toLowerCase(); bv = b.name.split(" ").slice(-1)[0].toLowerCase(); break;
        case "date":      av = new Date(a.date + " " + a.time); bv = new Date(b.date + " " + b.time); break;
        case "risk":      av = RISK_ORDER[a.riskLevel] ?? 9; bv = RISK_ORDER[b.riskLevel] ?? 9; break;
        case "screening": av = FIT_ORDER[a.fitScore] ?? 9; bv = FIT_ORDER[b.fitScore] ?? 9; break;
        case "call":      av = a.callDurationMins ?? 0; bv = b.callDurationMins ?? 0; break;
        case "complaint": av = a.chiefComplaint.toLowerCase(); bv = b.chiefComplaint.toLowerCase(); break;
        default: return 0;
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const SI = ({ k }) => sortKey !== k
    ? <span style={{ color: C.gray200, marginLeft: "4px", fontSize: "9px" }}>⇅</span>
    : <span style={{ color: C.sage, marginLeft: "4px", fontSize: "9px" }}>{sortDir === "asc" ? "↑" : "↓"}</span>;

  const th = (k, label, extra = {}) => ({
    style: { fontSize: "12px", letterSpacing: "0.06em", color: sortKey === k ? C.sage : C.gray400, textTransform: "uppercase", fontWeight: 600, padding: "0 16px 12px 0", borderBottom: `1px solid ${C.gray200}`, textAlign: "left", fontFamily: fonts.body, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", ...extra },
    onClick: () => handleSort(k),
  });

  const cell = (extra = {}) => ({ style: { padding: "14px 16px 14px 0", borderBottom: `1px solid ${C.gray100}`, verticalAlign: "middle", ...extra } });

  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th {...th("name", "Patient", { width: "150px", minWidth: "150px" })}>Patient <SI k="name" /></th>
          <th {...th("date", "Date", { width: "110px", minWidth: "110px" })}>Intake date <SI k="date" /></th>
          <th {...th("complaint", "Chief Complaint", { minWidth: "200px" })}>Chief Complaint <SI k="complaint" /></th>
          <th {...th("risk", "Risk", { width: "130px", minWidth: "130px" })}>Risk <SI k="risk" /></th>
          <th {...th("screening", "Fit", { width: "200px", minWidth: "200px" })}>Fit <SI k="screening" /></th>

          {showActions    && <th style={{ fontSize: "12px", letterSpacing: "0.06em", color: C.gray400, textTransform: "uppercase", fontWeight: 600, padding: "0 0 12px", borderBottom: `1px solid ${C.gray200}`, textAlign: "left", fontFamily: fonts.body, width: "130px", minWidth: "130px" }}>Decision</th>}
          {showDeclineNote && <th style={{ fontSize: "12px", letterSpacing: "0.06em", color: C.gray400, textTransform: "uppercase", fontWeight: 600, padding: "0 16px 12px 0", borderBottom: `1px solid ${C.gray200}`, textAlign: "left", fontFamily: fonts.body }}>Clinician Note</th>}
          {!showActions && !showDeclineNote && <th style={{ fontSize: "12px", letterSpacing: "0.06em", color: C.gray400, textTransform: "uppercase", fontWeight: 600, padding: "0 0 12px", borderBottom: `1px solid ${C.gray200}`, textAlign: "left", fontFamily: fonts.body, width: "130px" }}>Phone</th>}
        </tr>
      </thead>
      <tbody>
        {sorted.map(p => (
          <tr key={p.id} draggable onDragStart={() => onDragStart && onDragStart(p)} onClick={() => onSelect(p)} style={{ cursor: "grab" }} onMouseOver={e => e.currentTarget.style.background=C.sageLight} onMouseOut={e => e.currentTarget.style.background="transparent"}>
            <td {...cell()}>
              <div style={{ fontSize: "15px", fontWeight: 600, color: C.black, fontFamily: fonts.body }}>{p.name.split(" ")[1]}, {p.name.split(" ")[0]}</div>
              <div style={{ fontSize: "13px", color: C.gray400, fontFamily: fonts.body }}>{p.age ? `${p.age} YO` : ""}{p.gender ? ` ${genderAbbr(p.gender)}` : ""}</div>
            </td>
            <td {...cell()}>
              <div style={{ fontSize: "14px", color: C.gray700, fontFamily: fonts.body, whiteSpace: "nowrap" }}>{p.date}</div>
              <div style={{ fontSize: "12px", color: C.gray400, fontFamily: fonts.body }}>{p.time}</div>
            </td>
            <td {...cell()}>
              <div style={{ fontSize: "14px", color: C.gray700, lineHeight: 1.4, fontFamily: fonts.body }}>{p.chiefComplaint}</div>
            </td>
            <td {...cell()}>
              <RiskPill level={p.riskLevel} />
              {(p.siFlag || p.hiFlag || p.graveDisabilityFlag || (p.firearmsAccess && !p.firearmsSecured) || (p.pregnancyStatus && p.pregnancyStatus.toLowerCase() !== "no")) && (
                <div style={{ display: "flex", gap: "3px", flexWrap: "wrap", marginTop: "4px" }}>
                  {p.siFlag && <SIFlag present={true} />}
                  {p.hiFlag && <HIFlag present={true} />}
                  {p.graveDisabilityFlag && <GDFlag present={true} />}
                  {p.firearmsAccess && !p.firearmsSecured && <FirearmFlag present={true} />}
                  {p.pregnancyStatus && p.pregnancyStatus.toLowerCase() !== "no" && <PregnancyFlag present={true} />}
                </div>
              )}
            </td>
            <td {...cell()}>
              {p.status === "declined" && p.declineReason
                ? <span style={{ fontSize: "12px", fontWeight: 600, color: C.red, fontFamily: fonts.body }}>✕ {p.declineReason}</span>
                : p.intakeComplete
                  ? <span style={{ fontSize: "12px", fontWeight: 600, color: C.sageDark, fontFamily: fonts.body }}>✓ Complete</span>
                  : p.status === "reviewed"
                    ? <span style={{ fontSize: "12px", fontWeight: 600, color: C.sageDark, fontFamily: fonts.body }}>✓ Appt booked</span>
                    : p.status === "accepted"
                      ? <span style={{ fontSize: "12px", fontWeight: 500, color: C.amber, fontFamily: fonts.body }}>{hasBookingUrl ? "Awaiting booking" : "Outreach needed"}</span>
                      : <FitLabel score={p.fitScore} rationale={p.fitRationale} />
              }
            </td>

            {showActions && (
              <td {...cell({ padding: "14px 0" })} onClick={e => e.stopPropagation()}>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button onClick={e => { e.stopPropagation(); onDecide(p, "accept"); }} style={{ background: "transparent", border: `1px solid ${C.sage}`, borderRadius: "5px", padding: "4px 12px", color: C.sage, fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body }}>Accept</button>
                  <button onClick={e => { e.stopPropagation(); onDecide(p, "decline"); }} style={{ background: "none", border: "none", padding: "4px 8px", color: C.gray300, fontSize: "13px", cursor: "pointer", fontFamily: fonts.body }}>Defer</button>
                </div>
              </td>
            )}
            {showDeclineNote && (
              <td {...cell({ maxWidth: "280px" })}>
                {p.declineNote
                  ? <div>
                      <div style={{ fontSize: "13px", color: C.gray700, fontFamily: fonts.body, lineHeight: 1.5 }}>{p.declineNote}</div>
                      {p.decidedAt && <div style={{ fontSize: "12px", color: C.gray400, marginTop: "4px", fontFamily: fonts.body }}>{p.decidedAt}</div>}
                    </div>
                  : <span style={{ color: C.gray300, fontSize: "14px" }}>—</span>
                }
              </td>
            )}
            {!showActions && !showDeclineNote && (
              <td {...cell({ padding: "14px 0" })}>
                <a href={`tel:${p.phone}`} onClick={e => e.stopPropagation()} style={{ fontSize: "15px", color: C.sage, textDecoration: "none", fontWeight: 700, fontFamily: fonts.body, letterSpacing: "-0.01em", display: "block", marginBottom: "3px" }}>{p.phone}</a>
                {p.status === "reviewed" && p.decidedAt && (
                  <div style={{ fontSize: "12px", color: C.sageDark, fontFamily: fonts.body, lineHeight: 1.5 }}>
                    ✓ Booked · {p.decidedAt}
                  </div>
                )}
                {p.status === "accepted" && (() => {
                  const ts = p.smsSentAt ? new Date(p.smsSentAt) : null;
                  const valid = ts && !isNaN(ts);
                  if (valid) {
                    const dateStr = ts.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                    const timeStr = ts.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
                    return <div style={{ fontSize: "12px", color: C.sageDark, fontFamily: fonts.body, lineHeight: 1.5 }}>✓ SMS sent · {dateStr} · {timeStr}</div>;
                  }
                  if (isDemo) {
                    const label = p.decidedAt ? `SMS sent · ${p.decidedAt}` : "SMS sent";
                    return <div style={{ fontSize: "12px", color: C.sageDark, fontFamily: fonts.body, lineHeight: 1.5 }}>✓ {label}</div>;
                  }
                  // No SMS yet — phone col stays clean, Fit col shows the status
                  return null;
                })()}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── MOBILE CARDS ─────────────────────────────────────────────────────────────

function MobileCards({ rows, onSelect, onDecide, showActions, showFlag, showDeclineNote }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "17px" }}>
      {rows.map((p, idx) => (
        <div
          key={p.id}
          className="swipe-card"
          onClick={() => onSelect(p)}
          style={{ background: C.paperDark, border: `1px solid ${C.gray200}`, borderRadius: "17px", padding: "16px", cursor: "pointer", animation: `slideInRow 0.2s ease ${idx * 0.05}s both` }}
        >
          {/* Header row */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "8px" }}>
            <div>
              <div style={{ fontSize: "17px", fontWeight: 600, color: C.black, fontFamily: fonts.body, marginBottom: "2px" }}>
                {p.name.split(" ")[1]}, {p.name.split(" ")[0]}
              </div>
              <div style={{ fontSize: "16px", color: C.gray400, fontFamily: fonts.body }}>{p.age ? `${p.age} YO` : ""}{p.gender ? ` ${genderAbbr(p.gender)}` : ""} · {p.date} · {p.time}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
              <RiskPill level={p.riskLevel} />
              <SIFlag present={p.siFlag} />
              <HIFlag present={p.hiFlag} />
              <GDFlag present={p.graveDisabilityFlag} />
              <FirearmFlag present={p.firearmsAccess && !p.firearmsSecured} />
              <PregnancyFlag present={p.pregnancyStatus && p.pregnancyStatus.toLowerCase() !== "no" && p.pregnancyStatus.toLowerCase() !== "not pregnant"} />
            </div>
          </div>

          {/* Chief complaint */}
          <div style={{ fontSize: "17px", color: C.gray700, lineHeight: 1.5, marginBottom: "17px", fontFamily: fonts.body }}>
            {p.chiefComplaint}
          </div>

          {/* Screening + call */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: showActions ? "17px" : "0" }}>
            {showActions && <FitLabel score={p.fitScore} rationale={p.fitRationale} />}
            {!showActions && <StatusPill status={p.status} />}
            <span style={{ fontSize: "16px", color: p.callComplete ? C.gray400 : C.amber, fontWeight: p.callComplete ? 400 : 600, fontFamily: fonts.body }}>
              {p.callDuration || "< 1 min"} · {p.callComplete ? "Complete" : "⚠ Incomplete"}
            </span>
          </div>

          {/* Actions — Accept dominant, Decline secondary */}
          {showActions && (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }} onClick={e => e.stopPropagation()}>
              <button
                onClick={e => { e.stopPropagation(); onDecide(p, "accept"); }}
                style={{ flex: 1, background: C.sage, border: "none", borderRadius: "8px", padding: "16px", color: C.white, fontSize: "16px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body }}
              >Accept</button>
              <button
                onClick={e => { e.stopPropagation(); onDecide(p, "decline"); }}
                style={{ background: "none", border: `1px solid ${C.gray200}`, borderRadius: "8px", padding: "11px 16px", color: C.black, fontSize: "16px", fontWeight: 500, cursor: "pointer", fontFamily: fonts.body }}
              >Defer</button>
            </div>
          )}

          {/* Accepted — phone number */}
          {!showActions && !showFlag && !showDeclineNote && (
            <a
              href={`tel:${p.phone}`}
              onClick={e => e.stopPropagation()}
              style={{ display: "block", fontSize: "16px", color: C.sage, fontWeight: 500, fontFamily: fonts.body, textDecoration: "none", marginTop: "4px" }}
            >{p.phone}</a>
          )}

          {/* Clinical flag — shows on pending high/moderate risk */}
          {showFlag && p.declineFlag && (
            <div style={{ marginTop: "17px", paddingTop: "17px", borderTop: `1px solid ${C.redBorder}`, display: "flex", alignItems: "flex-start", gap: "6px" }}>
              <span style={{ fontSize: "16px", flexShrink: 0 }}>⚑</span>
              <div style={{ fontSize: "16px", color: C.red, lineHeight: 1.5, fontFamily: fonts.body }}>{p.declineFlag}</div>
            </div>
          )}

          {/* Physician decline note — shows on declined tab */}
          {showDeclineNote && (
            <div style={{ marginTop: "17px", paddingTop: "17px", borderTop: `1px solid ${C.gray200}` }}>
              {p.declineReason
                ? <div>
                    <div style={{ fontSize: "16px", fontWeight: 600, color: C.gray700, fontFamily: fonts.body, marginBottom: "2px" }}>{p.declineReason}</div>
                    {p.declineNote && <div style={{ fontSize: "16px", color: C.black, fontFamily: fonts.body, lineHeight: 1.4, marginBottom: "3px" }}>{p.declineNote}</div>}
                    <div style={{ fontSize: "17px", color: C.gray400, fontFamily: fonts.body }}>{p.decidedAt}</div>
                  </div>
                : <div style={{ fontSize: "16px", color: C.gray400, fontFamily: fonts.body, fontStyle: "italic" }}>No physician note recorded</div>
              }
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── PATIENT SLIDE PANEL ─────────────────────────────────────────────────────

function PatientPanel({ patient, onClose, onDecide, onOpenFull }) {
  const open = !!patient;
  const p = patient;
  const PANEL_WIDTH = 480;

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Risk-level accent color for left border
  const riskAccent = p?.riskLevel === "high" ? C.red
    : p?.riskLevel === "moderate" ? C.amber
    : C.sage;

  const FlagChip = ({ label, color, bg }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: bg, color, fontSize: "10px", fontWeight: 600, padding: "2px 7px", borderRadius: "3px", fontFamily: fonts.body, letterSpacing: "0.03em" }}>
      {label}
    </span>
  );

  const MetaLabel = ({ children }) => (
    <div style={{ fontSize: "10px", fontWeight: 600, color: C.gray400, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: fonts.body, marginBottom: "4px" }}>{children}</div>
  );

  const NotePreview = ({ text }) => {
    if (!text) return null;
    const SECTION_RE = /^[A-Z][A-Z\s\/&#]{2,}$/;
    const DIVIDER_RE = /^[\u2501\u2500=\-]{4,}$/;
    const sections = []; let cur = null;
    text.split("\n").forEach(line => {
      const t = line.trim();
      if (DIVIDER_RE.test(t)) return;
      if (SECTION_RE.test(t)) { if (cur) sections.push(cur); cur = { title: t, lines: [] }; }
      else if (cur) cur.lines.push(line);
    });
    if (cur) sections.push(cur);
    const order = t => /^CLINICAL/.test(t) ? 0 : /^LIKELY/.test(t) ? 1 : /^SAFETY/.test(t) ? 2 : 3;
    const top = [...sections].sort((a,b) => order(a.title) - order(b.title)).slice(0, 2);
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {top.map((sec, i) => {
          const isBrief = /^CLINICAL/.test(sec.title);
          const isSafety = /^SAFETY/.test(sec.title);
          const th = isBrief ? { bg: "#F5F8F5", border: C.sageBorder, hdrColor: C.sageDark, hdrBg: C.sageLight }
            : isSafety ? { bg: "#FFF8F7", border: C.redBorder, hdrColor: C.red, hdrBg: C.redLight }
            : { bg: C.white, border: C.gray200, hdrColor: C.gray500, hdrBg: C.gray100 };
          const content = sec.lines.join("\n").trim();
          if (!content) return null;
          return (
            <div key={i} style={{ border: `1px solid ${th.border}`, borderRadius: "6px", overflow: "hidden" }}>
              <div style={{ background: th.hdrBg, padding: "4px 10px" }}>
                <span style={{ fontSize: "9px", fontWeight: 700, color: th.hdrColor, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: fonts.body }}>{sec.title}</span>
              </div>
              <div style={{ background: th.bg, padding: "8px 10px" }}>
                <pre style={{ margin: 0, fontFamily: fonts.mono, fontSize: "11.5px", color: C.gray700, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{content}</pre>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {/* Overlay */}
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(28,31,26,0.25)", zIndex: 150, opacity: open ? 1 : 0, pointerEvents: open ? "auto" : "none", transition: "opacity 0.25s ease" }} />

      {/* Panel */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: PANEL_WIDTH,
        background: C.white,
        borderLeft: `3px solid ${open && p ? riskAccent : "transparent"}`,
        boxShadow: "-4px 0 32px rgba(28,31,26,0.10)",
        zIndex: 160,
        transform: open ? "translateX(0)" : `translateX(${PANEL_WIDTH + 20}px)`,
        transition: "transform 0.30s cubic-bezier(0.4,0,0.2,1)",
        display: "flex", flexDirection: "column",
      }} onClick={e => e.stopPropagation()}>
        {p && <>
          {/* Sticky header */}
          <div style={{ padding: "16px 20px 14px", borderBottom: `1px solid ${C.gray100}`, position: "sticky", top: 0, background: C.white, zIndex: 10, flexShrink: 0 }}>
            {/* Nav row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
              <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "13px", color: C.gray400, cursor: "pointer", fontFamily: fonts.body, padding: 0, display: "flex", alignItems: "center", gap: "4px" }}>← Back</button>
              <button onClick={() => onOpenFull(p)} style={{ background: "none", border: `1px solid ${C.gray200}`, borderRadius: "5px", padding: "4px 10px", cursor: "pointer", fontSize: "12px", color: C.gray500, fontFamily: fonts.body }}>Full note ↗</button>
            </div>

            {/* Patient name + meta */}
            <div style={{ marginBottom: "10px" }}>
              <div style={{ fontSize: "18px", fontWeight: 700, color: C.black, fontFamily: fonts.body, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{p.name}</div>
              <div style={{ fontSize: "12px", color: C.gray400, fontFamily: fonts.mono, marginTop: "3px" }}>
                {[p.age && `${p.age} YO`, p.gender && genderAbbr(p.gender), p.date, p.time].filter(Boolean).join("  ·  ")}
              </div>
            </div>

            {/* Flags row */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center" }}>
              <RiskPill level={p.riskLevel} />
              {p.siFlag && <FlagChip label="SI" color={C.red} bg={C.redLight} />}
              {p.hiFlag && <FlagChip label="HI" color={C.red} bg={C.redLight} />}
              {p.graveDisabilityFlag && <FlagChip label="Grave disability" color={C.red} bg={C.redLight} />}
              {p.firearmsAccess && !p.firearmsSecured && <FlagChip label="Firearms" color={C.amber} bg={C.amberLight} />}
              {p.pregnancyStatus && !["no","not pregnant"].includes((p.pregnancyStatus||"").toLowerCase()) && <FlagChip label="Pregnant" color={C.amber} bg={C.amberLight} />}
              {!p.callComplete && <FlagChip label="Call incomplete" color={C.amber} bg={C.amberLight} />}
            </div>
          </div>

          {/* Grave disability banner */}
          {p.graveDisabilityFlag && (
            <div style={{ background: C.redLight, borderBottom: `1px solid ${C.redBorder}`, padding: "8px 20px", flexShrink: 0 }}>
              <span style={{ fontSize: "12px", fontWeight: 600, color: C.red, fontFamily: fonts.body }}>Grave disability flagged — physician must assess before scheduling.</span>
            </div>
          )}

          {/* Scrollable body */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>

            {/* Chief complaint */}
            <div style={{ marginBottom: "18px" }}>
              <MetaLabel>Chief complaint</MetaLabel>
              <div style={{ fontSize: "14px", color: C.gray700, lineHeight: 1.6, fontFamily: fonts.body }}>{p.chiefComplaint}</div>
            </div>

            {/* Likely Dx */}
            {p.primaryDiagnosis && (
              <div style={{ marginBottom: "18px" }}>
                <MetaLabel>Likely Dx</MetaLabel>
                <div style={{ fontSize: "14px", color: C.black, fontWeight: 500, fontFamily: fonts.body, lineHeight: 1.4 }}>{p.primaryDiagnosis}</div>
              </div>
            )}

            {/* Screening — fit label + rationale, combined, no redundancy */}
            {p.fitRationale && (
              <div style={{ marginBottom: "18px" }}>
                <MetaLabel>Screening</MetaLabel>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px" }}>
                  <FitLabel score={p.fitScore} rationale={null} />
                </div>
                <div style={{ fontSize: "13px", color: C.gray500, lineHeight: 1.6, fontFamily: fonts.body }}>{p.fitRationale}</div>
              </div>
            )}

            {/* Clinical note preview */}
            {p.originalNote && (
              <div style={{ marginBottom: "18px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                  <MetaLabel>Clinical note</MetaLabel>
                  <button onClick={() => onOpenFull(p)} style={{ background: "none", border: "none", fontSize: "11px", color: C.sage, fontFamily: fonts.body, cursor: "pointer", padding: 0, textDecoration: "underline" }}>View full →</button>
                </div>
                <NotePreview text={p.originalNote} />
              </div>
            )}

            {/* Meds + allergies */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "18px" }}>
              {[
                { label: "Current meds", val: p.medicationsCurrent || "—" },
                { label: "Allergies", val: p.allergies || "—", warn: p.allergies && p.allergies !== "NKDA" && p.allergies !== "—" },
              ].map((row, i) => (
                <div key={i} style={{ background: C.paper, border: `1px solid ${row.warn ? C.amberBorder : C.gray200}`, borderRadius: "6px", padding: "10px 12px" }}>
                  <MetaLabel>{row.label}</MetaLabel>
                  <div style={{ fontSize: "13px", color: row.warn ? C.amber : C.black, fontFamily: fonts.body, lineHeight: 1.4, fontWeight: row.warn ? 500 : 400 }}>{row.val}</div>
                </div>
              ))}
            </div>

            {/* Call info */}
            <div style={{ fontSize: "12px", color: C.gray400, fontFamily: fonts.mono }}>
              {p.callDuration || "—"} call · <span style={{ color: p.callComplete ? C.sage : C.amber, fontWeight: 600 }}>{p.callComplete ? "Complete" : "Incomplete"}</span>
            </div>

          </div>

          {/* Sticky footer */}
          {p.status === "pending" ? (
            <div style={{ padding: "14px 20px", background: C.white, borderTop: `1px solid ${C.gray100}`, display: "flex", gap: "8px", flexShrink: 0 }}>
              <button
                onClick={() => onDecide(p, "accept")}
                style={{ flex: 1, background: C.sage, border: "none", borderRadius: "7px", padding: "13px", color: C.white, fontSize: "14px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body }}
                onMouseEnter={e => e.currentTarget.style.background = C.sageDark}
                onMouseLeave={e => e.currentTarget.style.background = C.sage}
              >Accept patient</button>
              <button
                onClick={() => onDecide(p, "decline")}
                style={{ background: "none", border: `1px solid ${C.gray200}`, borderRadius: "7px", padding: "13px 18px", color: C.gray700, fontSize: "14px", cursor: "pointer", fontFamily: fonts.body }}
              >Defer</button>
            </div>
          ) : (
            <div style={{ padding: "14px 20px", borderTop: `1px solid ${C.gray100}`, flexShrink: 0 }}>
              <a href={`tel:${p.phone}`} style={{ display: "block", textAlign: "center", fontSize: "14px", fontWeight: 600, color: C.sage, fontFamily: fonts.body, textDecoration: "none", padding: "11px", border: `1px solid ${C.sageBorder}`, borderRadius: "7px", background: C.sageLight }}>
                {p.phone}
              </a>
            </div>
          )}
        </>}
      </div>
    </>
  );
}

// ─── CLINICIAN SECURITY PAGE ──────────────────────────────────────────────────

function SecurityPage({ onBack }) {
  const StatusBadge = ({ status }) => {
    const s = status === "live"
      ? { bg: C.sageLight, color: C.sageDark, dot: C.sage, label: "Active" }
      : status === "pending"
      ? { bg: C.amberLight, color: C.amber, dot: C.amber, label: "In progress" }
      : { bg: C.gray100, color: C.gray500, dot: C.gray400, label: "Planned" };
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: s.bg, color: s.color, fontSize: "11px", fontWeight: 600, padding: "3px 9px", borderRadius: "20px", fontFamily: fonts.body, whiteSpace: "nowrap" }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, display: "inline-block" }} />
        {s.label}
      </span>
    );
  };

  const SectionLabel = ({ children }) => (
    <div style={{ fontSize: "11px", fontWeight: 600, color: C.gray400, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: fonts.body, marginBottom: "10px" }}>{children}</div>
  );

  const TableSection = ({ children, style = {} }) => (
    <div style={{ border: `1px solid ${C.gray200}`, borderRadius: "8px", overflow: "hidden", background: C.white, ...style }}>{children}</div>
  );

  const Row = ({ label, value, last = false, mono = false }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: last ? "none" : `1px solid ${C.gray100}` }}>
      <span style={{ fontSize: "13px", color: C.gray700, fontFamily: fonts.body }}>{label}</span>
      {typeof value === "string"
        ? <span style={{ fontSize: "13px", color: mono ? C.gray500 : C.black, fontFamily: mono ? fonts.mono : fonts.body }}>{value}</span>
        : value}
    </div>
  );

  const CheckRow = ({ children, last = false }) => (
    <div style={{ display: "flex", gap: "10px", alignItems: "flex-start", padding: "10px 16px", borderBottom: last ? "none" : `1px solid ${C.gray100}` }}>
      <div style={{ width: "14px", height: "14px", borderRadius: "50%", background: C.sageLight, border: `1px solid ${C.sageBorder}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "1px" }}>
        <span style={{ fontSize: "8px", color: C.sage, fontWeight: 700, lineHeight: 1 }}>✓</span>
      </div>
      <div style={{ fontSize: "13px", color: C.gray700, fontFamily: fonts.body, lineHeight: 1.55 }}>{children}</div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.paper, fontFamily: fonts.body }}>

      {/* Nav */}
      <div style={{ padding: "0 32px", borderBottom: `1px solid ${C.gray200}`, display: "flex", alignItems: "center", gap: "16px", height: "56px", position: "sticky", top: 0, background: C.paper, zIndex: 10 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", fontSize: "13px", color: C.gray400, cursor: "pointer", fontFamily: fonts.body, padding: 0 }}>← Dashboard</button>
        <div style={{ width: "1px", height: "14px", background: C.gray200 }} />
        <span style={{ fontSize: "13px", fontWeight: 600, color: C.black, fontFamily: fonts.body }}>Security</span>
      </div>

      <div style={{ maxWidth: "720px", margin: "0 auto", padding: "48px 32px 80px" }}>

        {/* Page header */}
        <div style={{ marginBottom: "40px" }}>
          <h1 style={{ fontSize: "28px", fontWeight: 700, color: C.black, fontFamily: fonts.body, margin: "0 0 10px", letterSpacing: "-0.03em" }}>Security & Compliance</h1>
          <p style={{ fontSize: "14px", color: C.gray500, lineHeight: 1.7, margin: 0 }}>
            PsychXAI is built on HIPAA-ready infrastructure. All patient data is encrypted, access-controlled, and stored exclusively within your practice. This page reflects current status and what is still in progress.
          </p>
        </div>

        {/* Compliance */}
        <div style={{ marginBottom: "36px" }}>
          <SectionLabel>Compliance</SectionLabel>
          <TableSection>
            {[
              { label: "SOC 2 Type 2 (via Supabase)", value: "active", status: "live" },
              { label: "HIPAA-ready infrastructure", value: "active", status: "live" },
              { label: "Business Associate Agreement — Supabase", value: "in progress", status: "pending" },
              { label: "Business Associate Agreement — Vercel", value: "in progress", status: "pending" },
              { label: "SOC 2 Type 2 (PsychXAI direct)", value: "planned", status: "planned" },
              { label: "HITRUST", value: "planned", status: "planned" },
            ].map((item, i, arr) => (
              <Row key={i} label={item.label} value={<StatusBadge status={item.status} />} last={i === arr.length - 1} />
            ))}
          </TableSection>
          <div style={{ fontSize: "12px", color: C.gray400, fontFamily: fonts.body, marginTop: "8px", lineHeight: 1.6 }}>
            Supabase, which powers PsychXAI's database and auth, is SOC 2 Type 2 certified and regularly audited. All projects are governed by the same compliance controls. See <a href="https://supabase.com/docs/guides/security/hipaa-compliance" target="_blank" rel="noreferrer" style={{ color: C.sage, textDecoration: "none" }}>Supabase HIPAA documentation</a>.
          </div>
        </div>

        {/* Data protection */}
        <div style={{ marginBottom: "36px" }}>
          <SectionLabel>Data protection</SectionLabel>
          <TableSection>
            <CheckRow>Encryption at rest — AES-256 across all databases and storage</CheckRow>
            <CheckRow>Encryption in transit — TLS 1.3 on all connections</CheckRow>
            <CheckRow>Row-level security (RLS) — clinicians access only their own patients</CheckRow>
            <CheckRow>Multi-factor authentication required for all staff accounts</CheckRow>
            <CheckRow>Automatic session timeout after period of inactivity</CheckRow>
            <CheckRow last>No PHI written to logs, error traces, or third-party analytics</CheckRow>
          </TableSection>
        </div>

        {/* Audit & access */}
        <div style={{ marginBottom: "36px" }}>
          <SectionLabel>Audit & access control</SectionLabel>
          <TableSection>
            <CheckRow>Every database read, write, and delete is logged with timestamp and user</CheckRow>
            <CheckRow>Platform audit logs available via Supabase dashboard</CheckRow>
            <CheckRow last>Role-based access — admin and clinician roles are separated</CheckRow>
          </TableSection>
        </div>

        {/* AI & data use */}
        <div style={{ marginBottom: "36px" }}>
          <SectionLabel>AI & data use</SectionLabel>
          <TableSection>
            <Row label="Patient data used to train AI models" value="Never" />
            <Row label="PHI sent to AI layer" value="De-identified only" />
            <Row label="Raw call transcript retained" value="Discarded after note generation" last />
          </TableSection>
          <div style={{ fontSize: "12px", color: C.gray400, fontFamily: fonts.body, marginTop: "8px", lineHeight: 1.6 }}>
            Sage processes intake calls to generate clinical briefs. Only de-identified content reaches the AI layer. You retain full ownership of all patient records.
          </div>
        </div>

        {/* Vendor chain */}
        <div style={{ marginBottom: "36px" }}>
          <SectionLabel>Subprocessors</SectionLabel>
          <TableSection>
            {[
              { vendor: "Supabase",          role: "Database, auth, storage",  baa: "In progress", status: "pending" },
              { vendor: "Vercel",            role: "Hosting, edge functions",   baa: "In progress", status: "pending" },
              { vendor: "Twilio",            role: "Voice / phone calls",       baa: "HIPAA-eligible", status: "live" },
              { vendor: "Anthropic",         role: "AI note generation",        baa: "De-identified data only", status: "live" },
            ].map((v, i, arr) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: i < arr.length - 1 ? `1px solid ${C.gray100}` : "none" }}>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: 500, color: C.black, fontFamily: fonts.body }}>{v.vendor}</div>
                  <div style={{ fontSize: "12px", color: C.gray400, fontFamily: fonts.body, marginTop: "1px" }}>{v.role} · {v.baa}</div>
                </div>
                <StatusBadge status={v.status} />
              </div>
            ))}
          </TableSection>
        </div>

        {/* Patient rights */}
        <div style={{ marginBottom: "36px" }}>
          <SectionLabel>Patient rights under HIPAA</SectionLabel>
          <TableSection>
            <CheckRow>Right to access — patients may request a copy of their intake record</CheckRow>
            <CheckRow>Right to amend — patients may request corrections to their record</CheckRow>
            <CheckRow last>Breach notification — reported within 60 days per the HIPAA Breach Notification Rule</CheckRow>
          </TableSection>
        </div>

      </div>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────

function Dashboard({ patients, onSelect, onDecide, onLogout, onSettings, onSecurity, onAdmin, isDemo, clinic, initialTab, onSmsSent }) {
  const [tab, setTab]             = useState(initialTab || "pending");
  const [search, setSearch]       = useState("");
  const [modal, setModal]         = useState(null);
  const [numCopied, setNumCopied] = useState(false);
  const [panelPatient, setPanelPatient] = useState(null);
  const isMobile                  = useMobile();

  const pending  = useMemo(() => patients.filter(p => p.status === "pending"),  [patients]);
  const accepted = useMemo(() => patients.filter(p => p.status === "accepted" || p.status === "reviewed" || p.intakeComplete), [patients]);
  const declined = useMemo(() => patients.filter(p => p.status === "declined"), [patients]);

  const applySearch = list => search.trim()
    ? list.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.chiefComplaint.toLowerCase().includes(search.toLowerCase()))
    : list;

  const siCount  = pending.filter(p => p.siFlag || p.hiFlag || p.graveDisabilityFlag).length;
  const highRisk = pending.filter(p => p.riskLevel === "high").length;

  const [dragOver, setDragOver] = useState(null);
  const dragPatient = useRef(null);
  const handleDragStart = (p) => { dragPatient.current = p; };

  const closeModal   = () => setModal(null);
  const openModal    = (patient, action) => setModal({ patient, action });

  const handleDrop = (targetTab) => {
    setDragOver(null);
    const p = dragPatient.current;
    if (!p) return;
    if (targetTab === "accepted" && p.status !== "accepted") {
      openModal(p, "accept");
    } else if (targetTab === "declined" && p.status !== "declined") {
      openModal(p, "decline");
    } else if (targetTab === "pending" && p.status !== "pending") {
      const now = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      onDecide(p.id, "pending", now, null, null);
    }
    dragPatient.current = null;
  };
  const confirmModal = async ({ reason, note, urgentFlag } = {}) => {
    const now = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    onDecide(modal.patient.id, modal.action, now, reason, note);

    // Fire booking SMS immediately on accept
    if (modal.action === "accept" && modal.patient?.id) {
      console.log("[SMS] Accept triggered. isDemo:", isDemo, "clinic.id:", clinic?.id, "patient.id:", modal.patient?.id);
      if (isDemo) {
        if (onSmsSent) onSmsSent(modal.patient.id, new Date().toISOString());
      } else if (clinic?.id) {
        console.log("[SMS] Firing edge function...");
        try {
          const smsRes = await fetch(
            `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-booking-sms`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
              },
              body: JSON.stringify({
                patientId: modal.patient.id,
                clinicId:  clinic.id,
              }),
            }
          );
          const smsData = await smsRes.json();
          console.log("[SMS] Edge function response:", smsData);
          if (smsData.success && onSmsSent) {
            onSmsSent(modal.patient.id, new Date().toISOString());
          }
        } catch (err) {
          console.error("[SMS] Fetch failed:", err);
        }
      } else {
        console.warn("[SMS] Skipped — clinic.id is missing. clinic:", JSON.stringify(clinic));
      }
    }

    closeModal();
  };

  const TabBtn = ({ k, label, count, alert }) => {
    const isDropTarget = ["pending","accepted","declined"].includes(k);
    const isOver = dragOver === k;
    return (
      <button
        onClick={() => setTab(k)}
        onDragOver={isDropTarget ? (e) => { e.preventDefault(); setDragOver(k); } : undefined}
        onDragLeave={isDropTarget ? () => setDragOver(null) : undefined}
        onDrop={isDropTarget ? () => handleDrop(k) : undefined}
        style={{
          background: isOver ? (k==="accepted" ? C.sageLight : k==="declined" ? C.redLight : C.amberLight) : "transparent",
          border: "none",
          borderBottom: `2px solid ${tab===k ? C.sage : isOver ? (k==="accepted" ? C.sage : k==="declined" ? C.red : C.amber) : "transparent"}`,
          borderRadius: isOver ? "6px 6px 0 0" : undefined,
          padding: isMobile ? "10px 0" : "12px 0",
          marginRight: isMobile ? "16px" : "28px",
          color: tab===k ? C.black : isOver ? (k==="accepted" ? C.sage : k==="declined" ? C.red : C.amber) : C.gray400,
          fontSize: isMobile ? "17px" : "16px",
          fontWeight: tab===k || isOver ? 600 : 400,
          cursor: "pointer", fontFamily: fonts.body,
          display: "inline-flex", alignItems: "center", gap: "6px",
          transition: "all 0.1s", whiteSpace: "nowrap",
        }}
      >
        {isOver ? (k==="accepted" ? "✓ " : k==="declined" ? "✕ " : "↺ ") : ""}{label}
        {count > 0 && (
          <span style={{
            background: tab===k ? C.sage : k==="pending" ? C.amberLight : k==="accepted" ? C.sageLight : k==="declined" ? C.redLight : C.gray100,
            color: tab===k ? C.white : k==="pending" ? C.amber : k==="accepted" ? C.sage : k==="declined" ? C.red : C.gray500,
            fontSize: "13px", fontWeight: 700, borderRadius: "100px", padding: "2px 8px", fontFamily: fonts.body
          }}>{count}</span>
        )}
      </button>
    );
  };

  // Auto-sort pending by urgency: SI/HI flags first, then high risk, then moderate, then low
  const sortedPending = useMemo(() => {
    return [...pending].sort((a, b) => {
      const urgencyScore = (p) => {
        if (p.siFlag || p.hiFlag || p.graveDisabilityFlag) return 0;
        if (p.riskLevel === "high") return 1;
        if (p.riskLevel === "moderate") return 2;
        return 3;
      };
      return urgencyScore(a) - urgencyScore(b);
    });
  }, [pending]);

  const needsSetup = !isDemo && !clinic.booking_url;
  const outreachNeeded = accepted.filter(p => p.status === "accepted" && !p.smsSentAt).length;

  const tabRows = { pending: applySearch(sortedPending), accepted: applySearch(accepted), declined: applySearch(declined) };
  const contextLines = {
    pending:  `${pending.length} patient${pending.length!==1?"s":""} awaiting your screening decision.`,
    accepted: `${accepted.length} patient${accepted.length!==1?"s":""} approved — call to arrange their first appointment.`,
    declined: `${declined.length} patient${declined.length!==1?"s":""} deferred.`,
  };

  const pad = isMobile ? "16px" : "32px";

  return (
    <div style={{ minHeight: "100vh", background: C.paper, fontFamily: fonts.body }}>
      {modal && <DecisionModal patient={modal.patient} action={modal.action} onConfirm={confirmModal} onCancel={closeModal} physician={clinic.physician} />}

      <PatientPanel
        patient={panelPatient}
        onClose={() => setPanelPatient(null)}
        onDecide={(p, action) => { setPanelPatient(null); openModal(p, action); }}
        onOpenFull={(p) => { setPanelPatient(null); onSelect(p, tab); }}
      />

      {isDemo && (
        <div style={{ background: C.amberLight, borderBottom: `1px solid ${C.amberBorder}`, padding: "0 16px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: C.amber, fontSize: "13px", fontFamily: fonts.body, fontWeight: 500 }}>⚠ Demo mode — sample data only. Sign in with your account to see real patients.</span>
        </div>
      )}

      {needsSetup && (
        <div style={{ background: C.amberLight, borderBottom: `1px solid ${C.amberBorder}`, padding: "0 20px", height: "36px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ color: C.amber, fontSize: "13px", fontFamily: fonts.body, fontWeight: 500 }}>
            Setup incomplete — add your booking link so Sage can automatically text patients when you accept them.
          </span>
          <button
            onClick={onSettings}
            style={{ background: C.amber, border: "none", borderRadius: "5px", padding: "3px 12px", color: C.white, fontSize: "12px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body, whiteSpace: "nowrap", marginLeft: "16px" }}
          >
            Set up now →
          </button>
        </div>
      )}

      {/* Nav */}
      <div style={{ padding: `0 ${pad}`, borderBottom: `1px solid ${C.gray200}`, display: "flex", alignItems: "center", justifyContent: "space-between", height: "60px", position: "sticky", top: isDemo ? 32 : 0, background: C.paper, zIndex: 10 }}>
        <button onClick={() => setTab("pending")} style={{ display: "flex", alignItems: "center", gap: "8px", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          <LogoMark size={36} />
          <span style={{ fontSize: "20px", fontWeight: 700, color: C.black, fontFamily: fonts.body, letterSpacing: "-0.03em" }}>PsychXAI</span>
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: "17px" }}>
          {onAdmin && (
            <button onClick={onAdmin} style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: "5px", padding: "4px 10px", color: C.red, fontSize: "16px", cursor: "pointer", fontFamily: fonts.body }}>Admin</button>
          )}
          {!isMobile && <span style={{ fontSize: "17px", color: C.black, fontFamily: fonts.body }}>{clinic.physician ? clinic.physician.split(",")[0] : ""}</span>}
          {onSettings && (
            <button onClick={onSettings} style={{ background: "none", border: `1px solid ${C.sage}`, borderRadius: "6px", padding: "5px 14px", color: C.sage, fontSize: "15px", fontWeight: 500, cursor: "pointer", fontFamily: fonts.body }}>Settings</button>
          )}
          {onSecurity && !isMobile && (
            <button onClick={onSecurity} style={{ background: "none", border: `1px solid ${C.gray200}`, borderRadius: "6px", padding: "5px 12px", color: C.gray500, fontSize: "13px", cursor: "pointer", fontFamily: fonts.body }}>🛡 Security</button>
          )}
          <button onClick={onLogout} style={{ background: "none", border: "none", color: C.gray400, fontSize: "16px", cursor: "pointer", fontFamily: fonts.body, textDecoration: "underline" }}>Sign out</button>
        </div>
      </div>

      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: `${isMobile?"20px":pad} ${pad}`, paddingBottom: "80px" }}>

        {/* Hero number + stats */}
        <div style={{ marginBottom: "20px" }}>
          {patients.length > 0 ? (
            <>
              {/* Compact header row */}
              <div style={{ display: "flex", alignItems: "center", gap: "24px", marginBottom: "20px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                  <span style={{ fontSize: isMobile ? "36px" : "44px", fontWeight: 700, color: C.black, letterSpacing: "-0.04em", lineHeight: 1, fontFamily: fonts.body }}>{patients.length}</span>
                  <span style={{ fontSize: "16px", color: C.gray400, fontFamily: fonts.body }}>intakes this week</span>
                </div>

              </div>
            </>
          ) : (
            // Empty state hero — welcoming, not a big zero
            <div style={{ marginBottom: "8px" }}>
              <div style={{ fontSize: "16px", color: C.gray500, fontFamily: fonts.body, fontWeight: 400 }}>
                No intakes yet this week
              </div>
              <div style={{ fontSize: "16px", color: C.gray400, fontFamily: fonts.body, marginTop: "4px" }}>
                Share your intake number with new patients to get started
              </div>
            </div>
          )}
        </div>



        {/* Tabs + Search — desktop only */}
        {!isMobile && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${C.gray100}`, marginBottom: "16px", gap: "8px" }}>
            <div style={{ display: "flex", overflowX: "auto" }}>
              <TabBtn k="pending"  label="Pending"  count={pending.length}  alert={siCount>0||highRisk>0} />
              <TabBtn k="accepted" label="Accepted" count={accepted.length} alert={false} />
              <TabBtn k="declined" label="Deferred" count={declined.length} alert={false} />
              <TabBtn k="summary"  label="Summary"  count={0}              alert={false} />
              <TabBtn k="setup"    label="Setup"    count={0}              alert={false} />
            </div>
            <div style={{ position: "relative", paddingBottom: "17px", flexShrink: 0 }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." style={{ border: `1px solid ${C.gray200}`, borderRadius: "6px", padding: "6px 12px 6px 28px", fontSize: "17px", fontFamily: fonts.body, color: C.black, background: C.paperDark, outline: "none", width: "220px", fontSize: "15px" }} onFocus={e => e.target.style.borderColor=C.sage} onBlur={e => e.target.style.borderColor=C.gray200} />
              <span style={{ position: "absolute", left: "9px", top: "36%", transform: "translateY(-50%)", color: C.gray400, fontSize: "16px" }}>⌕</span>
            </div>
          </div>
        )}

        {/* Mobile search */}
        {isMobile && tab !== "summary" && (
          <div style={{ position: "relative", marginBottom: "17px" }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search patients..." style={{ width: "100%", border: `1px solid ${C.gray200}`, borderRadius: "6px", padding: "8px 12px 8px 30px", fontSize: "16px", fontFamily: fonts.body, color: C.black, background: C.paperDark, outline: "none", boxSizing: "border-box" }} onFocus={e => e.target.style.borderColor=C.sage} onBlur={e => e.target.style.borderColor=C.gray200} />
            <span style={{ position: "absolute", left: "17px", top: "50%", transform: "translateY(-50%)", color: C.gray400, fontSize: "17px" }}>⌕</span>
          </div>
        )}

        {/* Summary tab */}
        {tab === "summary" && (
          <div style={{ paddingTop: "8px" }}>
            <ROIWidget patients={patients} physicianRate={clinic.physicianRate || 350} />
          </div>
        )}

        {/* Setup tab */}
        {tab === "setup" && (() => {
          const copyNum = () => {
            navigator.clipboard.writeText(formatPhone(clinic.intakeNumber));
            setNumCopied(true); setTimeout(() => setNumCopied(false), 2000);
          };
          return (
          <div style={{ paddingTop: "8px", maxWidth: "580px" }}>

            {/* 1 — Intake number */}
            <div style={{ background: C.sageLight, border: `1px solid ${C.sageBorder}`, borderRadius: "12px", padding: "18px 22px", marginBottom: "10px" }}>
              <div style={{ fontSize: "11px", color: C.sage, fontFamily: fonts.body, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "8px" }}>Your patient intake line</div>
              <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
                <div style={{ fontSize: "24px", fontWeight: 700, color: C.sageDark, fontFamily: fonts.body, letterSpacing: "0.02em" }}>{formatPhone(clinic.intakeNumber)}</div>
                <button onClick={copyNum} style={{ background: numCopied ? C.sage : C.white, border: `1px solid ${numCopied ? C.sage : C.sageBorder}`, borderRadius: "6px", padding: "5px 12px", color: numCopied ? C.white : C.sageDark, fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body, transition: "all 0.15s", whiteSpace: "nowrap" }}>
                  {numCopied ? "✓ Copied" : "Copy"}
                </button>
              </div>
              <div style={{ fontSize: "12px", color: C.sage, fontFamily: fonts.body, marginTop: "5px" }}>Sage answers every call, 24/7 — no staff needed</div>
            </div>

            {/* 2 — Send to a patient right now (primary action) */}
            {clinic.intakeNumber && clinic.intakeNumber !== "—" && (
              <div style={{ background: C.paperDark, border: `1px solid ${C.gray200}`, borderRadius: "12px", padding: "18px 22px", marginBottom: "10px" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: C.black, fontFamily: fonts.body, marginBottom: "10px" }}>Send to your next patient</div>
                <div style={{ fontSize: "13px", color: C.gray700, fontFamily: fonts.body, fontStyle: "italic", lineHeight: 1.65, marginBottom: "12px", background: C.paper, border: `1px solid ${C.gray200}`, borderRadius: "6px", padding: "10px 13px" }}>
                  {`"Hi [Name], before your first appointment please call ${formatPhone(clinic.intakeNumber)} to complete a brief intake with Sage, our AI assistant. It takes about 20 minutes and helps ${clinic.physician || "your doctor"} prepare for your visit."`}
                </div>
                <IntakeShareButtons intakeNumber={clinic.intakeNumber} physician={clinic.physician} slug={clinic.slug} />
              </div>
            )}

            {/* 3 — Other ways to share + trust page (collapsed reference) */}
            <div style={{ background: C.paperDark, border: `1px solid ${C.gray200}`, borderRadius: "12px", padding: "14px 22px" }}>
              <div style={{ fontSize: "12px", fontWeight: 600, color: C.gray500, fontFamily: fonts.body, marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.08em" }}>Other ways to share</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: clinic.slug ? "12px" : "0" }}>
                {[
                  { label: "Website — New Patients page", sub: "Replace your existing callback number." },
                  { label: "PCP & therapist referrals", sub: 'Add: "Please call this number before your first appointment."' },
                ].map((row, i) => (
                  <div key={i} style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                    <div style={{ width: "4px", height: "4px", borderRadius: "50%", background: C.gray400, flexShrink: 0, marginTop: "7px" }} />
                    <div>
                      <span style={{ fontSize: "13px", fontWeight: 600, color: C.black, fontFamily: fonts.body }}>{row.label}</span>
                      <span style={{ fontSize: "12px", color: C.gray500, fontFamily: fonts.body }}> — {row.sub}</span>
                    </div>
                  </div>
                ))}
              </div>
              {clinic.slug && (
                <div style={{ borderTop: `1px solid ${C.gray200}`, paddingTop: "10px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  <span style={{ fontSize: "12px", color: C.gray500, fontFamily: fonts.body }}>Patient info page:</span>
                  <a href={`/intake/${clinic.slug}`} target="_blank" rel="noreferrer" style={{ fontSize: "12px", color: C.sage, fontFamily: fonts.mono, textDecoration: "none", fontWeight: 600 }}>psychxai.com/intake/{clinic.slug}</a>
                  <button onClick={() => navigator.clipboard.writeText(`https://psychxai.com/intake/${clinic.slug}`)} style={{ background: "none", border: `1px solid ${C.sageBorder}`, borderRadius: "4px", padding: "2px 8px", color: C.sage, fontSize: "11px", cursor: "pointer", fontFamily: fonts.body }}>Copy link</button>
                </div>
              )}
            </div>

          </div>
          );
        })()}

        {/* Patient tabs */}
        {tab !== "summary" && tab !== "setup" && (
          <>
            {tabRows[tab].length === 0
              ? <EmptyState tab={tab} />
              : isMobile
                ? <MobileCards rows={tabRows[tab]} onSelect={p => setPanelPatient(p)} onDecide={openModal} showActions={tab==="pending"} showDeclineNote={tab==="declined"} onDragStart={handleDragStart} />
                : <PatientTable rows={tabRows[tab]} onSelect={p => setPanelPatient(p)} onDecide={openModal} showActions={tab==="pending"} showDeclineNote={tab==="declined"} onDragStart={handleDragStart} physician={clinic.physician} hasBookingUrl={!!clinic.booking_url} isDemo={isDemo} />
            }


          </>
        )}

      </div>

      {/* Mobile bottom tab bar */}
      {isMobile && (
        <div className="bottom-tab-bar">
          {[
            { k: "pending",  icon: "📋", label: "Pending",  count: pending.length },
            { k: "accepted", icon: "✓",  label: "Accepted", count: accepted.length },
            { k: "declined", icon: "—",  label: "Deferred", count: declined.length },
            { k: "summary",  icon: "📊", label: "Summary",  count: 0 },
            { k: "setup",    icon: "⚙", label: "Setup",    count: 0 },
          ].map(t => (
            <button key={t.k} className={`bottom-tab${tab === t.k ? " active" : ""}`} onClick={() => setTab(t.k)}>
              <span className="bottom-tab-icon">{t.icon}</span>
              <span>{t.label}{t.count > 0 ? ` · ${t.count}` : ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── NOTE SCREEN ──────────────────────────────────────────────────────────────


// ─── NOTE RENDERER ────────────────────────────────────────────────────────────
// PsychXAI App — build v2.1 SMS-fix
function NoteRenderer({ text, style = {} }) {
  if (!text) return null;

  const SECTION_RE = /^[A-Z][A-Z\s\/&#]{2,}$/;
  const DIVIDER_RE = /^[\u2501\u2500=\-]{4,}$/;

  const sections = [];
  let currentSection = null;
  let headerLines = [];

  text.split("\n").forEach(line => {
    const trimmed = line.trim();
    if (DIVIDER_RE.test(trimmed)) return;
    if (SECTION_RE.test(trimmed)) {
      if (currentSection) sections.push(currentSection);
      currentSection = { title: trimmed, lines: [] };
    } else if (!currentSection) {
      // Skip TYPE line — obvious from context
      if (trimmed && !/^TYPE[\s:]/i.test(trimmed)) headerLines.push(trimmed);
    } else {
      currentSection.lines.push(line);
    }
  });
  if (currentSection) sections.push(currentSection);

  const isSafety = (t) => /^SAFETY/.test(t);
  const isMeds   = (t) => /^MED/.test(t);
  const isPlan   = (t) => /^PLAN/.test(t);
  const isBrief  = (t) => /^CLINICAL/.test(t);

  const sectionTheme = (title) => {
    if (isSafety(title)) return { bg: "#FFF8F7", border: "#E8C4C0", hdrColor: "#B5433A", hdrBg: "#FAF0EE" };
    if (isBrief(title))  return { bg: "#F5F8F5", border: "#B8CEB9", hdrColor: "#2A4A31", hdrBg: "#E8F0E9" };
    return { bg: "#FDFCF8", border: "#DDE0DA", hdrColor: "#4A4F46", hdrBg: "#ECEEE9" };
  };

  // Order: Clinical Brief → Likely DX → Safety → Medications → HPI → Plan → rest
  const sectionOrder = (t) => {
    if (isBrief(t))              return 0;
    if (/^LIKELY DX/.test(t))    return 1;
    if (isSafety(t))             return 3;
    if (isMeds(t))               return 2;
    if (/^HISTORY/.test(t))      return 4;
    if (isPlan(t))               return 5;
    if (/^BIOPSYCH/.test(t))     return 6;
    return 7;
  };
  // Fallback — if no structured sections found (truncated call), wrap everything
  // in a single "Clinical note" card so it's always styled, never raw text
  if (sections.length === 0 && headerLines.length > 0) {
    const allText = headerLines.join("\n");
    sections.push({ title: "CLINICAL NOTE", lines: allText.split("\n") });
    headerLines = [];
  }

  const reordered = [...sections].sort((a, b) => sectionOrder(a.title) - sectionOrder(b.title));

  const renderContent = (lines, title) => {
    const cleaned = lines
      .map(l => l.trimEnd())
      .filter((l, i, arr) => !(l.trim() === "" && (i === 0 || i === arr.length - 1)));

    if (isMeds(title)) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
          {cleaned.map((line, i) => {
            if (!line.trim()) return <div key={i} style={{ height: "4px" }} />;
            const ci = line.indexOf(":");
            if (ci > 0 && ci < 26) {
              return (
                <div key={i} style={{ display: "flex", gap: "10px", fontSize: "13px", lineHeight: 1.65 }}>
                  <span style={{ color: "#7A7F76", fontWeight: 600, minWidth: "100px", flexShrink: 0 }}>{line.slice(0, ci).trim()}</span>
                  <span style={{ color: "#1C1F1A" }}>{line.slice(ci + 1).trim()}</span>
                </div>
              );
            }
            return <div key={i} style={{ fontSize: "13px", color: "#1C1F1A", lineHeight: 1.65 }}>{line.trim()}</div>;
          })}
        </div>
      );
    }

    if (isPlan(title)) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          {cleaned.map((line, i) => {
            if (!line.trim()) return <div key={i} style={{ height: "2px" }} />;
            const isBullet = /^[\-\u00b7\u2022]/.test(line.trim());
            const content  = isBullet ? line.trim().replace(/^[\-\u00b7\u2022]\s*/, "") : line.trim();
            return (
              <div key={i} style={{ display: "flex", gap: "8px", fontSize: "13px", lineHeight: 1.65, color: "#1C1F1A" }}>
                {isBullet && <span style={{ color: "#3D6B47", fontWeight: 700, flexShrink: 0 }}>{"\u2192"}</span>}
                <span>{content}</span>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div>
        {cleaned.map((line, i) => {
          if (!line.trim()) return <div key={i} style={{ height: "5px" }} />;
          const ci = line.indexOf(":");
          const isLbl = ci > 0 && ci < 20 && line.slice(0, ci).trim().split(" ").length <= 2;
          if (isLbl) return (
            <div key={i} style={{ fontSize: "13px", color: "#1C1F1A", lineHeight: 1.7 }}>
              <span style={{ fontWeight: 700 }}>{line.slice(0, ci).trim()}:</span>
              <span> {line.slice(ci + 1).trim()}</span>
            </div>
          );
          return <div key={i} style={{ fontSize: "13px", color: "#1C1F1A", lineHeight: 1.7 }}>{line.trim()}</div>;
        })}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: "820px", display: "flex", flexDirection: "column", gap: "8px", ...style }}>
      {headerLines.length > 0 && (
        <div style={{ marginBottom: "4px", display: "flex", flexDirection: "column", gap: "2px" }}>
          {headerLines.map((line, i) => {
            const ci = line.indexOf(":");
            if (ci > 0 && ci < 22) return (
              <div key={i} style={{ display: "flex", gap: "12px", fontSize: "12px", color: "#7A7F76", lineHeight: 1.8 }}>
                <span style={{ minWidth: "130px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{line.slice(0, ci).trim()}</span>
                <span style={{ color: "#1C1F1A" }}>{line.slice(ci + 1).trim()}</span>
              </div>
            );
            return <div key={i} style={{ fontSize: "12px", color: "#7A7F76" }}>{line}</div>;
          })}
        </div>
      )}
      {reordered.map((sec, si) => {
        const th = sectionTheme(sec.title);
        return (
          <div key={si} style={{ border: `1px solid ${th.border}`, borderRadius: "8px", overflow: "hidden" }}>
            <div style={{ background: th.hdrBg, padding: "6px 14px", borderBottom: `1px solid ${th.border}` }}>
              <span style={{ fontSize: "10.5px", fontWeight: 700, color: th.hdrColor, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                {sec.title}
              </span>
            </div>
            <div style={{ background: th.bg, padding: "11px 14px" }}>
              {renderContent(sec.lines, sec.title)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NoteScreen({ patientId, patients, clinic = {}, onBack, onSign, onDecide, initialTab, onIntakeComplete, isDemo = false }) {
  const patient = patients.find(p => p.id === patientId);
  const [note, setNote]                 = useState(patient.originalNote);
  const [reviewed, setReviewed]         = useState(patient.status === "reviewed" || patient.intakeComplete);
  const [intakeComplete, setIntakeComplete] = useState(patient.intakeComplete || false);
  const [copied, setCopied]             = useState(false);
  const [justReviewed, setJustReviewed] = useState(false);
  const [undoTimer, setUndoTimer]       = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [modal, setModal]               = useState(null);
  const [reviewTime, setReviewTime]     = useState(null);
  const [enteredAt]                     = useState(() => Date.now());
  const noteScrollRef                   = useRef(null);
  const isMobile                        = useMobile();

  const liveStatus = patients.find(p => p.id === patientId)?.status ?? patient.status;
  const decided    = liveStatus === "accepted" || liveStatus === "declined" || liveStatus === "reviewed";
  const hasEdits   = note !== patient.originalNote;

  // 1. Scroll note to top on mount
  useEffect(() => {
    if (noteScrollRef.current) noteScrollRef.current.scrollTop = 0;
  }, [patientId]);

  // 3. Keyboard shortcuts: Cmd+Enter = mark reviewed, Cmd+Shift+C = copy
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        if (liveStatus === "accepted" && !reviewed) handleSign();
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === "c") {
        e.preventDefault();
        if (liveStatus === "accepted" || reviewed) handleCopy();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [liveStatus, reviewed]);

  // Format note for EHR paste — strip raw dividers, clean whitespace
  const formatForEHR = (raw) => {
    if (!raw) return "";
    return raw
      .split("\n")
      .map(line => {
        // Strip ━━━ / --- / === divider lines entirely
        if (/^[\u2501\u2500=\-]{3,}$/.test(line.trim())) return null;
        // Strip the TYPE line
        if (/^TYPE[\s:]/i.test(line.trim())) return null;
        return line;
      })
      .filter(line => line !== null)
      // Collapse 3+ consecutive blank lines to 2
      .reduce((acc, line) => {
        const blank = line.trim() === "";
        const lastTwo = acc.slice(-2).every(l => l.trim() === "");
        if (blank && lastTwo) return acc;
        return [...acc, line];
      }, [])
      .join("\n")
      .trim();
  };

  const handleCopy = () => {
    const text = formatForEHR(note);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true); setTimeout(() => setCopied(false), 2000);
      }).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
  };
  const fallbackCopy = (text) => {
    const el = document.createElement("textarea");
    el.value = text;
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.focus();
    el.select();
    try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
    document.body.removeChild(el);
  };
  const handleSign = () => {
    const mins = Math.round((Date.now() - enteredAt) / 60000);
    setReviewTime(mins);
    setReviewed(true);
    setJustReviewed(true);
    onSign(patient.id, note);
    const t = setTimeout(() => setUndoTimer(null), 20000);
    setUndoTimer(t);
  };

  const handleIntakeComplete = () => {
    setIntakeComplete(true);
    if (onIntakeComplete) onIntakeComplete(patient.id);
  };

  const handleUndo = () => {
    if (undoTimer) clearTimeout(undoTimer);
    setUndoTimer(null);
    setReviewed(false);
    setJustReviewed(false);
    // Revert status in Supabase via parent
    onSign(patient.id, note, true); // pass undo flag
  };
  const confirmDecide = ({ reason, note } = {}) => {
    const now = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
    onDecide(patient.id, modal, now, reason, note);
    setModal(null);
  };

  const nameParts = patient.name.split(" ");
  const clinicalName = nameParts.length >= 2 ? `${nameParts[nameParts.length-1]}, ${nameParts.slice(0,-1).join(" ")}` : patient.name;

  // 4. Time saved label (vs ~18 min avg manual intake note)
  const timeSavedMins = Math.max(0, 18 - (reviewTime ?? 0));
  const timeSavedLabel = `~${timeSavedMins} min saved`;

  // Single highest-priority alert only
  const alertBanner = (() => {
    if (patient.graveDisabilityFlag) return { bg: C.redLight, border: C.redBorder, icon: "", bold: "Grave disability flagged.", text: "Patient may be unable to meet basic needs. Physician must assess before scheduling.", color: C.red };
    if (patient.siFlag) return { bg: C.redLight, border: C.redBorder, icon: "", bold: "Suicidal ideation flagged.", text: "Review safety section carefully. Direct clinician assessment required.", color: C.red };
    if (patient.hiFlag) return { bg: C.redLight, border: C.redBorder, icon: "", bold: "Homicidal ideation flagged.", text: "Review safety section carefully. Duty to warn assessment required.", color: C.red };
    if (patient.fitScore === "decline") return { bg: C.redLight, border: C.redBorder, icon: "", bold: "Higher level of care indicated.", text: patient.fitRationale, color: C.red };
    if (liveStatus === "declined") return { bg: C.redLight, border: C.redBorder, icon: "", bold: "Deferred.", text: "Not appropriate for this practice at this time.", color: C.red };
    if (patient.pregnancyStatus && patient.pregnancyStatus.toLowerCase() !== "no" && patient.pregnancyStatus.toLowerCase() !== "not pregnant") return { bg: C.amberLight, border: C.amberBorder, icon: "", bold: "Pregnancy reported.", text: `${patient.pregnancyStatus} — medication plan requires review before appointment.`, color: C.amber };
    if (patient.firearmsAccess && !patient.firearmsSecured) return { bg: C.amberLight, border: C.amberBorder, icon: "", bold: "Firearm access — unsecured.", text: "Safe storage counseling indicated at appointment.", color: C.amber };
    if (patient.fitScore === "review" && liveStatus === "pending") { const rat = patient.fitRationale.replace(/^Clinical review advised before scheduling\.?\s*/i, "").replace(/^Clinical review advised\.?\s*/i, ""); const short = rat.split(".")[0]; return { bg: C.amberLight, border: C.amberBorder, icon: "", bold: "Clinical review advised.", text: short + ".", color: C.amber }; }
    if (patient.fitScore === "accept" && liveStatus === "pending") {
      const rat = (patient.fitRationale || "").replace(/^Appropriate for outpatient psychiatry\.?\s*/i, "").replace(/^Appropriate for outpatient intake\.?\s*/i, "").replace(/^Appropriate for intake\.?\s*/i, "");
      const short = rat.split(".")[0];
      return { bg: C.sageLight, border: C.sageBorder, icon: "", bold: "Appropriate for intake.", text: short ? short + "." : "No safety concerns identified.", color: C.sageDark };
    }
    // justReviewed handled by sidebar only
    return null;
  })();

  const epicBg = "#FFFFFF";
  const epicBorder = "#C8C8C8";
  const epicText = "#1A1A1A";
  const epicMuted = "#555555";
  const epicFont = "'Segoe UI', 'Arial', sans-serif";
  const epicMono = "'Consolas', 'SF Mono', 'Courier New', monospace";
  const noteMaxWidth = isMobile ? "100%" : "960px";

  return (
    <div style={{ minHeight: "100vh", background: C.paper, fontFamily: fonts.body, display: "flex", flexDirection: "column" }}>
      {modal && <DecisionModal patient={patient} action={modal} onConfirm={confirmDecide} onCancel={() => setModal(null)} physician={clinic.physician} />}

      {/* Sticky top nav — PsychXAI chrome */}
      <div style={{ position: "sticky", top: 0, zIndex: 20, background: C.paper, borderBottom: `1px solid ${C.gray100}` }}>
        {/* Compliance strip */}
        <div style={{ background: C.paperDark, padding: "4px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {hasEdits && !reviewed && (
              <span style={{ fontSize: "11px", color: saveStatus === "saving" ? C.amber : saveStatus === "unsaved" ? C.gray400 : C.gray400, fontFamily: fonts.body }}>
                {saveStatus === "saving" ? "Saving…" : saveStatus === "unsaved" ? "Unsaved" : "✓ Draft saved"}
              </span>
            )}
  
          </div>
        </div>

        {/* Nav row */}
        <div style={{ padding: "8px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <button onClick={onBack} style={{ background: "transparent", border: "none", color: C.gray500, fontSize: "14px", cursor: "pointer", padding: 0, fontFamily: fonts.body, fontWeight: 500 }}>← {initialTab ? initialTab.charAt(0).toUpperCase() + initialTab.slice(1) : "Pending"}</button>
            <div style={{ width: "1px", height: "14px", background: C.gray200 }} />
            <span style={{ fontSize: "18px", color: C.black, fontWeight: 700, fontFamily: fonts.body }}>{clinicalName}</span>
            <StatusPill status={liveStatus} />
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          </div>
        </div>

        {/* Single alert banner */}
        {alertBanner && (
          <div style={{ background: alertBanner.bg, borderTop: `1px solid ${alertBanner.border}`, padding: "7px 20px", display: "flex", alignItems: "flex-start", gap: "8px" }}>
            {alertBanner.icon && <span style={{ flexShrink: 0, fontSize: "14px" }}>{alertBanner.icon}</span>}
            <span style={{ fontSize: "14px", color: alertBanner.color, fontFamily: fonts.body }}>
              <strong>{alertBanner.bold}</strong>{" "}{alertBanner.text}
            </span>
            {justReviewed && (
              <button onClick={handleCopy} style={{ marginLeft: "auto", flexShrink: 0, background: C.sage, border: "none", borderRadius: "4px", padding: "3px 12px", color: C.white, fontSize: "13px", cursor: "pointer", fontFamily: fonts.body }}>Copy</button>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* Left sidebar — patient context */}
        {!isMobile && (
          <div style={{ width: "220px", flexShrink: 0, borderRight: `1px solid ${C.gray100}`, padding: "20px 16px", display: "flex", flexDirection: "column", gap: "16px", overflowY: "auto", background: C.paper }}>
            <div>
              <div style={{ fontSize: "13px", color: C.gray500, fontFamily: fonts.body }}>{patient.age} · {patient.date}</div>
              {patient.phone && <div style={{ fontSize: "13px", color: C.gray500, fontFamily: fonts.body, marginTop: "2px" }}>{patient.phone}</div>}
            </div>
            <div style={{ height: "1px", background: C.gray100 }} />
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "8px" }}>
                <RiskPill level={patient.riskLevel} />
              </div>
              <div>
                <div style={{ fontSize: "10px", color: C.gray400, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "2px", fontFamily: fonts.body }}>Likely Dx</div>
                <div style={{ fontSize: "13px", color: patient.primaryDiagnosis ? C.black : C.gray400, fontFamily: fonts.body, lineHeight: 1.4, fontStyle: patient.primaryDiagnosis ? "normal" : "italic" }}>
                  {patient.primaryDiagnosis || "Pending review"}
                </div>
              </div>
              {patient.medicationsCurrent && <div><div style={{ fontSize: "10px", color: C.gray400, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "2px", fontFamily: fonts.body }}>Meds</div><div style={{ fontSize: "13px", color: C.black, fontFamily: fonts.body, lineHeight: 1.4 }}>{patient.medicationsCurrent}</div></div>}
              {patient.allergies && <div><div style={{ fontSize: "10px", color: C.gray400, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "2px", fontFamily: fonts.body }}>Allergies</div><div style={{ fontSize: "13px", color: patient.allergies === "NKDA" ? C.gray500 : C.amber, fontFamily: fonts.body, fontWeight: patient.allergies === "NKDA" ? 400 : 500 }}>{patient.allergies}</div></div>}
              {patient.chiefComplaint && <div><div style={{ fontSize: "10px", color: C.gray400, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "2px", fontFamily: fonts.body }}>CC</div><div style={{ fontSize: "13px", color: C.gray700, fontFamily: fonts.body, lineHeight: 1.4 }}>{patient.chiefComplaint}</div></div>}
            </div>
            <div style={{ height: "1px", background: C.gray100 }} />
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>

              {liveStatus === "accepted" || liveStatus === "reviewed" || patient.intakeComplete ? (() => {
                const hasBookingUrl = !!clinic.booking_url;
                const smsDone       = isDemo
                  ? (liveStatus === "accepted" || liveStatus === "reviewed")
                  : !!patients.find(p => p.id === patientId)?.smsSentAt;
                const step2Done     = reviewed;
                const step3Done     = intakeComplete;

                const Circle = ({ done, num }) => (
                  <div style={{
                    width: "20px", height: "20px", borderRadius: "50%", flexShrink: 0,
                    background: done ? C.sage : C.gray200,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "11px", color: done ? C.white : C.gray400,
                    fontWeight: 700, transition: "background 0.2s",
                  }}>
                    {done ? "✓" : num}
                  </div>
                );

                const Line = ({ done }) => (
                  <div style={{ width: "1px", minHeight: "14px", flex: 1, background: done ? C.sage : C.gray200, marginTop: "2px", transition: "background 0.2s" }} />
                );

                const StepLabel = ({ done, title, sub }) => (
                  <div style={{ paddingTop: "1px", flex: 1 }}>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: done ? C.sageDark : C.black, fontFamily: fonts.body }}>{title}</div>
                    <div style={{ fontSize: "11px", color: C.gray400, fontFamily: fonts.body, marginTop: "1px", lineHeight: 1.4 }}>{sub}</div>
                  </div>
                );

                const actionBtn = (label, onClick) => (
                  <button onClick={onClick} style={{ marginTop: "6px", background: C.sage, border: "none", borderRadius: "5px", padding: "5px 10px", color: C.white, fontSize: "11px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body, display: "block" }}>
                    {label}
                  </button>
                );

                return (
                  <div style={{ display: "flex", flexDirection: "column" }}>

                    {hasBookingUrl ? (
                      // ── Variant A: booking link — 3 steps ────────────────
                      <>
                        {/* Step 1 — SMS sent (auto) */}
                        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start", paddingBottom: "10px" }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Circle done={smsDone} num="1" />
                            <Line done={smsDone || step2Done} />
                          </div>
                          <StepLabel
                            done={smsDone}
                            title="SMS sent to patient"
                            sub={smsDone
                              ? (() => {
                                const ts = patients.find(p => p.id === patientId)?.smsSentAt;
                                const d  = ts ? new Date(ts) : null;
                                const valid = d && !isNaN(d);
                                const dateStr = valid
                                  ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
                                  : new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
                                const timeStr = valid
                                  ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
                                  : new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
                                return `Sent ${dateStr} · ${timeStr} · booking link delivered`;
                              })()
                              : "Fires automatically when you accept"}
                          />
                        </div>

                        {/* Step 2 — Appt booked */}
                        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start", paddingBottom: "10px" }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Circle done={step2Done} num="2" />
                            <Line done={step2Done || step3Done} />
                          </div>
                          <div style={{ paddingTop: "1px", flex: 1 }}>
                            <StepLabel done={step2Done} title="Appt booked by patient" sub={step2Done ? "Confirmed" : "Confirm once patient books via link"} />
                            {!step2Done && liveStatus === "accepted" && actionBtn("Mark booked", handleSign)}
                          </div>
                        </div>

                        {/* Step 3 — Intake complete */}
                        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                          <Circle done={step3Done} num="3" />
                          <div style={{ paddingTop: "1px", flex: 1 }}>
                            <StepLabel done={step3Done} title="Intake complete" sub={step3Done ? `Signed · ${clinic.physician ? clinic.physician.split(",")[0] : ""}` : "After first appointment"} />
                            {!step3Done && step2Done && actionBtn("Mark complete", handleIntakeComplete)}
                          </div>
                        </div>
                      </>
                    ) : (
                      // ── Variant B: no booking link — 2 steps ─────────────
                      <>
                        {/* Step 1 — Book */}
                        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start", paddingBottom: "10px" }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Circle done={step2Done} num="1" />
                            <Line done={step2Done || step3Done} />
                          </div>
                          <div style={{ paddingTop: "1px", flex: 1 }}>
                            <StepLabel done={step2Done} title="Appointment booked" sub={step2Done ? "Confirmed" : ""} />
                            {!step2Done && liveStatus === "accepted" && actionBtn("Mark booked", handleSign)}
                          </div>
                        </div>

                        {/* Step 2 — Intake complete */}
                        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                          <Circle done={step3Done} num="2" />
                          <div style={{ paddingTop: "1px", flex: 1 }}>
                            <StepLabel done={step3Done} title="Intake complete" sub={step3Done ? `Signed · ${clinic.physician ? clinic.physician.split(",")[0] : ""}` : ""} />
                            {!step3Done && step2Done && actionBtn("Mark complete", handleIntakeComplete)}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Copy note */}
                    <div style={{ marginTop: "14px", paddingTop: "12px", borderTop: `1px solid ${C.gray100}` }}>
                      <button onClick={handleCopy} style={{ width: "100%", background: copied ? C.sageLight : "transparent", border: `1px solid ${copied ? C.sageBorder : C.gray200}`, borderRadius: "5px", padding: "7px 12px", color: copied ? C.sageDark : C.gray600, fontSize: "12px", cursor: "pointer", fontFamily: fonts.body, textAlign: "left", transition: "all 0.15s" }}>
                        {copied ? "✓ Copied to clipboard" : "Copy note to EHR"}
                      </button>
                    </div>
                    {undoTimer && (
                      <button onClick={handleUndo} style={{ background: "transparent", border: "none", color: C.gray400, fontSize: "11px", cursor: "pointer", fontFamily: fonts.body, textDecoration: "underline", padding: "4px 0", textAlign: "left" }}>Undo</button>
                    )}
                  </div>
                );
              })() : (
                <>
                  {!decided && <>
                    <button onClick={() => setModal("accept")} style={{ background: C.sage, border: "none", borderRadius: "5px", padding: "9px 12px", color: C.white, fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body, textAlign: "left" }}>✓ Accept patient</button>
                    <button onClick={() => setModal("decline")} style={{ background: "transparent", border: `1px solid ${C.gray200}`, borderRadius: "5px", padding: "9px 12px", color: C.gray500, fontSize: "13px", cursor: "pointer", fontFamily: fonts.body, textAlign: "left" }}>Defer</button>
                  </>}
                </>
              )}
            </div>
          </div>
        )}

        {/* Epic-style note panel */}
        <div ref={noteScrollRef} style={{ flex: 1, overflowY: "auto", background: epicBg, display: "flex", flexDirection: "column", borderLeft: `1px solid ${C.gray100}` }}>
          {/* Epic-style note header bar */}
          <div style={{ background: "#F0F0F0", borderBottom: `1px solid ${epicBorder}`, padding: "6px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
<div />
            <span style={{ fontSize: "11px", color: reviewed ? "#2A6B2A" : "#999", fontFamily: epicFont, fontWeight: reviewed ? 600 : 400 }}>
            </span>
          </div>

          {/* Note content */}
          <div style={{ flex: 1, padding: "20px 28px 60px", minHeight: 0 }}>
            {reviewed
              ? <NoteRenderer text={showOriginal ? patient.originalNote : note} style={{ fontFamily: epicMono, maxWidth: noteMaxWidth }} />
              : <NoteRenderer text={showOriginal ? patient.originalNote : note} style={{ fontFamily: epicMono, maxWidth: noteMaxWidth, userSelect: "text" }} />
            }
          </div>

          {/* Sticky footer — mark reviewed or time saved */}
          <div style={{ borderTop: `1px solid ${epicBorder}`, padding: "10px 20px", background: "#F8F8F8", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
            <span style={{ fontSize: "11px", color: "#999", fontFamily: epicFont, flex: 1 }}>
              AI-generated draft · PsychXAI · For clinician review only · Must be reviewed, edited, and co-signed by treating psychiatrist or psychiatric NP before inclusion in any medical record.
            </span>
            {!isMobile && reviewed && justReviewed && (
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
                <span style={{ fontSize: "12px", color: C.sageDark, fontFamily: fonts.body, background: C.sageLight, border: `1px solid ${C.sageBorder}`, borderRadius: "4px", padding: "4px 10px" }}>
                  ✓ Reviewed · {timeSavedLabel}
                </span>
                <button
                  onClick={handleCopy}
                  title="Copy to EHR (⌘⇧C)"
                  style={{ background: copied ? C.sageLight : C.white, border: `1px solid ${copied ? C.sageBorder : "#C8C8C8"}`, borderRadius: "5px", padding: "7px 16px", color: copied ? C.sageDark : "#333", fontSize: "13px", cursor: "pointer", fontFamily: fonts.body, whiteSpace: "nowrap" }}
                >
                  {copied ? "✓ Copied" : "Copy to EHR"} <span style={{ fontSize: "11px", opacity: 0.6 }}>⌘⇧C</span>
                </button>
              </div>
            )}
            {!isMobile && reviewed && !justReviewed && (
              <button
                onClick={handleCopy}
                title="Copy to EHR (⌘⇧C)"
                style={{ background: copied ? C.sageLight : C.white, border: `1px solid ${copied ? C.sageBorder : "#C8C8C8"}`, borderRadius: "5px", padding: "7px 16px", color: copied ? C.sageDark : "#333", fontSize: "13px", cursor: "pointer", fontFamily: fonts.body, whiteSpace: "nowrap", flexShrink: 0 }}
              >
                {copied ? "✓ Copied" : "Copy to EHR"} <span style={{ fontSize: "11px", opacity: 0.6 }}>⌘⇧C</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile footer */}
      {isMobile && !decided && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: C.paper, borderTop: `1px solid ${C.gray100}`, padding: "10px 16px", display: "flex", gap: "8px", zIndex: 30 }}>
          <button onClick={() => setModal("accept")} style={{ flex: 1, background: C.sage, border: "none", borderRadius: "5px", padding: "11px", color: C.white, fontSize: "15px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body }}>Accept</button>
          <button onClick={() => setModal("decline")} style={{ background: "transparent", border: `1px solid ${C.gray200}`, borderRadius: "5px", padding: "11px 18px", color: C.gray500, fontSize: "15px", cursor: "pointer", fontFamily: fonts.body }}>Defer</button>
        </div>
      )}
      {isMobile && liveStatus === "accepted" && !reviewed && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: C.paper, borderTop: `1px solid ${C.gray100}`, padding: "10px 16px", display: "flex", gap: "8px", zIndex: 30 }}>
          <button onClick={handleSign} style={{ flex: 1, background: C.sage, border: "none", borderRadius: "5px", padding: "11px", color: C.white, fontSize: "15px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body }}>Mark as scheduled</button>
          <button onClick={handleCopy} style={{ background: C.white, border: `1px solid ${C.gray200}`, borderRadius: "5px", padding: "11px 16px", color: C.gray700, fontSize: "15px", cursor: "pointer", fontFamily: fonts.body }}>{copied ? "✓" : "Copy"}</button>
        </div>
      )}
    </div>
  );
}

// ─── SETTINGS ─────────────────────────────────────────────────────────────────

// ─── SETTINGS ─────────────────────────────────────────────────────────────────

function Settings({ clinic, onBack, onLogout, onClinicUpdate }) {
  const [currentPw, setCurrentPw]   = useState("");
  const [newPw, setNewPw]           = useState("");
  const [confirmPw, setConfirmPw]   = useState("");
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState(null);
  const [err, setErr]               = useState(null);
  const [bookingUrl, setBookingUrl]   = useState(clinic.booking_url || "");
  const [physicianRate, setPhysicianRate] = useState(String(clinic.physicianRate || 350));
  const [rateSaving, setRateSaving]       = useState(false);
  const [rateMsg, setRateMsg]             = useState(null);
  const [urlSaving, setUrlSaving]     = useState(false);
  const [urlMsg, setUrlMsg]           = useState(null);
  const [urlErr, setUrlErr]           = useState(null);

  // Inline practice editing
  const [editingPractice, setEditingPractice] = useState(false);
  const [editName, setEditName]               = useState(clinic.name || "");
  const [editPhysician, setEditPhysician]     = useState(clinic.physician || "");
  const [practiceSaving, setPracticeSaving]   = useState(false);
  const [practiceMsg, setPracticeMsg]         = useState(null);

  const detectSystem = (url) => {
    if (!url) return null;
    if (url.includes("clientsecure.me")) return "simplepractice";
    if (url.includes("janeapp.com"))     return "jane";
    if (url.includes("calendly.com"))    return "calendly";
    return "other";
  };
  const [schedulingSystem, setSchedulingSystem] = useState(clinic.scheduling_system || detectSystem(clinic.booking_url));
  const isMobile                      = useMobile();
  const pad                           = isMobile ? "16px" : "32px";

  const handleSaveBooking = async () => {
    setUrlMsg(null); setUrlErr(null);
    if (bookingUrl.trim()) {
      try { new URL(bookingUrl); } catch { setUrlErr("Please enter a valid URL (include https://)."); return; }
    }
    setUrlSaving(true);
    const { error } = await db.clinics.update(clinic.id, {
      booking_url: bookingUrl.trim() || null,
      scheduling_system: schedulingSystem || null,
    });
    setUrlSaving(false);
    if (error) { setUrlErr("Failed to save. Please try again."); return; }
    // Update parent so Settings shows correct state on re-entry
    if (onClinicUpdate) onClinicUpdate({ booking_url: bookingUrl.trim() || null, scheduling_system: schedulingSystem || null });
    setUrlMsg("Saved.");
  };

  const handleSaveRate = async () => {
    const parsed = parseInt(physicianRate, 10);
    if (isNaN(parsed) || parsed < 50 || parsed > 5000) { setRateMsg("Please enter a valid rate between $50 and $5,000."); return; }
    setRateSaving(true);
    if (clinic.id) {
      await db.clinics.update(clinic.id, { physician_rate: parsed });
    }
    if (onClinicUpdate) onClinicUpdate({ physicianRate: parsed });
    setRateSaving(false);
    setRateMsg("Saved.");
    setTimeout(() => setRateMsg(null), 3000);
  };

  const handleSavePractice = async () => {
    setPracticeSaving(true);
    if (clinic.id) {
      await db.clinics.update(clinic.id, {
        name:           editName.trim() || clinic.name,
        physician_name: editPhysician.trim() || clinic.physician,
      });
    }
    if (onClinicUpdate) onClinicUpdate({
      name:      editName.trim() || clinic.name,
      physician: editPhysician.trim() || clinic.physician,
    });
    setPracticeSaving(false);
    setEditingPractice(false);
    setPracticeMsg("Saved.");
    setTimeout(() => setPracticeMsg(null), 3000);
  };

  const handleChangePassword = async () => {
    setMsg(null); setErr(null);
    if (!currentPw) { setErr("Please enter your current password."); return; }
    if (!newPw || !confirmPw) { setErr("Please fill in all fields."); return; }
    if (newPw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (newPw !== confirmPw) { setErr("New passwords do not match."); return; }
    if (newPw === currentPw) { setErr("New password must be different from your current password."); return; }
    setSaving(true);
    // Verify current password by re-authenticating
    const { error: signInErr } = await auth.signInWithPassword({
      email: clinic._email,
      password: currentPw,
    });
    if (signInErr) { setSaving(false); setErr("Current password is incorrect."); return; }
    const { error } = await auth.updateUser({ password: newPw });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    setMsg("Password updated successfully.");
    setCurrentPw(""); setNewPw(""); setConfirmPw("");
  };

  return (
    <div style={{ minHeight: "100vh", background: C.paper, fontFamily: fonts.body }}>
      {/* Nav */}
      <div style={{ padding: `0 ${pad}`, borderBottom: `1px solid ${C.gray200}`, display: "flex", alignItems: "center", justifyContent: "space-between", height: "60px", position: "sticky", top: 0, background: C.paper, zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "17px" }}>
          <button onClick={onBack} style={{ background: "none", border: "none", color: C.gray400, fontSize: "17px", cursor: "pointer", fontFamily: fonts.body, padding: 0 }}>← Dashboard</button>
          <div style={{ width: "1px", height: "17px", background: C.gray200 }} />
          <span style={{ fontSize: fs.sm, fontWeight: 600, color: C.black, fontFamily: fonts.body }}>Settings</span>
        </div>
        <button onClick={onLogout} style={{ background: "none", border: "none", color: C.gray400, fontSize: "16px", cursor: "pointer", fontFamily: fonts.body, textDecoration: "underline" }}>Sign out</button>
      </div>

      <div style={{ maxWidth: "680px", margin: "0 auto", padding: `40px ${pad}` }}>

        {/* Practice info — inline editable */}
        <div style={{ marginBottom: "32px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "17px" }}>
            <div style={{ fontSize: fs.xs, color: C.gray400, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: fonts.body }}>Practice</div>
            {!editingPractice
              ? <button onClick={() => { setEditName(clinic.name || ""); setEditPhysician(clinic.physician || ""); setEditingPractice(true); }} style={{ background: "none", border: `1px solid ${C.gray200}`, borderRadius: "5px", padding: "3px 10px", color: C.gray500, fontSize: "12px", cursor: "pointer", fontFamily: fonts.body }}>Edit</button>
              : <div style={{ display: "flex", gap: "8px" }}>
                  <button onClick={() => setEditingPractice(false)} style={{ background: "none", border: `1px solid ${C.gray200}`, borderRadius: "5px", padding: "3px 10px", color: C.gray500, fontSize: "12px", cursor: "pointer", fontFamily: fonts.body }}>Cancel</button>
                  <button onClick={handleSavePractice} disabled={practiceSaving} style={{ background: C.sage, border: "none", borderRadius: "5px", padding: "3px 12px", color: C.white, fontSize: "12px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body }}>
                    {practiceSaving ? "Saving…" : "Save"}
                  </button>
                </div>
            }
          </div>
          <div style={{ background: C.paperDark, borderRadius: "8px", border: `1px solid ${editingPractice ? C.sageBorder : C.gray200}`, overflow: "hidden", transition: "border-color 0.15s" }}>
            {/* Editable rows */}
            {[
              { label: "Practice name", value: clinic.name,      editVal: editName,      setVal: setEditName,      editable: true },
              { label: "Provider",      value: clinic.physician, editVal: editPhysician, setVal: setEditPhysician, editable: true },
            ].map(({ label, value, editVal, setVal, editable }, i) => (
              <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 20px", borderBottom: `1px solid ${C.gray100}` }}>
                <span style={{ fontSize: fs.xs, color: C.gray500, fontFamily: fonts.body, flexShrink: 0, minWidth: "110px" }}>{label}</span>
                {editingPractice && editable
                  ? <input
                      value={editVal}
                      onChange={e => setVal(e.target.value)}
                      style={{ flex: 1, border: `1px solid ${C.gray200}`, borderRadius: "5px", padding: "5px 10px", fontSize: "14px", fontFamily: fonts.body, color: C.black, background: C.white, outline: "none", textAlign: "right" }}
                      onFocus={e => e.target.style.borderColor = C.sage}
                      onBlur={e => e.target.style.borderColor = C.gray200}
                    />
                  : <span style={{ fontSize: fs.sm, color: C.black, fontFamily: fonts.body, fontWeight: 500 }}>{value || "—"}</span>
                }
              </div>
            ))}
            {/* Read-only rows */}
            {[
              { label: "Intake number", value: clinic.intakeNumber },
              { label: "Account email", value: clinic._email },
            ].map(({ label, value }, i) => (
              <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 20px", borderBottom: i === 0 ? `1px solid ${C.gray100}` : "none" }}>
                <span style={{ fontSize: fs.xs, color: C.gray500, fontFamily: fonts.body, flexShrink: 0, minWidth: "110px" }}>{label}</span>
                <span style={{ fontSize: fs.sm, color: C.gray400, fontFamily: fonts.body }}>{value || "—"}</span>
              </div>
            ))}
          </div>
          {practiceMsg && <div style={{ fontSize: "13px", color: C.sage, marginTop: "8px", fontFamily: fonts.body }}>✓ {practiceMsg}</div>}
          {!editingPractice && <div style={{ fontSize: "12px", color: C.gray400, marginTop: "6px", fontFamily: fonts.body }}>Questions or support — <a href="mailto:support@psychxai.com" style={{ color: C.sage, textDecoration: "none" }}>support@psychxai.com</a></div>}
        </div>

        {/* Patient Booking */}
        <div style={{ marginBottom: "32px" }}>
          <div style={{ fontSize: fs.xs, color: C.gray400, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "17px", fontFamily: fonts.body }}>Patient Booking</div>
          <div style={{ background: C.paperDark, borderRadius: "8px", padding: "20px", border: `1px solid ${C.gray200}` }}>

            {/* Step 1 — scheduling system picker */}
            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: fs.xs, color: C.gray500, display: "block", marginBottom: "8px", fontFamily: fonts.body, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Your scheduling system
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {[
                  { key: "simplepractice", label: "SimplePractice",  hint: "clientsecure.me",         hasLink: true,  logo: (
                    <svg width="20" height="20" viewBox="0 0 40 40" fill="none"><rect width="40" height="40" rx="8" fill="#1A3557"/><rect x="10" y="12" width="5" height="16" rx="2" fill="white"/><rect x="10" y="12" width="14" height="5" rx="2" fill="white"/><rect x="10" y="20" width="12" height="4" rx="2" fill="white"/></svg>
                  )},
                  { key: "jane",           label: "Jane App",         hint: "janeapp.com",              hasLink: true,  logo: (
                    <svg width="20" height="20" viewBox="0 0 40 40" fill="none"><rect width="40" height="40" rx="8" fill="#E8734A"/><rect x="17" y="10" width="6" height="14" rx="3" fill="white"/><path d="M11 28c0-3.3 2.7-6 6-6h6c3.3 0 6 2.7 6 6" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round"/></svg>
                  )},
                  { key: "calendly",       label: "Calendly",         hint: "calendly.com",             hasLink: true,  logo: (
                    <svg width="20" height="20" viewBox="0 0 40 40" fill="none"><rect width="40" height="40" rx="8" fill="#FFFFFF" stroke="#E0E0E0"/><rect x="8" y="12" width="24" height="20" rx="3" fill="#006BFF"/><rect x="8" y="12" width="24" height="8" rx="3" fill="#006BFF"/><rect x="8" y="17" width="24" height="3" fill="#0052CC"/><rect x="13" y="8" width="3" height="7" rx="1.5" fill="#006BFF"/><rect x="24" y="8" width="3" height="7" rx="1.5" fill="#006BFF"/><rect x="12" y="25" width="4" height="3" rx="1" fill="white"/><rect x="18" y="25" width="4" height="3" rx="1" fill="white"/><rect x="24" y="25" width="4" height="3" rx="1" fill="white"/></svg>
                  )},
                  { key: "therapynotes",   label: "TherapyNotes",     hint: "No booking link",          hasLink: false, logo: (
                    <svg width="20" height="20" viewBox="0 0 40 40" fill="none"><rect width="40" height="40" rx="8" fill="#00897B"/><rect x="11" y="10" width="14" height="18" rx="2" fill="white" opacity="0.9"/><rect x="14" y="14" width="8" height="1.5" rx="0.75" fill="#00897B"/><rect x="14" y="17" width="8" height="1.5" rx="0.75" fill="#00897B"/><rect x="14" y="20" width="5" height="1.5" rx="0.75" fill="#00897B"/><path d="M27 24l-4 4-2-2 1-1 1 1 3-3 1 1z" fill="white"/></svg>
                  )},
                  { key: "google",         label: "Google Calendar",  hint: "No booking link",          hasLink: false, logo: (
                    <svg width="20" height="20" viewBox="0 0 40 40" fill="none"><rect width="40" height="40" rx="8" fill="white" stroke="#E0E0E0"/><rect x="7" y="11" width="26" height="22" rx="2" fill="white" stroke="#E0E0E0"/><rect x="7" y="11" width="26" height="8" rx="2" fill="#4285F4"/><rect x="7" y="16" width="26" height="3" fill="#4285F4"/><rect x="13" y="7" width="3" height="7" rx="1.5" fill="#EA4335"/><rect x="24" y="7" width="3" height="7" rx="1.5" fill="#EA4335"/><circle cx="14" cy="26" r="2" fill="#34A853"/><circle cx="20" cy="26" r="2" fill="#FBBC04"/><circle cx="26" cy="26" r="2" fill="#EA4335"/></svg>
                  )},
                  { key: "other",          label: "Other",            hint: "",                         hasLink: null,  logo: (
                    <svg width="20" height="20" viewBox="0 0 40 40" fill="none"><rect width="40" height="40" rx="8" fill="#ECEEE9"/><circle cx="14" cy="20" r="2.5" fill="#7A7F76"/><circle cx="20" cy="20" r="2.5" fill="#7A7F76"/><circle cx="26" cy="20" r="2.5" fill="#7A7F76"/></svg>
                  )},
                ].map(sys => (
                  <div
                    key={sys.key}
                    onClick={() => {
                      setSchedulingSystem(sys.key);
                      setUrlMsg(null); setUrlErr(null);
                      if (!sys.hasLink) setBookingUrl("");
                    }}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "9px 12px", border: `1px solid ${schedulingSystem === sys.key ? C.sageBorder : C.gray200}`,
                      borderRadius: "6px", cursor: "pointer",
                      background: schedulingSystem === sys.key ? C.sageLight : C.white,
                      transition: "all 0.1s", userSelect: "none",
                    }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={{
                        width: "14px", height: "14px", borderRadius: "50%", flexShrink: 0,
                        border: `2px solid ${schedulingSystem === sys.key ? C.sage : C.gray300}`,
                        background: schedulingSystem === sys.key ? C.sage : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {schedulingSystem === sys.key && <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: C.white }} />}
                      </div>
                      <div style={{ flexShrink: 0 }}>{sys.logo}</div>
                      <span style={{ fontSize: "14px", color: C.black, fontFamily: fonts.body, fontWeight: 500 }}>{sys.label}</span>
                    </div>
                    {sys.hint && (
                      <span style={{ fontSize: "11px", color: C.gray400, fontFamily: fonts.body }}>{sys.hint}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Step 2 — system-specific instructions + URL input */}
            {schedulingSystem && (() => {
              const smsPh = {
                simplepractice: "https://example.clientsecure.me",
                jane:           "https://example.janeapp.com",
                calendly:       "https://calendly.com/example/new-patient-intake",
                other:          "https://your-booking-link.com",
              };
              const instructions = {
                simplepractice: { hasLink: true,  placeholder: "https://yourname.clientsecure.me", howTo: "Find it in SimplePractice → Settings → Client Portal → copy your booking page URL.", reassurance: "Patients only see appointment types you've made available in your client portal. Your SimplePractice calendar is unchanged — the appointment appears exactly as it always does." },
                jane:           { hasLink: true,  placeholder: "https://yourname.janeapp.com",     howTo: "Find it in Jane App → Settings → Online Booking → copy your booking URL.",                  reassurance: "Patients book directly into your Jane calendar. Nothing changes in your existing workflow." },
                calendly:       { hasLink: true,  placeholder: "https://calendly.com/yourname/new-patient-intake", howTo: "Log into Calendly → copy the link from your event type for new patient intakes.", reassurance: "Patients pick from the availability windows you've set in Calendly." },
                therapynotes:   { hasLink: false, placeholder: null, howTo: "TherapyNotes doesn't have a public booking link. When you accept a patient, Sage texts them that your office will reach out within 1 business day.", reassurance: null },
                google:         { hasLink: false, placeholder: null, howTo: "Google Calendar doesn't have a native booking link. When you accept a patient, Sage texts them that your office will reach out within 1 business day.", reassurance: null },
                other:          { hasLink: true,  placeholder: "https://your-booking-link.com",    howTo: "Paste your booking link below. If you don't have one, leave it blank and Sage will tell patients to expect a call from your office.", reassurance: null },
              };
              const info = instructions[schedulingSystem];
              const smsLinkExample = bookingUrl || smsPh[schedulingSystem] || "[your booking link]";
              const dr = clinic.physician ? clinic.physician.split(",")[0] : "Your doctor";

              return (
                <div style={{ background: C.paper, border: `1px solid ${C.gray200}`, borderRadius: "8px", padding: "14px 16px", marginBottom: "0" }}>
                  <div style={{ fontSize: "13px", color: C.gray700, fontFamily: fonts.body, lineHeight: 1.6, marginBottom: "12px" }}>{info.howTo}</div>

                  {info.hasLink && (
                    <div style={{ marginBottom: "10px" }}>
                      <input
                        type="url" value={bookingUrl}
                        onChange={e => { setBookingUrl(e.target.value); setUrlMsg(null); setUrlErr(null); }}
                        placeholder={info.placeholder}
                        style={{ width: "100%", border: `1px solid ${urlErr ? C.red : C.gray200}`, borderRadius: "6px", padding: "10px 12px", fontSize: "14px", fontFamily: fonts.body, color: C.black, background: C.white, outline: "none", boxSizing: "border-box" }}
                        onFocus={e => e.target.style.borderColor = C.sage}
                        onBlur={e => e.target.style.borderColor = urlErr ? C.red : C.gray200}
                      />
                      {bookingUrl && (() => {
                        const detected = [
                          { match: "clientsecure.me", label: "✓ SimplePractice detected" },
                          { match: "janeapp.com",     label: "✓ Jane App detected" },
                          { match: "calendly.com",    label: "✓ Calendly detected" },
                        ].find(d => bookingUrl.includes(d.match));
                        return detected ? <div style={{ fontSize: "12px", color: C.sageDark, marginTop: "4px", fontFamily: fonts.body }}>{detected.label}</div> : null;
                      })()}
                    </div>
                  )}

                  {info.reassurance && (
                    <div style={{ fontSize: "12px", color: C.gray500, fontFamily: fonts.body, lineHeight: 1.6, borderTop: `1px solid ${C.gray200}`, paddingTop: "10px", marginBottom: "10px" }}>
                      {info.reassurance}
                    </div>
                  )}

                  <div style={{ borderTop: `1px solid ${C.gray200}`, paddingTop: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                      <div style={{ fontSize: "11px", fontWeight: 600, color: C.gray400, textTransform: "uppercase", letterSpacing: "0.1em" }}>SMS patients will receive</div>
                      <div style={{ fontSize: "11px", fontWeight: 600, color: info.hasLink ? C.sageDark : C.amber, background: info.hasLink ? C.sageLight : C.amberLight, border: `1px solid ${info.hasLink ? C.sageBorder : C.amberBorder}`, borderRadius: "4px", padding: "1px 6px" }}>
                        {info.hasLink ? "Variant A — booking link" : "Variant B — office outreach"}
                      </div>
                    </div>
                    <div style={{ fontSize: "13px", color: C.black, lineHeight: 1.6, fontStyle: "italic" }}>
                      {info.hasLink
                        ? `"Hi [Name] — ${dr} reviewed your intake and would like to schedule your first appointment. Book here: ${smsLinkExample} — link available for 48 hours."`
                        : `"Hi [Name] — ${dr} reviewed your intake and would like to schedule your first appointment. Someone from our office will reach out within 1 business day."`
                      }
                    </div>
                    {info.hasLink && !bookingUrl && (
                      <div style={{ fontSize: "11px", color: C.amber, marginTop: "5px", fontFamily: fonts.body }}>
                        ↑ Paste your link above to replace the example URL
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {urlErr && <div style={{ fontSize: fs.xs, color: C.red, fontFamily: fonts.body, marginTop: "8px" }}>{urlErr}</div>}
            {urlMsg && <div style={{ fontSize: fs.xs, color: C.sage, fontFamily: fonts.body, marginTop: "8px" }}>✓ {urlMsg}</div>}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
              <button
                onClick={handleSaveBooking}
                disabled={urlSaving || !schedulingSystem}
                style={{ background: !schedulingSystem ? C.gray200 : C.sage, border: "none", borderRadius: "6px", padding: "8px 20px", color: !schedulingSystem ? C.gray400 : C.white, fontSize: "15px", fontWeight: 600, cursor: !schedulingSystem ? "not-allowed" : "pointer", fontFamily: fonts.body, opacity: urlSaving ? 0.7 : 1 }}
              >
                {urlSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>

        {/* Physician rate */}
        <div style={{ marginBottom: "32px" }}>
          <div style={{ fontSize: fs.xs, color: C.gray400, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "17px", fontFamily: fonts.body }}>Time Saved Calculation</div>
          <div style={{ background: C.paperDark, borderRadius: "8px", padding: "20px", border: `1px solid ${C.gray200}` }}>
            <label style={{ fontSize: fs.xs, color: C.gray500, display: "block", marginBottom: "5px", fontFamily: fonts.body }}>Physician rate (used for Summary tab)</label>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: C.gray400, fontSize: "15px", fontFamily: fonts.body }}>$</span>
                <input
                  type="number" min="50" max="5000" value={physicianRate}
                  onChange={e => { setPhysicianRate(e.target.value); setRateMsg(null); }}
                  style={{ border: `1px solid ${C.gray200}`, borderRadius: "6px", padding: "10px 12px 10px 22px", fontSize: "16px", fontFamily: fonts.body, color: C.black, background: C.white, outline: "none", width: "110px" }}
                  onFocus={e => e.target.style.borderColor = C.sage}
                  onBlur={e => e.target.style.borderColor = C.gray200}
                />
              </div>
              <span style={{ fontSize: "14px", color: C.gray400, fontFamily: fonts.body }}>per hour</span>
              <button onClick={handleSaveRate} disabled={rateSaving} style={{ background: rateSaving ? C.gray200 : C.sage, border: "none", borderRadius: "6px", padding: "8px 20px", color: C.white, fontSize: "15px", fontWeight: 600, cursor: rateSaving ? "not-allowed" : "pointer", fontFamily: fonts.body }}>
                {rateSaving ? "Saving…" : "Save"}
              </button>
            </div>
            {rateMsg && <div style={{ fontSize: "13px", color: rateMsg.startsWith("S") ? C.sage : C.red, marginTop: "8px", fontFamily: fonts.body }}>{rateMsg.startsWith("S") ? "✓ " : ""}{rateMsg}</div>}
            <div style={{ fontSize: "12px", color: C.gray400, marginTop: "8px", lineHeight: 1.6, fontFamily: fonts.body }}>
              Used to calculate time value on the Summary tab.
            </div>
          </div>
        </div>

        {/* Change password */}
        <div>
          <div style={{ fontSize: fs.xs, color: C.gray400, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "17px", fontFamily: fonts.body }}>Security</div>
          <div style={{ background: C.paperDark, borderRadius: "8px", padding: "20px", border: `1px solid ${C.gray200}`, display: "flex", flexDirection: "column", gap: "17px" }}>
            {[
              { label: "Current password",  val: currentPw, set: setCurrentPw },
              { label: "New password",      val: newPw,     set: setNewPw },
              { label: "Confirm password",  val: confirmPw, set: setConfirmPw },
            ].map(({ label, val, set }) => (
              <div key={label}>
                <label style={{ fontSize: fs.xs, color: C.gray500, display: "block", marginBottom: "5px", fontFamily: fonts.body }}>{label}</label>
                <input
                  type="password" value={val}
                  onChange={e => set(e.target.value)}
                  style={{ width: "100%", border: `1px solid ${C.gray200}`, borderRadius: "6px", padding: "10px 12px", fontSize: "16px", fontFamily: fonts.body, color: C.black, background: C.white, outline: "none", boxSizing: "border-box" }}
                  onFocus={e => e.target.style.borderColor = C.sage}
                  onBlur={e => e.target.style.borderColor = C.gray200}
                />
              </div>
            ))}
            {err && <div style={{ fontSize: fs.xs, color: C.red, fontFamily: fonts.body }}>{err}</div>}
            {msg && <div style={{ fontSize: fs.xs, color: C.sage, fontFamily: fonts.body }}>✓ {msg}</div>}
            <button
              onClick={handleChangePassword}
              disabled={saving}
              style={{ background: saving ? C.gray200 : C.sage, border: "none", borderRadius: "6px", padding: "16px", color: C.white, fontSize: fs.sm, fontWeight: 600, cursor: saving ? "default" : "pointer", fontFamily: fonts.body }}
              onMouseEnter={e => { if (!saving) e.currentTarget.style.background = C.sageDark; }}
              onMouseLeave={e => { if (!saving) e.currentTarget.style.background = C.sage; }}
            >
              {saving ? "Updating..." : "Update password"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── ADMIN PAGE ───────────────────────────────────────────────────────────────

// adminCall is imported from aws-client

function AdminPage({ onBack, onLogout }) {
  const [clinics, setClinics]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editId, setEditId]       = useState(null);
  const [editData, setEditData]   = useState({});
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newClinic, setNewClinic] = useState({ name: "", physician_name: "", email: "", intake_number: "", temp_password: "", slug: "" });
  const [createMsg, setCreateMsg] = useState(null);
  const [createErr, setCreateErr] = useState(null);
  const [creating, setCreating]   = useState(false);
  const [resetId, setResetId]     = useState(null);
  const [resetPw, setResetPw]     = useState("");
  const [resetMsg, setResetMsg]   = useState(null);
  const isMobile                  = useMobile();
  const pad                       = isMobile ? "16px" : "32px";

  useEffect(() => { loadClinics(); }, []);

  const loadClinics = async () => {
    setLoading(true);
    // Load directly from Supabase — no edge function needed for reads
    const { data, error } = await supabase
      .from("clinics")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) console.error("Load clinics error:", error);
    // Get intake counts separately
    const { data: intakeCounts } = await supabase
      .from("intakes")
      .select("clinic_id");
    const counts = {};
    (intakeCounts || []).forEach(i => { counts[i.clinic_id] = (counts[i.clinic_id] || 0) + 1; });
    setClinics((data || []).map(c => ({ ...c, intakeCount: counts[c.id] || 0 })));
    setLoading(false);
  };

  const startEdit = (c) => {
    setEditId(c.id);
    setEditData({ name: c.name, physician_name: c.physician_name, intake_number: c.intake_number });
  };

  const saveEdit = async () => {
    setSaving(true); setMsg(null);
    await adminCall("update", { clinic_id: editId, ...editData });
    setSaving(false); setEditId(null);
    setMsg("Saved ✓"); setTimeout(() => setMsg(null), 2000);
    loadClinics();
  };

  const handleCreate = async () => {
    setCreateErr(null); setCreateMsg(null);
    if (!newClinic.email || !newClinic.temp_password || !newClinic.name) {
      setCreateErr("Practice name, email and password are required."); return;
    }
    setCreating(true);
    const result = await adminCall("create", {
      email:          newClinic.email,
      password:       newClinic.temp_password,
      name:           newClinic.name,
      physician_name: newClinic.physician_name,
      intake_number:  newClinic.intake_number,
      slug:           newClinic.slug,
    });
    setCreating(false);
    if (result.error) { setCreateErr(result.error); return; }
    setCreateMsg(`✓ Account created for ${newClinic.email}`);
    setNewClinic({ name: "", physician_name: "", email: "", intake_number: "", temp_password: "", slug: "" });
    setTimeout(() => { setShowCreate(false); setCreateMsg(null); }, 2000);
    loadClinics();
  };

  const handleResetPassword = async (clinicEmail) => {
    if (!resetPw || resetPw.length < 8) { setResetMsg("Min 8 characters"); return; }
    const result = await adminCall("reset_password", { email: clinicEmail, password: resetPw });
    if (result.error) { setResetMsg(result.error); return; }
    setResetMsg("✓ Password reset");
    setTimeout(() => { setResetId(null); setResetPw(""); setResetMsg(null); }, 2000);
  };

  const Field = ({ label, value, onChange, mono, placeholder }) => (
    <div style={{ marginBottom: "17px" }}>
      <label style={{ fontSize: "17px", color: C.gray400, letterSpacing: "0.08em", textTransform: "uppercase", display: "block", marginBottom: "4px", fontFamily: fonts.body }}>{label}</label>
      <input
        value={value || ""} onChange={e => onChange(e.target.value)}
        placeholder={placeholder || ""}
        style={{ width: "100%", border: `1px solid ${C.gray200}`, borderRadius: "5px", padding: "8px 10px", fontSize: "17px", fontFamily: mono ? fonts.mono : fonts.body, color: C.black, background: C.white, outline: "none", boxSizing: "border-box" }}
        onFocus={e => e.target.style.borderColor = C.sage}
        onBlur={e => e.target.style.borderColor = C.gray200}
      />
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.paper, fontFamily: fonts.body }}>
      {/* Nav */}
      <div style={{ padding: `0 ${pad}`, borderBottom: `1px solid ${C.gray200}`, display: "flex", alignItems: "center", justifyContent: "space-between", height: "60px", position: "sticky", top: 0, background: C.paper, zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "17px" }}>
          <button onClick={onBack} style={{ background: "none", border: "none", color: C.gray400, fontSize: "17px", cursor: "pointer", fontFamily: fonts.body, padding: 0 }}>← Dashboard</button>
          <div style={{ width: "1px", height: "17px", background: C.gray200 }} />
          <span style={{ fontSize: fs.sm, fontWeight: 600, color: C.black }}>Admin — Clinic Management</span>
          <span style={{ fontSize: fs.xs, color: C.red, background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: "4px", padding: "2px 8px", fontFamily: fonts.body }}>Founder only</span>
        </div>
        <button onClick={onLogout} style={{ background: "none", border: "none", color: C.gray400, fontSize: "16px", cursor: "pointer", fontFamily: fonts.body, textDecoration: "underline" }}>Sign out</button>
      </div>

      <div style={{ maxWidth: "900px", margin: "0 auto", padding: `32px ${pad}` }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
          <div>
            <div style={{ fontSize: fs.xs, color: C.gray400, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: fonts.body }}>
              Clinics {!loading && `(${clinics.length})`}
            </div>
          </div>
          <button
            onClick={() => { setShowCreate(!showCreate); setCreateMsg(null); setCreateErr(null); }}
            style={{ background: C.sage, border: "none", borderRadius: "6px", padding: "7px 16px", color: C.white, fontSize: "17px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body }}
          >+ New clinic</button>
        </div>

        {/* Create form */}
        {showCreate && (
          <div style={{ background: C.sageLight, border: `1px solid ${C.sageBorder}`, borderRadius: "17px", padding: "20px", marginBottom: "24px" }}>
            <div style={{ fontSize: "16px", fontWeight: 600, color: C.sageDark, marginBottom: "16px", fontFamily: fonts.body }}>Create new clinic account</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0 20px" }}>
              <Field label="Practice name *"     value={newClinic.name}           onChange={v => setNewClinic(p => ({...p, name: v}))}           placeholder="Valley Psychiatry" />
              <Field label="Provider name"       value={newClinic.physician_name} onChange={v => setNewClinic(p => ({...p, physician_name: v}))} placeholder="Dr. Jane Smith, MD" />
              <Field label="Email *"             value={newClinic.email}          onChange={v => setNewClinic(p => ({...p, email: v}))}           placeholder="doctor@clinic.com" />
              <Field label="Temporary password *" value={newClinic.temp_password} onChange={v => setNewClinic(p => ({...p, temp_password: v}))}  placeholder="min 8 characters" />
              <Field label="Intake number (E.164)" value={newClinic.intake_number} onChange={v => setNewClinic(p => ({...p, intake_number: v}))} placeholder="+14155550100" mono />
              <Field label="URL slug (e.g. drtran)" value={newClinic.slug}        onChange={v => setNewClinic(p => ({...p, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "")}))} placeholder="drtran" mono />
            </div>
            {createErr && <div style={{ fontSize: fs.xs, color: C.red, marginBottom: "17px", fontFamily: fonts.body }}>⚠ {createErr}</div>}
            {createMsg && <div style={{ fontSize: fs.xs, color: C.sage, marginBottom: "17px", fontFamily: fonts.body }}>{createMsg}</div>}
            <div style={{ display: "flex", gap: "17px" }}>
              <button onClick={handleCreate} disabled={creating} style={{ background: creating ? C.gray200 : C.sage, border: "none", borderRadius: "6px", padding: "9px 18px", color: C.white, fontSize: "17px", fontWeight: 600, cursor: creating ? "default" : "pointer", fontFamily: fonts.body }}>
                {creating ? "Creating..." : "Create account"}
              </button>
              <button onClick={() => setShowCreate(false)} style={{ background: "none", border: `1px solid ${C.gray200}`, borderRadius: "6px", padding: "9px 14px", color: C.gray500, fontSize: "17px", cursor: "pointer", fontFamily: fonts.body }}>Cancel</button>
            </div>
          </div>
        )}

        {msg && <div style={{ fontSize: fs.xs, color: C.sage, marginBottom: "17px", fontFamily: fonts.body }}>✓ {msg}</div>}

        {/* Clinics list */}
        {loading ? (
          <div style={{ fontSize: fs.xs, color: C.gray400, fontFamily: fonts.body }}>Loading clinics...</div>
        ) : clinics.length === 0 ? (
          <div style={{ fontSize: fs.xs, color: C.gray400, fontFamily: fonts.body }}>No clinics yet. Create one above.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "17px" }}>
            {clinics.map(c => {
              const intakeCount = c.intakeCount || 0;
              const joined = new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
              const isEditing   = editId   === c.id;
              const isResetting = resetId  === c.id;

              return (
                <div key={c.id} style={{ background: C.paperDark, border: `1px solid ${C.gray200}`, borderRadius: "17px", padding: "16px 20px" }}>
                  {isEditing ? (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0 20px" }}>
                        <Field label="Practice name"   value={editData.name}           onChange={v => setEditData(p => ({...p, name: v}))} />
                        <Field label="Provider name"   value={editData.physician_name} onChange={v => setEditData(p => ({...p, physician_name: v}))} />
                        <Field label="Intake number (E.164)" value={editData.intake_number} onChange={v => setEditData(p => ({...p, intake_number: v}))} mono />
                      </div>
                      <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                        <button onClick={saveEdit} disabled={saving} style={{ background: C.sage, border: "none", borderRadius: "5px", padding: "7px 14px", color: C.white, fontSize: "16px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body }}>
                          {saving ? "Saving..." : "Save"}
                        </button>
                        <button onClick={() => setEditId(null)} style={{ background: "none", border: `1px solid ${C.gray200}`, borderRadius: "5px", padding: "7px 12px", color: C.gray500, fontSize: "16px", cursor: "pointer", fontFamily: fonts.body }}>Cancel</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "17px" }}>
                        <div>
                          <div style={{ fontSize: "17px", fontWeight: 600, color: C.black, marginBottom: "3px", fontFamily: fonts.body }}>{c.name || "Unnamed practice"}</div>
                          <div style={{ fontSize: fs.xs, color: C.gray500, fontFamily: fonts.body, lineHeight: 1.9 }}>
                            {c.physician_name && <><span>{c.physician_name}</span><span style={{ color: C.gray300 }}> · </span></>}
                            <span>{c.email}</span><br />
                            <span style={{ fontFamily: fonts.mono, color: c.intake_number ? C.black : C.gray400 }}>
                              {c.intake_number || "No intake number set"}
                            </span>
                            <span style={{ color: C.gray400 }}> · {intakeCount} intake{intakeCount !== 1 ? "s" : ""} · Joined {joined}</span>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                          <button onClick={() => startEdit(c)} style={{ background: "none", border: `1px solid ${C.gray200}`, borderRadius: "5px", padding: "5px 12px", color: C.gray700, fontSize: "16px", cursor: "pointer", fontFamily: fonts.body }}>Edit</button>
                          <button onClick={() => { setResetId(isResetting ? null : c.id); setResetPw(""); setResetMsg(null); }} style={{ background: "none", border: `1px solid ${C.gray200}`, borderRadius: "5px", padding: "5px 12px", color: C.gray700, fontSize: "16px", cursor: "pointer", fontFamily: fonts.body }}>
                            {isResetting ? "Cancel" : "Reset password"}
                          </button>
                        </div>
                      </div>
                      {isResetting && (
                        <div style={{ marginTop: "17px", display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", borderTop: `1px solid ${C.gray100}`, paddingTop: "17px" }}>
                          <input
                            type="text" placeholder="New temporary password (min 8 chars)"
                            value={resetPw} onChange={e => setResetPw(e.target.value)}
                            style={{ border: `1px solid ${C.gray200}`, borderRadius: "5px", padding: "7px 10px", fontSize: "17px", fontFamily: fonts.mono, color: C.black, outline: "none", minWidth: "240px", background: C.white }}
                            onFocus={e => e.target.style.borderColor = C.sage}
                            onBlur={e => e.target.style.borderColor = C.gray200}
                          />
                          <button onClick={() => handleResetPassword(c.email)} style={{ background: C.sage, border: "none", borderRadius: "5px", padding: "7px 14px", color: C.white, fontSize: "16px", fontWeight: 600, cursor: "pointer", fontFamily: fonts.body }}>Set password</button>
                          {resetMsg && <span style={{ fontSize: "16px", color: resetMsg.includes("✓") ? C.sage : C.red, fontFamily: fonts.body }}>{resetMsg}</span>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// ─── APP ──────────────────────────────────────────────────────────────────────

export default function App() {
  const CLINIC = {
    name: "Demo Practice",
    physician: "Dr. Jane Doe, MD",
    intakeNumber: "+1 (555) 010-0001",
    slug: "janedoe",
    booking_url: null,
    _email: "demo@psychxai.com",
  };

  // ── Shared intake mapper — single source of truth ────────────────────────
  const cleanField = (val) => {
    if (!val) return null;
    const dirty = ["not gathered", "incomplete", "n/a", "unknown", "none reported", "not applicable", "see transcript", "not collected"];
    if (dirty.some(d => val.toLowerCase().includes(d))) return null;
    return val.trim() || null;
  };

  const mapIntake = (i) => ({
    id: i.id,
    name: i.patient_name || "Unknown",
    age: i.patient_age || "",
    gender: i.patient_gender || null,
    dob: i.patient_dob || null,
    phone: i.patient_phone || "",
    date: new Date(i.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    time: new Date(i.created_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
    chiefComplaint: i.chief_complaint || "",
    primaryDiagnosis: cleanField(i.primary_diagnosis),
    medicationsCurrent: cleanField(i.medications_current),
    medicationsPrior: cleanField(i.medications_prior),
    allergies: cleanField(i.allergies),
    riskLevel: i.risk_level || "low",
    siFlag: i.si_flag || false,
    hiFlag: i.hi_flag || false,
    status: i.status || "pending",
    fitScore: i.fit_score || "review",
    fitRationale: i.fit_rationale || "",
    declineFlag: i.decline_flag || null,
    declineReason: i.decline_reason || null,
    declineNote: i.decline_note || null,
    callDuration: i.call_duration || "—",
    callDurationMins: i.call_duration_mins || 0,
    callComplete: i.call_complete !== false,
    smsSentAt: i.sms_sent_at || null,
    intakeComplete: i.intake_complete || false,
    decidedAt: i.decided_at || null,
    originalNote: i.note || "",
    seizureHistory: i.seizure_history || false,
    cardiacHistory: i.cardiac_history || false,
    strokeHistory: i.stroke_history || false,
    pregnancyStatus: i.pregnancy_status || null,
    graveDisabilityFlag: i.grave_disability_flag || false,
    firearmsAccess: i.firearm_access || false,
    firearmsSecured: i.firearm_secured || null,
    legalHistory: i.legal_history || null,
    developmentalHistory: i.developmental_history || null,
    menstrualMoodChanges: i.menstrual_mood_changes || false,
    sleepHours: i.sleep_hours || null,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [lastTab, setLastTab]         = useState("pending");
  const [patients, setPatients]     = useState([]);
  const [isDemo, setIsDemo]         = useState(false);
  const [clinicData, setClinicData] = useState({ name: "", physician: "", intakeNumber: "", _email: "" });
  const [session, setSession]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [screen, setScreen]         = useState("login");

  // ── Auth state listener — persist session across refresh ────────────────
  useEffect(() => {
    auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.email) {
        setSession(session);
        loadClinicData(session.user.email).then(() => {
          setScreen("dashboard");
          setLoading(false);
        });
      } else {
        setScreen("login");
        setLoading(false);
      }
    });

    const { data: { subscription } } = auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session?.user?.email) {
        setSession(session);
      }
      if (event === "SIGNED_OUT") {
        setSession(null);
        setScreen("login");
        setPatients([]);
        setIsDemo(false);
        setClinicData({ name: "", physician: "", intakeNumber: "", _email: "" });
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  // ── Realtime polling — new intakes appear without refresh ────────────────
  useEffect(() => {
    if (isDemo || screen !== "dashboard") return;
    const poll = setInterval(async () => {
      const { data: clinic } = await db.clinics.getByEmail(clinicData._email || "");
      if (!clinic) return;
      const { data: intakes } = await db.intakes.getByClinic(clinic.id);
      if (!intakes) return;
      setPatients(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newOnes = intakes.filter(i => !existingIds.has(i.id));
        if (newOnes.length === 0) return prev;
        return [...newOnes.map(mapIntake), ...prev];
      });
    }, 30000);
    return () => clearInterval(poll);
  }, [isDemo, screen, clinicData._email]);

  // ── Update browser tab title with pending count (clinic users only) ──────
  useEffect(() => {
    if (screen === "login" || !session) { document.title = "PsychXAI"; return; }
    const pending = patients.filter(p => p.status === "pending").length;
    document.title = pending > 0 ? `(${pending}) PsychXAI` : "PsychXAI";
  }, [patients, screen, session]);

  // ── Handle browser back button — stay in app, don't sign out ────────────
  useEffect(() => {
    const handlePop = (e) => {
      e.preventDefault();
      if (screen === "note") {
        setScreen("dashboard");
        window.history.pushState(null, "", window.location.href);
      } else if (screen === "dashboard") {
        // Stay on dashboard — don't go back to login
        window.history.pushState(null, "", window.location.href);
      }
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, [screen]);

  // ── Load clinic config from Supabase ────────────────────────────────────
  const loadClinicData = async (email) => {
    if (email === "demo@psychxai.com") {
      setPatients(DEMO_PATIENTS); setClinicData(CLINIC); setIsDemo(true); return;
    }
    setPatients([]); setIsDemo(false);
    const { data: clinic } = await db.clinics.getByEmail(email);
    if (!clinic) { setClinicData({ name: "My Practice", physician: email, intakeNumber: "—", _email: email }); return; }
    setClinicData({ id: clinic.id, name: clinic.name || "My Practice", physician: clinic.physician_name || email, intakeNumber: clinic.intake_number || "—", slug: clinic.slug || null, booking_url: clinic.booking_url || null, scheduling_system: clinic.scheduling_system || null, clinic_phone: clinic.clinic_phone || null, physicianRate: clinic.physician_rate || 350, _email: email });
    const { data: intakes } = await db.intakes.getByClinic(clinic.id);
    setPatients(intakes?.length > 0 ? intakes.map(mapIntake) : []);
  };

  // ── Real login via Supabase Auth ─────────────────────────────────────────
  const handleLogin = async (email, password) => {
    const { error: authError } = await auth.signInWithPassword({ email, password });
    if (authError) return { error: authError };
    const { data: clinic } = await db.clinics.getByEmail(email);
    if (clinic) {
      setClinicData({
        id: clinic.id,
        name: clinic.name || "My Practice",
        physician: clinic.physician_name || email,
        intakeNumber: clinic.intake_number || "—",
        slug: clinic.slug || null,
        booking_url: clinic.booking_url || null,
        scheduling_system: clinic.scheduling_system || null,
        clinic_phone: clinic.clinic_phone || null,
        physicianRate: clinic.physician_rate || 350,
        _email: email,
      });
      const { data: intakes } = await db.intakes.getByClinic(clinic.id);
      setPatients(intakes?.length > 0 ? intakes.map(mapIntake) : []);
      setScreen("dashboard");
    } else {
      setClinicData({ name: "My Practice", physician: email, intakeNumber: "—", _email: email });
      setPatients([]);
      setScreen("dashboard");
    }
    setIsDemo(false);
    return { error: null };
  };


  // ── Demo login ───────────────────────────────────────────────────────────
  const handleDemo = () => {
    setPatients(DEMO_PATIENTS);
    setClinicData(CLINIC);
    setIsDemo(true);
    setScreen("dashboard");
  };

  // ── Sign out ─────────────────────────────────────────────────────────────
  const handleLogout = async () => {
    if (!isDemo) await auth.signOut();
    setScreen("login");
    setPatients([]);
    setIsDemo(false);
    setSession(null);
    setClinicData({ name: "", physician: "", intakeNumber: "", _email: "" });
  };

  // ── Update patient status ────────────────────────────────────────────────
  const handleDecide = async (id, action, decidedAt, declineReason, declineNote) => {
    // Only auto-switch tab if this was the last pending patient
    const remainingPending = patients.filter(p => p.status === "pending" && p.id !== id).length;
    if (remainingPending === 0 && action !== "pending") {
      setLastTab(action === "accept" ? "accepted" : "declined");
    }

    if (action === "pending") {
      setPatients(prev => prev.map(p => p.id === id ? { ...p, status: "pending", decidedAt: null, declineReason: null, declineNote: null } : p));
      if (!isDemo) await db.intakes.update(id, { status: "pending", decided_at: null, decline_reason: null, decline_note: null });
      return;
    }

    const status = action === "accept" ? "accepted" : "declined";

    setPatients(prev => prev.map(p => p.id === id ? {
      ...p, status, decidedAt,
      declineReason: action === "decline" ? declineReason : null,
      declineNote:   action === "decline" ? declineNote   : null,
      ...(action === "decline" ? { fitScore: "decline", fitRationale: declineReason + (declineNote ? ` — ${declineNote}` : "") } : {}),
    } : p));

    if (!isDemo) {
      await db.intakes.update(id, {
        status,
        decided_at: decidedAt,
        decline_reason: action === "decline" ? declineReason : null,
        decline_note:   action === "decline" ? declineNote   : null,
        ...(action === "decline" ? {
          fit_score: "decline",
          fit_rationale: declineReason + (declineNote ? ` — ${declineNote}` : ""),
        } : {}),
      });
    }
  };

  // ── Mark scheduled (reviewed) ────────────────────────────────────────────
  const handleSign = async (id, editedNote, undo = false) => {
    if (undo) {
      setPatients(prev => prev.map(p => p.id === id ? { ...p, status: "accepted", originalNote: editedNote || p.originalNote } : p));
      if (!isDemo) await db.intakes.update(id, { status: "accepted" });
      return;
    }
    setPatients(prev => prev.map(p => p.id === id ? { ...p, status: "reviewed", originalNote: editedNote || p.originalNote } : p));
    if (!isDemo) {
      await db.intakes.update(id, {
        status: "reviewed",
        ...(editedNote ? { note: editedNote } : {}),
      });
    }
  };

  // ── Mark intake complete ──────────────────────────────────────────────────
  const handleIntakeComplete = async (id) => {
    setPatients(prev => prev.map(p => p.id === id ? { ...p, intakeComplete: true } : p));
    if (!isDemo) await db.intakes.update(id, { intake_complete: true });
  };

  // ── Intake landing page — ABSOLUTE FIRST, public, no auth needed ────────
  const pathname    = window.location.pathname;
  const intakeMatch = pathname.match(/^\/intake\/([a-z0-9-]+)$/i);
  if (intakeMatch) {
    return <><FontLoader /><IntakeLandingPage slug={intakeMatch[1]} /></>;
  }

  const ADMIN_EMAIL = "peakpsychiatrist@gmail.com";
  const isAdmin     = clinicData._email === ADMIN_EMAIL;
  const isAuthed    = isDemo || !!session;

  // ── Loading state ────────────────────────────────────────────────────────
  if (loading) return <AILoader />;


  // ── Login gate — ALWAYS show login if not authed ─────────────────────────
  if (!isAuthed || screen === "login") return (
    <><FontLoader /><GlobalStyles /><Login onLogin={handleLogin} onDemo={handleDemo} /></>
  );

  // ── Authenticated screens ────────────────────────────────────────────────
  if (screen === "note" && selectedId) return (
    <><FontLoader /><GlobalStyles />
    <NoteScreen
      patientId={selectedId}
      patients={patients}
      clinic={clinicData}
      onBack={() => setScreen("dashboard")}
      initialTab={lastTab}
      onSign={handleSign}
      onDecide={handleDecide}
      onIntakeComplete={handleIntakeComplete}
      isDemo={isDemo}
    /></>
  );

  if (screen === "security") return (
    <><FontLoader /><GlobalStyles />
    <SecurityPage onBack={() => setScreen("dashboard")} /></>
  );

  if (screen === "settings") return (
    <><FontLoader /><GlobalStyles />
    <Settings
      clinic={clinicData}
      onBack={() => setScreen("dashboard")}
      onLogout={handleLogout}
      onClinicUpdate={(updates) => setClinicData(prev => ({ ...prev, ...updates }))}
    /></>
  );

  if (screen === "admin" && isAdmin) return (
    <><FontLoader /><GlobalStyles />
    <AdminPage
      onBack={() => setScreen("dashboard")}
      onLogout={handleLogout}
    /></>
  );

  return (
    <><FontLoader /><GlobalStyles />
    <Dashboard
      initialTab={lastTab}
      patients={patients}
      onSelect={(p, fromTab) => { setSelectedId(p.id); setLastTab(fromTab || "pending"); setScreen("note"); }}
      onDecide={handleDecide}
      onLogout={handleLogout}
      onSettings={() => setScreen("settings")}
      onSecurity={() => setScreen("security")}
      onAdmin={isAdmin ? () => setScreen("admin") : null}
      isDemo={isDemo}
      clinic={clinicData}
      onSmsSent={(patientId, ts) => setPatients(prev => prev.map(p => p.id === patientId ? { ...p, smsSentAt: ts } : p))}
    /></>
  );
}
